import { useState, useEffect, useCallback, useRef, useMemo, lazy, Suspense } from 'react';
import UploadPanel from './components/Upload/UploadPanel';
import SessionHeader, { AnalysisToolbar, SessionMeta } from './components/Analysis/SessionHeader';
import ShortcutOverlay from './components/ShortcutOverlay/ShortcutOverlay';
import { HelpProvider } from './contexts/HelpContext';
import { IssueReportProvider, useIssueReport } from './contexts/IssueReportContext';
import { listIssues } from './services/api';
import LapSelector, { getLapColor, LAP_COLORS, COMPARE_PALETTES } from './components/LapSelector/LapSelector';
import TrackMap from './components/TrackMap/TrackMap';
import EcuMergeModal from './components/Analysis/EcuMergeModal';
import VideoRenderModal from './components/VideoPanel/VideoRenderModal.jsx';
// Lazy so three.js (~150 KB gzip) only downloads when someone opens the 3D
// view — this is the app's only code-split, and the reason for it.
const Lap3DModal = lazy(() => import('./components/Lap3D/Lap3DModal'));
const VideoStudio = lazy(() => import('./components/VideoStudio/VideoStudio'));
const Lap3DPanel = lazy(() => import('./components/Lap3D/Lap3DPanel'));
import ShareModal from './components/Upload/ShareModal.jsx';
import { pickLap3dLaps, toLap3dRequest } from './utils/lap3dSelection';
import { lapDisplayNumber } from './utils/lapDelta';
import SpeedTrace from './components/Analysis/SpeedTrace';
import GTrace from './components/Analysis/GTrace';
import TimeDeltaTrace from './components/Analysis/TimeDeltaTrace';
import PowerTrace, { POWER_PANEL_KEY } from './components/Analysis/PowerTrace';
import ChannelTrace from './components/Analysis/ChannelTrace';
import ReadoutsPanel from './components/Analysis/ReadoutsPanel';
import VideoPanel from './components/VideoPanel/VideoPanel';
import MetricsPanel from './components/Analysis/MetricsPanel';
import SessionRecapDialog from './components/Analysis/SessionRecapDialog';
import HeadToHead from './components/Analysis/HeadToHead';
import TrackDifference from './components/Analysis/TrackDifference';
import PromptPanel from './components/Analysis/PromptPanel';
import CommentsPanel from './components/Analysis/CommentsPanel';
import SectorTimes from './components/Analysis/SectorTimes';
import FrictionCircle from './components/Analysis/FrictionCircle';
import ScatterPlot from './components/Analysis/ScatterPlot';
import WindowFrame from './components/WindowFrame/WindowFrame';
import MobilePanel from './components/MobileShell/MobilePanel';
import MobileTabBar from './components/MobileShell/MobileTabBar';
import AccountSettings, { loadDriverProfile } from './components/Settings/AccountSettings';
import MasqueradeModal from './components/Auth/MasqueradeModal';
import { getDisplayUnit, makeValueTransform, loadUnitPrefs, saveUnitPrefs } from './utils/unitConvert';
import { channelsFromSession, parseChannelKey } from './utils/channelTags';
import {
  MAX_EXTRA_CHANNELS, removePanelConfig, resolvePanelExtraChannels,
  setPanelColorMode, setPanelSharedScale, setPanelSplit, togglePanelExtraChannel,
} from './utils/channelPanelConfigs';
import { getBestLapTime, isBestLap } from './utils/lapStats';
import { useSession } from './hooks/useSession';
import { useTabManager, formulaDefault } from './hooks/useTabManager';
import { useTabHotkeys } from './hooks/useTabHotkeys';
import { useChartKeyboardNav } from './hooks/useChartKeyboardNav';
import TabBar from './components/TabBar/TabBar';
import { saveTrack, getTrack, computeMetrics, buildBriefing, patchSessionMeta, realignSessionSf, unalignSessionSf, redetectCorners, snapCorner, getChannelTrace, getUserPreferences, saveUserPreferences, getSectorTimes, getSectorMetrics, getFormulas, createFormula, updateFormula, deleteFormula, getFormulaTrace, getCapabilities, getAppConfig, deleteSession, getSessionLeaderboardTrack, setShareToken, listShares, getPowerTrace, getSessionVehicle } from './services/api';
import { mergeSnappedCorner } from './utils/cornerGeometry.js';
import GuestHome from './components/Public/GuestHome';
import { addGuestSession } from './services/guestSessions';
import { subscribeZoom, publishZoom } from './utils/chartZoomBus';
import FormulaEditor from './components/Analysis/FormulaEditor';
import { BUILTIN_FORMULAS, isBuiltinFormula } from './components/Analysis/builtinFormulas';
import LeaderboardPage from './components/Leaderboard/LeaderboardPage';
import ProgressPage from './components/Progress/ProgressPage';
import ChannelStatsPage from './components/ChannelStats/ChannelStatsPage';
const ReportPage = lazy(() => import('./components/Report/ReportPage'));
import SimilarityLabPage from './components/SimilarityLab/SimilarityLabPage';
import IssuesPage from './components/Issues/IssuesPage';
import SearchPage from './components/Search/SearchPage';
import AppHeader from './components/common/AppHeader';
import { defaultSectors } from './utils/sectors';
import { sfOffsetM, lapStartPoint, SF_MISMATCH_WARN_M } from './utils/sfAlignment';
import { ANALYSIS_PLOT_MIN_WIDTH } from './utils/panelLayout';
import { useIsMobile } from './hooks/useIsMobile';
import useSessionRecap from './hooks/useSessionRecap';
import './App.css';

// Query-string params the app owns for view/session state. The URL-sync effect
// rewrites exactly these; anything else in the query string is preserved.
const OWNED_URL_PARAMS = ['view', 'track', 'q', 'session', 'compare', 'laps', 'issue'];

// ── Collapsible LAPS reflow ────────────────────────────────────────────────
// Floating windows use viewport-absolute coordinates. Collapsing LAPS remaps
// x/width; collapsing the analysis toolbar remaps y/height. Both transforms are
// display-only, with user drags/resizes inverse-mapped before persistence so
// either control can be toggled without corrupting the canonical layout.
const EXPANDED_SIDEBAR = 180;
const RAIL_WIDTH       = 32;
// Keep in sync with AppHeader.module.css and SessionHeader.module.css. The
// expanded state has a 56px toolbar plus 34px metadata strip; collapsed mode
// combines both into one 30px bar.
const EXPANDED_ANALYSIS_CHROME = 90;
const COLLAPSED_ANALYSIS_CHROME = 30;
const ANALYSIS_CHROME_DELTA = EXPANDED_ANALYSIS_CHROME - COLLAPSED_ANALYSIS_CHROME;

function groupChannelSelections(channels, compareDriverLabel) {
  const byName = new Map();
  channels.forEach(ch => {
    if (!byName.has(ch.name)) byName.set(ch.name, []);
    byName.get(ch.name).push(ch);
  });
  return [...byName.entries()].map(([name, entries]) => {
    const primaryEntry = entries.find(entry => entry.source === 'primary');
    const cmpEntries = entries.filter(entry => entry.source !== 'primary');
    const unit = (primaryEntry || entries[0]).unit;
    const title = primaryEntry ? name : `${name} — ${compareDriverLabel(cmpEntries[0].source)}`;
    return { name, unit, title, entries, primaryEntry, cmpEntries };
  });
}

function reflowScale() {
  const W = window.innerWidth;
  return (W - RAIL_WIDTH) / (W - EXPANDED_SIDEBAR);
}

// expanded-space → collapsed display-space
function remapRect(rect, sidebarCollapsed, headerCollapsed = false, panelBounds = null) {
  if (!rect) return rect;
  const out = { ...rect };

  if (sidebarCollapsed) {
    const s = reflowScale();
    if (typeof rect.x === 'number')
      out.x = Math.max(RAIL_WIDTH, RAIL_WIDTH + (rect.x - EXPANDED_SIDEBAR) * s);
    if (typeof rect.width === 'number') out.width = rect.width * s;
  }

  if (headerCollapsed && panelBounds) {
    const collapsedTop = panelBounds.top;
    const collapsedHeight = panelBounds.height;
    const expandedTop = collapsedTop + ANALYSIS_CHROME_DELTA;
    const expandedHeight = collapsedHeight - ANALYSIS_CHROME_DELTA;
    if (expandedHeight > 0) {
      const s = collapsedHeight / expandedHeight;
      if (typeof rect.y === 'number')
        out.y = collapsedTop + (rect.y - expandedTop) * s;
      if (typeof rect.height === 'number') out.height = rect.height * s;
    }
  }

  return out;
}

// collapsed display-space → expanded-space (for persisting drags/resizes)
function inverseRemapRect(rect, sidebarCollapsed, headerCollapsed = false, panelBounds = null) {
  if (!rect) return rect;
  const out = { ...rect };

  if (sidebarCollapsed) {
    const s = reflowScale();
    if (typeof rect.x === 'number')
      out.x = EXPANDED_SIDEBAR + (rect.x - RAIL_WIDTH) / s;
    if (typeof rect.width === 'number') out.width = rect.width / s;
  }

  if (headerCollapsed && panelBounds) {
    const collapsedTop = panelBounds.top;
    const collapsedHeight = panelBounds.height;
    const expandedTop = collapsedTop + ANALYSIS_CHROME_DELTA;
    const expandedHeight = collapsedHeight - ANALYSIS_CHROME_DELTA;
    if (expandedHeight > 0) {
      const s = collapsedHeight / expandedHeight;
      if (typeof rect.y === 'number')
        out.y = expandedTop + (rect.y - collapsedTop) / s;
      if (typeof rect.height === 'number') out.height = rect.height / s;
    }
  }

  return out;
}

function slugify(s) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}
// Stable empty-object reference for lap-color lookups with no saved override.
const EMPTY_COLORS = {};
// Default VideoPanel trim (seconds) for a session's video. Rendered (overlay)
// videos carry their own sync offset; raw videos start at the df time origin.
function videoDefaultTrim(vs) {
  if (!vs?.has_rendered_video) return 0;
  return (vs.rendered_video_sync_offset_sec ?? 0) < 0
    ? vs.rendered_video_sync_offset_sec + (vs.off_track_gap_sec ?? 0)
    : -(vs.df_csv_time_start ?? 0);
}
// The <video> source URL for a session (HLS if available, else rendered/raw).
function videoSrcFor(vs, vid) {
  return vs?.hls_url ?? (vs?.has_rendered_video ? `/api/rendered_video/${vid}` : `/api/video/${vid}`);
}
function nextTurnId(corners) {
  const nums = corners.map(c => parseInt((c.id || '').replace(/\D/g, ''))).filter(n => !isNaN(n));
  const next = nums.length ? Math.max(...nums) + 1 : 1;
  return { id: `T${next}`, label: `Turn ${next}` };
}
// A track_id is "temp" if it came from a tempfile-stem session name that leaked
// in (e.g. "tmpq09g9t6b" from a Venue-less XRK upload) or is a bare UUID. Mirrors
// the backend _is_temp() in session_store.find_track_by_gps. We must never persist
// a track file under such an id: it creates a junk circuit at the real track's GPS
// location that the leaderboard's centroid matcher can latch onto.
const _UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
function isTempTrackId(trackId) {
  return !trackId || trackId === 'track' || trackId.startsWith('tmp') || _UUID_RE.test(trackId);
}
function persistTrack(trackId, trackName, corners, sectors) {
  if (!trackId || isTempTrackId(trackId)) return;
  saveTrack({
    track_id: trackId,
    track_name: trackName,
    corners,
    sectors: sectors || [],
  }).catch(() => {});
}
// Backwards-compatible alias used widely below.
function persistCorners(trackId, trackName, corners, sectors) {
  persistTrack(trackId, trackName, corners, sectors);
}

// Compute which lap indices to auto-select given a mode and available data.
// Returns an array of lap indices (integers).
function computeLapIndices(mode, allLaps, sectorData) {
  const timed  = (allLaps || []).filter(l => !l.is_outlap && !l.is_inlap);
  const sorted = [...timed].sort((a, b) => a.lap_time_seconds - b.lap_time_seconds);

  if (mode.type === 'best') {
    return sorted.slice(0, mode.count).map(l => l.index);
  }

  if (mode.type === 'sector_best' && sectorData && mode.sectorIdx != null) {
    const si   = mode.sectorIdx;
    const rows = (sectorData.laps || [])
      .filter(l => !l.is_outlap && !l.is_inlap && l.sector_times[si] != null);
    return [...rows]
      .sort((a, b) => a.sector_times[si] - b.sector_times[si])
      .slice(0, mode.count)
      .map(l => l.lap_index);
  }

  // sector_best with no sectorData yet — show fastest N as placeholder
  return sorted.slice(0, mode.count).map(l => l.index);
}

const EXPERIENCE_LABELS = {
  hpde_novice:       'HPDE Novice',
  hpde_intermediate: 'HPDE Intermediate',
  hpde_advanced:     'HPDE Advanced',
  scca_solo:         'SCCA / Club Racing',
  instructor:        'Instructor',
  pro:               'Professional',
};

// Desktop panel wrapper. (Mobile renders MobilePanel directly — only the
// active panel is mounted, so there is no shared shell.) Defined outside App
// so React sees a stable component reference and avoids unnecessary unmounts
// when props change.
function PanelShell({
  id, title, onClose,
  children,
  layout, onUpdate, allLayouts,
  onMaximize, maximized, maximizedContainerRef,
  minWidth, minHeight,
}) {
  return (
    <WindowFrame
      id={id} title={title} layout={layout} onUpdate={onUpdate}
      allLayouts={allLayouts} onClose={onClose} onMaximize={onMaximize}
      maximized={maximized} maximizedContainerRef={maximizedContainerRef}
      minWidth={minWidth} minHeight={minHeight}
    >
      {children}
    </WindowFrame>
  );
}

// Maximum number of comparison (non-primary) drivers that can be overlaid at
// once. Total drivers on the analysis page = 1 primary + MAX_COMPARE.
const MAX_COMPARE = 3;

// Immutably set one slot of a fixed-length comparison state array. `val` may be
// a value or an updater (prev => next), mirroring a normal setState updater.
function setAt(setter, slot, val) {
  setter(prev => prev.map((v, i) =>
    i === slot ? (typeof val === 'function' ? val(v) : val) : v));
}

// Read an analytics page's persisted filter/selection state from the URL: the
// current JSON-encoded `q` param, falling back to the older single `track`
// param (bookmarks/links minted before per-page state was serialized).
function parsePageParams(urlParams) {
  const q = urlParams.get('q');
  if (q) {
    try {
      return JSON.parse(q);
    } catch {
      // fall through to the legacy form below
    }
  }
  const trackId = urlParams.get('track');
  return trackId ? { trackId } : null;
}

function AppInner({
  onLogout, currentUser, onUpdateUser, onRequestMasquerade, onStopMasquerade,
  appConfig = null, publicMode = false, guestMode = false, initialSessionId = null,
}) {
  const videoRef    = useRef(null);
  // Telemetry time a pending navigation wants the video parked at — set when a
  // video-search hit is opened, consumed by the session-init effect below.
  const pendingVideoSeekRef = useRef(null);
  const mainAreaRef = useRef(null);
  const isMobile    = useIsMobile();
  // Both no-account modes (public share link + guest sandbox) are read-only and
  // hide account/owner UI. guestMode additionally enables anonymous upload.
  const restricted  = publicMode || guestMode;
  const productCapabilities = appConfig?.capabilities || {
    user_accounts: true,
    session_sharing: true,
    public_leaderboards: true,
    cross_session_analytics: true,
    sync_to_server: false,
  };
  const [showLapDrawer, setShowLapDrawer] = useState(false);
  // "Report a problem" — the dialog lives in IssueReportProvider (above this
  // component's early returns); we only open it and publish what's on screen.
  const issueReport = useIssueReport();
  // Mobile shows one panel at a time; remember which across visits.
  const [activeMobilePanel, setActiveMobilePanel] = useState(
    () => localStorage.getItem('mobile.activePanel')
  );
  const [lapsCollapsed, setLapsCollapsed] = useState(
    () => localStorage.getItem('trackapp:lapsCollapsed') === 'true'
  );
  useEffect(() => {
    localStorage.setItem('trackapp:lapsCollapsed', String(lapsCollapsed));
  }, [lapsCollapsed]);
  const [analysisHeaderCollapsed, setAnalysisHeaderCollapsed] = useState(
    () => localStorage.getItem('trackapp:analysisHeaderCollapsed') === 'true'
  );
  const [panelAreaBounds, setPanelAreaBounds] = useState(null);
  useEffect(() => {
    localStorage.setItem('trackapp:analysisHeaderCollapsed', String(analysisHeaderCollapsed));
  }, [analysisHeaderCollapsed]);

  // ── Session 1 ─────────────────────────────────────────────────────────────
  const {
    session, setSession, sessionId, loading, error, uploadProgress,
    duplicateInfo, forceUpload, clearDuplicate,
    lapTraces, loadingTraces, upload, uploadMov, uploadXrk, uploadMotec, uploadSmartycam3, loadSession, fetchTrace,
    resetSession,
    tbMeta, fetchTb,
  } = useSession();
  const TB_LAP_INDEX = -1;

  // Track the live desktop panel area. Header collapse changes both its top and
  // height; these bounds drive the display-only vertical layout transform.
  useEffect(() => {
    if (isMobile || !mainAreaRef.current) return undefined;
    const element = mainAreaRef.current;
    const measure = () => {
      const rect = element.getBoundingClientRect();
      const next = { top: rect.top, height: rect.height };
      setPanelAreaBounds(prev => (
        prev && Math.abs(prev.top - next.top) < 0.5 && Math.abs(prev.height - next.height) < 0.5
          ? prev
          : next
      ));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [isMobile, sessionId, session]);

  // Bumped on every successful upload so UploadPanel re-fetches its session list.
  // The app-level upload paths (incl. force-past-duplicate) open the new session
  // in the analysis view; this guarantees the list reflects it on return rather
  // than relying solely on remount timing.
  const [uploadSignal, setUploadSignal] = useState(0);
  // Set when an upload's file contained multiple RaceChrono fragments
  // (separate recording sessions bundled into one CSV export) — the app
  // stays on the sessions list rather than jumping into any one of them.
  const [multiSessionNotice, setMultiSessionNotice] = useState('');
  const noteUploaded = (d) => {
    if (d?.session_id) setUploadSignal(n => n + 1);
    if (d?.additional_session_ids?.length) {
      const n = d.additional_session_ids.length;
      setMultiSessionNotice(
        `This file contained ${n + 1} separate track sessions — ${n} additional ` +
        `session${n > 1 ? 's were' : ' was'} created and added to Recent Sessions.`
      );
    }
    return d;
  };

  const [selectedLaps, setSelectedLaps] = useState([]);
  const [corners, setCorners]           = useState([]);
  const [sectors, setSectors]           = useState([]);
  const [trackId, setTrackId]           = useState(null);
  const [trackName, setTrackName]       = useState('');
  // Canonical start/finish {lat, lon} from the track definition — the reference
  // for detecting sessions segmented from a different (device-beacon) S/F.
  const [trackStartFinish, setTrackStartFinish] = useState(null);
  const [trackElevationProfile, setTrackElevationProfile] = useState([]);
  // 'primary' | slot number while a re-align request is in flight, else null.
  const [realigningSlot, setRealigningSlot] = useState(null);

  // ── Comparison drivers (up to MAX_COMPARE) ───────────────────────────────
  // Each comparison slot is an independent useSession() hook. We call a fixed
  // number unconditionally (rules of hooks) and treat slots with a loaded
  // session as active. Slot 0 is the "primary comparison" used by the URL
  // ?compare param, the AI briefing, and the video panel.
  const cmp0 = useSession();
  const cmp1 = useSession();
  const cmp2 = useSession();
  const cmpHooks = [cmp0, cmp1, cmp2];
  // Stable per-slot functions (memoized inside useSession) for slot-indexed use.
  const cmpLoad  = [cmp0.loadSession,  cmp1.loadSession,  cmp2.loadSession];
  const cmpReset = [cmp0.resetSession, cmp1.resetSession, cmp2.resetSession];
  const cmpFetch = [cmp0.fetchTrace,   cmp1.fetchTrace,   cmp2.fetchTrace];
  const cmpSessionIds = [cmp0.sessionId, cmp1.sessionId, cmp2.sessionId];
  const cmpSessionIdKey = cmpSessionIds.join(',');

  // Per-slot comparison state (fixed-length arrays indexed by slot 0..MAX_COMPARE-1).
  const [cmpSelectedLaps,   setCmpSelectedLaps]   = useState([[], [], []]);
  const [cmpMetrics,        setCmpMetrics]        = useState([null, null, null]);
  const [cmpMetricsLoading, setCmpMetricsLoading] = useState([false, false, false]);
  const [cmpMetricsError,   setCmpMetricsError]   = useState([null, null, null]);
  const [cmpSectorMetrics,  setCmpSectorMetrics]  = useState([null, null, null]);
  const [cmpSectorTimesState, setCmpSectorTimesState] = useState([null, null, null]);

  const resetAllCmpMetrics = useCallback(() => {
    setCmpMetrics([null, null, null]);
    setCmpSectorMetrics([null, null, null]);
  }, []);

  // ── Deep-link: ?session=<id> ─────────────────────────────────────────────
  // Auto-load the session from the URL on first render (post-auth). In public
  // (share-link) mode the session id comes from the resolved token instead.
  //
  // Lap selections from a copied view link (?laps=…) are staged here, keyed by
  // session id so async load order doesn't matter, and consumed by the session
  // init effects below. restoredPrimaryRef marks that Driver 1's selection came
  // from a link, so the first comparison load must not collapse it to best-lap.
  const pendingLapRestoreRef = useRef(new Map());
  const restoredPrimaryRef = useRef(false);
  // Set when a comparison session arrives before the primary has loaded, so the
  // best-vs-best collapse can't run yet — the primary init effect honours it.
  const pendingCollapsePrimaryRef = useRef(false);
  // Run-once latch: StrictMode re-fires mount effects in dev; a second
  // loadSession() would commit a fresh session object after the one-shot
  // restore/collapse flags were consumed, stomping the applied lap selection.
  const deepLinkRanRef = useRef(false);
  useEffect(() => {
    if (deepLinkRanRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const id = restricted ? initialSessionId : params.get('session');
    if (!id) return;
    deepLinkRanRef.current = true;
    // ?compare=<id>[,<id>,<id>] loads up to MAX_COMPARE comparison sessions.
    const compareIds = restricted ? [] :
      (params.get('compare') || '').split(',')
        .map(s => s.trim())
        .filter(cid => cid && cid !== id)
        .slice(0, MAX_COMPARE);
    // ?laps=<i,i>|<i>|… — selected lap indices per driver [primary, ...compare].
    const lapsParam = restricted ? null : params.get('laps');
    if (lapsParam) {
      const perDriver = lapsParam.split('|').map(seg =>
        seg.split(',').map(Number).filter(n => Number.isInteger(n) && n >= 0));
      [id, ...compareIds].forEach((sid, i) => {
        if (perDriver[i]?.length) pendingLapRestoreRef.current.set(sid, perDriver[i]);
      });
      restoredPrimaryRef.current = pendingLapRestoreRef.current.has(id);
      // Consume the param so refresh/Back doesn't re-apply a stale selection.
      const url = new URL(window.location.href);
      url.searchParams.delete('laps');
      history.replaceState(null, '', url);
    }
    loadSession(id).then(data => {
      if (data) {
        setCanComment(data.can_comment !== false);
        setIsOwner(data.is_owner !== false);
        // Remember guest uploads in this browser so they show in the guest home.
        if (guestMode) addGuestSession({ id, name: data?.meta?.session_name, date: data?.meta?.date });
      }
    });
    compareIds.forEach((cid, slot) => cmpLoad[slot](cid));
  }, [loadSession]); // eslint-disable-line react-hooks/exhaustive-deps

  // Address-bar sync lives below, after the `view`/leaderboard state is declared
  // (it depends on `view`, which is declared later — referencing it here would TDZ).


  // Per-user, per-session lap color overrides, persisted via user preferences
  // (see the prefs load/save effects below): { [sessionId]: { [lapIndex]: '#hex' } }.
  // Keyed by (session_id, lap_index) — safe as long as an existing session's laps
  // are never renumbered in place (true today; see CLAUDE.md on disk-reload vs
  // re-parse). A future "reprocess this session's laps" admin tool would silently
  // invalidate entries here.
  const [lapColorsByServer, setLapColorsByServer] = useState(null); // null = not yet loaded from server
  // Sticky per-user color for "my fastest lap" (primary/owned sessions only),
  // persisted alongside lapColorsByServer via the same preferences effects below.
  const [fastestLapColor, setFastestLapColor] = useState(null);

  // NOTE: customColors1/cmpCustomColors/lapColorMap/cmpLapColorMaps are declared
  // further below, after `isOwner`, since the fastest-lap override needs it.

  // ── Driver profile ────────────────────────────────────────────────────────
  const [driverProfile, setDriverProfile] = useState(() => {
    const stored = loadDriverProfile(currentUser?.id, !currentUser?.masquerade);
    if (currentUser?.display_name) {
      return {
        name:       currentUser.display_name,
        experience: currentUser.driver_experience || stored?.experience || 'hpde_intermediate',
        car:        currentUser.driver_car        || stored?.car        || '',
        notes:      currentUser.driver_notes      || stored?.notes      || '',
      };
    }
    return stored;
  });
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [adminModeActive, setAdminModeActive] = useState(
    () => localStorage.getItem('ta_adminMode') !== 'false'
  );
  const toggleAdminMode = useCallback(() => {
    setAdminModeActive(previous => {
      const next = !previous;
      localStorage.setItem('ta_adminMode', String(next));
      return next;
    });
  }, []);
  const [settingsInitialTab, setSettingsInitialTab] = useState('profile');
  // Cars-tab auto-open: set when a session's "Car Physics" shortcut opens
  // Settings straight onto that car's physics editor (see handleOpenCarPhysics).
  const [settingsInitialCarId, setSettingsInitialCarId] = useState(null);
  const [settingsAutoOpenPhysics, setSettingsAutoOpenPhysics] = useState(false);
  const [studioSeed, setStudioSeed] = useState(null);
  const [view, setView] = useState('sessions'); // 'sessions' | 'leaderboard' | 'progress' | 'channelstats' | 'search' | 'simlab'
  // Each analytics page's full filter/selection state, owned here so it survives
  // that page unmounting (view switch) and round-trips through the URL (?q=) so
  // Back/Forward restores it. null = page hasn't reported its state yet (fresh
  // deep-link/nav — the page falls back to its own defaults, seeded only with
  // {trackId} when arriving from handleGoToX below).
  const [leaderboardPageParams, setLeaderboardPageParams] = useState(null);
  const [progressPageParams, setProgressPageParams] = useState(null);
  const [channelStatsPageParams, setChannelStatsPageParams] = useState(null);
  const [searchPageParams, setSearchPageParams] = useState(null);
  // Session report: { session_ids, title?, template_id?, definition? } — set by
  // the Sessions page's Report button or restored from ?q=.
  const [reportPageParams, setReportPageParams] = useState(null);
  const [simLabInitialTrackId, setSimLabInitialTrackId] = useState(null);
  // Admin issue queue: ?issue=<id> deep link (from the notification email) and
  // the open count shown as a badge on the nav item.
  const [issuesInitialId, setIssuesInitialId] = useState(null);
  const [openIssueCount, setOpenIssueCount] = useState(null);
  // Set synchronously when a session-loading navigation starts (onLoadSession
  // handlers below), before the async fetch resolves and `sessionId` itself
  // updates — see the URL-sync effect for why this avoids a spurious extra
  // history entry.
  const [pendingSessionId, setPendingSessionId] = useState(null);

  // ── Deep-link: ?view=leaderboard|progress[&track=<id>] ───────────────────
  // Land directly on the leaderboard/progress view from a bookmark/shared URL.
  // Honoured only for full (non-restricted) users — guests/public share views
  // can't load it.
  useEffect(() => {
    if (restricted) return;
    const params = new URLSearchParams(window.location.search);
    const pageParams = parsePageParams(params);
    if (params.get('view') === 'studio') {
      setView('studio');
    } else if (params.get('view') === 'leaderboard' && productCapabilities.public_leaderboards) {
      setLeaderboardPageParams(pageParams);
      setView('leaderboard');
    } else if (params.get('view') === 'progress' && productCapabilities.cross_session_analytics) {
      setProgressPageParams(pageParams);
      setView('progress');
    } else if (params.get('view') === 'channelstats' && productCapabilities.cross_session_analytics) {
      setChannelStatsPageParams(pageParams);
      setView('channelstats');
    } else if (params.get('view') === 'search' && productCapabilities.cross_session_analytics) {
      setSearchPageParams(pageParams);
      setView('search');
    } else if (params.get('view') === 'report' && productCapabilities.cross_session_analytics) {
      setReportPageParams(pageParams);
      setView('report');
    } else if (params.get('view') === 'simlab') {
      // Admin-only view — enforcement happens at the render block, so a
      // non-admin deep link just falls through to the sessions view.
      setSimLabInitialTrackId(params.get('track') || null);
      setView('simlab');
    } else if (params.get('view') === 'issues') {
      setIssuesInitialId(params.get('issue') || null);
      setView('issues');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Deep-link: ?settings[=<tab>] ─────────────────────────────────────────
  // Open the account settings modal straight from an email link (e.g. the
  // "X is now following you" notification → ?settings=following). Honoured only
  // for full (non-restricted) users, who are the only ones with an account.
  useEffect(() => {
    if (restricted || !productCapabilities.user_accounts) return;
    const params = new URLSearchParams(window.location.search);
    if (!params.has('settings')) return;
    const KNOWN_TABS = ['profile', 'groups', 'following', 'security', 'apikeys'];
    const tab = params.get('settings');
    setSettingsInitialTab(KNOWN_TABS.includes(tab) ? tab : 'profile');
    setShowSettingsModal(true);
    // Consume the param so closing/reopening the modal doesn't re-trigger it.
    const url = new URL(window.location.href);
    url.searchParams.delete('settings');
    history.replaceState(null, '', url);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Browser-history integration (refs so they don't retrigger renders):
  //   navStateRef          — the last "navigation identity" we synced (the
  //     top-level view, or the primary session id within the sessions view). A
  //     change in this identity is what earns a real Back entry.
  //   hydratingFromUrlRef  — true while we are applying URL → state (the initial
  //     deep-link load, or a Back/Forward pop). While true we never write history
  //     (the browser already owns the URL) and we release it once state has caught
  //     up to the URL. Starts true to cover the initial deep-link hydration.
  const navStateRef = useRef(null);
  const hydratingFromUrlRef = useRef(true);

  // Keep the address bar in sync so the current view is always shareable, and turn
  // genuine navigations (switching the top-level view, or loading a different
  // primary session) into real history entries so Back moves *within* the app
  // instead of falling through to the landing/share page. Compare add/remove and
  // within-leaderboard track changes refine the current screen → replace, not push.
  // Skipped in public mode — the URL there is /s/<token>. Only the four params we
  // own are touched, so anything else in the query string is preserved.
  useEffect(() => {
    if (publicMode) return;
    const url = new URL(window.location.href);
    OWNED_URL_PARAMS.forEach(k => url.searchParams.delete(k));
    if (view === 'studio') {
      url.searchParams.set('view', 'studio');
    } else if (view === 'leaderboard') {
      url.searchParams.set('view', 'leaderboard');
      if (leaderboardPageParams) url.searchParams.set('q', JSON.stringify(leaderboardPageParams));
    } else if (view === 'progress') {
      url.searchParams.set('view', 'progress');
      if (progressPageParams) url.searchParams.set('q', JSON.stringify(progressPageParams));
    } else if (view === 'channelstats') {
      url.searchParams.set('view', 'channelstats');
      if (channelStatsPageParams) url.searchParams.set('q', JSON.stringify(channelStatsPageParams));
    } else if (view === 'search') {
      url.searchParams.set('view', 'search');
      if (searchPageParams) url.searchParams.set('q', JSON.stringify(searchPageParams));
    } else if (view === 'report') {
      url.searchParams.set('view', 'report');
      if (reportPageParams) url.searchParams.set('q', JSON.stringify(reportPageParams));
    } else if (view === 'simlab') {
      url.searchParams.set('view', 'simlab');
      if (simLabInitialTrackId) url.searchParams.set('track', simLabInitialTrackId);
    } else if (view === 'issues') {
      url.searchParams.set('view', 'issues');
      if (issuesInitialId) url.searchParams.set('issue', issuesInitialId);
    } else if (pendingSessionId || sessionId) {
      // A pending load (still in flight) always wins over whatever session
      // happens to still be sitting in state from before this navigation — the
      // analytics pages don't unmount the previously-loaded session, so
      // `sessionId` can be stale here (see the pendingSessionId comment above).
      url.searchParams.set('session', pendingSessionId || sessionId);
      const compareList = cmpSessionIds.filter(Boolean).join(',');
      if (compareList) url.searchParams.set('compare', compareList);
    }

    const navKey = view === 'studio' ? 'studio' : view === 'leaderboard' ? 'leaderboard'
      : view === 'progress' ? 'progress'
      : view === 'channelstats' ? 'channelstats'
      : view === 'search' ? 'search'
      : view === 'report' ? 'report'
      : view === 'simlab' ? 'simlab'
      : view === 'issues' ? 'issues'
      : (pendingSessionId || sessionId || 'home');

    // Applying URL → state: leave history alone; release the latch once our state
    // matches the URL the browser is already showing (handles async session loads).
    if (hydratingFromUrlRef.current) {
      navStateRef.current = navKey;
      if (window.location.search === url.search) hydratingFromUrlRef.current = false;
      return;
    }

    const prev = navStateRef.current;
    navStateRef.current = navKey;
    // Guests (restricted but not public) keep the legacy replace-only behaviour.
    const isNavigation = !restricted && prev !== null && prev !== navKey;
    if (isNavigation) history.pushState(null, '', url);
    else history.replaceState(null, '', url);
  }, [view, leaderboardPageParams, progressPageParams, channelStatsPageParams, searchPageParams, reportPageParams, simLabInitialTrackId, sessionId, pendingSessionId, cmpSessionIdKey, publicMode, restricted]);

  // Back/Forward within the app: re-hydrate state from the URL the browser just
  // restored, rather than letting the press fall through to a full document load.
  // Only full (non-restricted) users have an in-app history stack to walk.
  useEffect(() => {
    if (restricted) return;
    function onPopState() {
      hydratingFromUrlRef.current = true;
      const params = new URLSearchParams(window.location.search);
      if (params.get('view') === 'studio') { setView('studio'); return; }
      if (params.get('view') === 'leaderboard') {
        setLeaderboardPageParams(parsePageParams(params));
        setView('leaderboard');
        return;
      }
      if (params.get('view') === 'progress') {
        setProgressPageParams(parsePageParams(params));
        setView('progress');
        return;
      }
      if (params.get('view') === 'channelstats') {
        setChannelStatsPageParams(parsePageParams(params));
        setView('channelstats');
        return;
      }
      if (params.get('view') === 'search') {
        setSearchPageParams(parsePageParams(params));
        setView('search');
        return;
      }
      if (params.get('view') === 'report') {
        setReportPageParams(parsePageParams(params));
        setView('report');
        return;
      }
      if (params.get('view') === 'simlab') {
        setSimLabInitialTrackId(params.get('track') || null);
        setView('simlab');
        return;
      }
      if (params.get('view') === 'issues') {
        setIssuesInitialId(params.get('issue') || null);
        setView('issues');
        return;
      }
      setView('sessions');
      const id = params.get('session');
      if (id && id !== sessionId) {
        loadSession(id).then(data => {
          if (data) {
            setCanComment(data.can_comment !== false);
            setIsOwner(data.is_owner !== false);
          }
        });
      } else if (!id && sessionId) {
        resetSession();
      }
      const urlCompareIds = (params.get('compare') || '').split(',')
        .map(s => s.trim()).filter(Boolean).slice(0, MAX_COMPARE);
      cmpSessionIds.forEach((cur, slot) => {
        const want = urlCompareIds[slot] || null;
        if (want && want !== cur) cmpLoad[slot](want);
        else if (!want && cur) cmpReset[slot]();
      });
    }
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [restricted, sessionId, cmpSessionIdKey, loadSession, resetSession]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Panel visibility + window layout (managed per-tab) ───────────────────
  const [channelTracesMap,  setChannelTracesMap]  = useState({});
  const [primarySectorTimesState, setPrimarySectorTimesState] = useState(null);
  const currentPrimarySectorTimes = primarySectorTimesState?.sessionId === sessionId
    ? primarySectorTimesState
    : null;
  const sectorTimesData = currentPrimarySectorTimes?.data || null;
  const sectorTimesLoading = currentPrimarySectorTimes
    ? currentPrimarySectorTimes.loading
    : Boolean(sessionId && sectors.length);
  const sectorTimesError = currentPrimarySectorTimes?.error || null;
  const [lapSelectionMode,  setLapSelectionMode]  = useState({ type: 'best', count: 3 });
  const [canComment,        setCanComment]        = useState(true);
  const [isOwner,           setIsOwner]           = useState(true);

  // Derived per-lap color override view for the primary driver. The sticky
  // fastest-lap color only applies when this is the user's own session (not one
  // shared to them by someone else and loaded as primary) — otherwise a color
  // picked on the user's own fastest lap would bleed onto someone else's driving.
  const customColors1 = useMemo(() => {
    const base = (sessionId && lapColorsByServer?.[sessionId]) || EMPTY_COLORS;
    if (!isOwner || !fastestLapColor || !session?.laps?.length) return base;
    const bestTime = getBestLapTime(session.laps);
    const bestLap = session.laps.find(l => isBestLap(l, bestTime));
    return bestLap ? { ...base, [bestLap.index]: fastestLapColor } : base;
  }, [sessionId, lapColorsByServer, fastestLapColor, isOwner, session?.laps]);
  // Derived per-slot views for comparison drivers (no fastest-lap override here).
  const cmpCustomColors = useMemo(
    () => cmpSessionIds.map(id => (id && lapColorsByServer?.[id]) || EMPTY_COLORS),
    [cmpSessionIdKey, lapColorsByServer] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Resolved color map for VideoPanel lap switcher chips (Driver 1 only, excludes TB)
  const lapColorMap = useMemo(() => {
    const map = {};
    selectedLaps.forEach(lapIndex => {
      if (lapIndex === TB_LAP_INDEX) return;
      map[lapIndex] = getLapColor(lapIndex, selectedLaps, LAP_COLORS, customColors1);
    });
    return map;
  }, [selectedLaps, customColors1]);

  // Per-slot resolved color maps for comparison drivers (used by the video panel).
  const cmpLapColorMaps = useMemo(() =>
    cmpSelectedLaps.map((sel, slot) => {
      const map = {};
      sel.forEach(lapIndex => {
        if (lapIndex === TB_LAP_INDEX) return;
        map[lapIndex] = getLapColor(lapIndex, sel, COMPARE_PALETTES[slot], cmpCustomColors[slot]);
      });
      return map;
    }),
    [cmpSelectedLaps, cmpCustomColors]);

  // ── Formula panels ────────────────────────────────────────────────────────
  // formulas: FormulaOut[] — user's saved formula library (from server)
  // formulaTracesMap: { [formulaId]: { [lapIndex]: LapTraceOut } }
  // formulaEditorOpen: null | 'create' | FormulaOut (formula to edit)
  const [formulas,          setFormulas]          = useState([]);
  const [formulaTracesMap,  setFormulaTracesMap]  = useState({});
  const [formulaEditorOpen, setFormulaEditorOpen] = useState(null);
  // cmpFormulaTraces: per-slot { [formulaId]: { [lapIndex]: LapTraceOut } } —
  // the same formulas re-evaluated against each comparison driver's session, so
  // a formula panel overlays every driver the way the channel panels do.
  const [cmpFormulaTraces, setCmpFormulaTraces] = useState([{}, {}, {}]);

  // ── Unit preferences ──────────────────────────────────────────────────────
  const [unitPrefs,        setUnitPrefs]        = useState(() => loadUnitPrefs());
  const [showEcuModal, setShowEcuModal] = useState(false);
  const [showRenderModal, setShowRenderModal] = useState(false);
  // The lap set the 3D ghost race is open on (null = closed). Holding the laps
  // rather than a boolean means the modal is keyed by what it's showing.
  const [lap3dLaps, setLap3dLaps] = useState(null);
  // The lap set the 3D *panel* is racing. Separate from the modal's: a panel
  // outlives any one selection, and seeding it explicitly (rather than tracking
  // selectedLaps) is what stops a stray tick in the lap list from tearing the
  // scene down mid-corner. Held in state so the array identity is stable —
  // Lap3DView keys its fetch on the content, but a new array every render would
  // still churn the memos.
  const [lap3dPanelLaps, setLap3dPanelLaps] = useState(null);

  const [showShareModal, setShowShareModal] = useState(false);
  const [sessionShared, setSessionShared] = useState(false);

  // ── Power panel (virtual dyno) ──────────────────────────────────────────────
  // powerTracesMap: { [lapIndex]: LapTraceOut } for the primary driver.
  // cmpPowerTraces: per-slot { [lapIndex]: LapTraceOut } for comparison drivers.
  // vehicleParams: primary car params (auto-loaded, panel-editable).
  // cmpVehicleParams: per-slot comparison car params (auto-loaded only).
  const [powerTracesMap,  setPowerTracesMap]  = useState({});
  const [cmpPowerTraces,  setCmpPowerTraces]  = useState([{}, {}, {}]);

  // cmpChannelTraces: per-slot { [channelName]: { [lapIndex]: LapTraceOut } }
  // for arbitrary (non-canonical) channels picked from a comparison driver.
  const [cmpChannelTraces, setCmpChannelTraces] = useState([{}, {}, {}]);

  // The scatter plot picks its axes by channel name and plots every driver
  // that logs that name, so each comparison driver carries its own per-slot
  // channel cache and the set of names its logger recorded. Memoized on the
  // underlying state (the useSession objects are rebuilt every render) so the
  // plot's fetch-on-miss effects only re-run when something actually changed.
  const scatterCompareDrivers = useMemo(() => cmpHooks.map((h, slot) => h.session ? ({
    slot,
    name: h.session.meta?.racer || `Driver ${slot + 2}`,
    lapTraces: h.lapTraces,
    selectedLaps: cmpSelectedLaps[slot],
    palette: COMPARE_PALETTES[slot],
    customColors: cmpCustomColors[slot],
    channelTraces: cmpChannelTraces[slot] || {},
    channels: new Set(h.session.channels || []),
  }) : null).filter(Boolean), [
    cmp0.session, cmp1.session, cmp2.session,
    cmp0.lapTraces, cmp1.lapTraces, cmp2.lapTraces,
    cmpSelectedLaps, cmpCustomColors, cmpChannelTraces,
  ]); // eslint-disable-line react-hooks/exhaustive-deps
  const [vehicleParams,   setVehicleParams]   = useState(null);
  const [cmpVehicleParams, setCmpVehicleParams] = useState([null, null, null]);
  const vehicleParamsRef   = useRef(null);
  const cmpVehicleParamsRef = useRef([null, null, null]);
  useEffect(() => { vehicleParamsRef.current    = vehicleParams;    }, [vehicleParams]);
  useEffect(() => { cmpVehicleParamsRef.current = cmpVehicleParams; }, [cmpVehicleParams]);

  // ── Tab manager — owns panel visibility + window layout for each tab ─────
  const {
    tabs, activeTabId,
    winLayout, updateWindow, getChannelLayout, resetLayout, arrangeLayout, applyServerLayout,
    panelVisibility, updatePanelVisibility, applyServerVisibility,
    selectedChannelNames, updateSelectedChannelNames, applyServerChannels,
    valueChannelNames, updateValueChannelNames, applyServerValueChannels,
    channelPanelConfigs, updateChannelPanelConfigs, applyServerChannelPanelConfigs,
    createTab, renameTab, deleteTab, switchTab,
  } = useTabManager();

  const getWorkspaceLayoutBounds = useCallback(() => {
    const rect = mainAreaRef.current?.getBoundingClientRect();
    return rect ? {
      // Layouts are persisted in expanded-sidebar + expanded-header
      // coordinates; display remaps handle collapsed chrome losslessly.
      left: lapsCollapsed ? EXPANDED_SIDEBAR : rect.left,
      top: analysisHeaderCollapsed ? rect.top + ANALYSIS_CHROME_DELTA : rect.top,
      width: lapsCollapsed ? window.innerWidth - EXPANDED_SIDEBAR : rect.width,
      height: analysisHeaderCollapsed ? rect.height - ANALYSIS_CHROME_DELTA : rect.height,
    } : null;
  }, [lapsCollapsed, analysisHeaderCollapsed]);

  const resetWorkspaceLayout = useCallback((preset = 'standard') => {
    resetLayout(preset, getWorkspaceLayoutBounds());
  }, [resetLayout, getWorkspaceLayoutBounds]);

  // True once we know the account has never saved a layout — drives the
  // "apply Delta preset for new users" effect below. Set from the user
  // preferences fetch further down in this component.
  const [needsDefaultLayout, setNeedsDefaultLayout] = useState(false);

  // Public (account-free) viewers have no saved preferences, so default them to
  // the "Standard" layout once the shared session loads.
  // Placed after useTabManager so resetLayout is initialized before this runs.
  const publicLayoutAppliedRef = useRef(false);
  useEffect(() => {
    if (!restricted || !session || publicLayoutAppliedRef.current) return;
    publicLayoutAppliedRef.current = true;
    resetWorkspaceLayout('standard');
  }, [restricted, session, resetWorkspaceLayout]);

  // Logged-in accounts that have never saved a layout (fresh signups, see
  // needsDefaultLayout below) get the "Delta" preset once their first session
  // loads, instead of the bare Speed Trace + Map fallback.
  const newUserLayoutAppliedRef = useRef(false);
  useEffect(() => {
    if (restricted || !session || !needsDefaultLayout || newUserLayoutAppliedRef.current) return;
    newUserLayoutAppliedRef.current = true;
    resetWorkspaceLayout('delta');
  }, [restricted, session, needsDefaultLayout, resetWorkspaceLayout]);

  // Mirror the loaded session's share status into local state so the header's
  // share button can reflect it (and update optimistically from the modal).
  useEffect(() => {
    setSessionShared(!!session?.has_shares);
  }, [sessionId, session?.has_shares]);

  const {
    showMap, showSpeedTrace, showGTrace, showDeltaTrace, showVideo,
    showMetrics, showPromptPanel, showFrictionCircle, showScatterPlot,
    showComments, showSplits, showPowerTrace, showHeadToHead, showLap3D,
    showTrackDifference,
  } = panelVisibility;

  useTabHotkeys({
    tabs,
    activeTabId,
    onSwitch: switchTab,
    onCreate: createTab,
    onClose:  deleteTab,
  });

  useChartKeyboardNav();

  // ── Delta base-lap override — shared by TimeDeltaTrace and the map ribbon.
  // null = auto (fastest selected lap). Reset whenever the session changes so
  // a stale lap index can't silently match a lap in the new session.
  const [deltaBaseOverride, setDeltaBaseOverride] = useState(null);
  useEffect(() => { setDeltaBaseOverride(null); }, [sessionId]);
  const [trackDifferencePair, setTrackDifferencePair] = useState(null);
  useEffect(() => { setTrackDifferencePair(null); }, [sessionId]);

  // ── Global zoom state (drives header Reset Zoom button) ──────────────────
  const studioDistanceRange = useRef(null);
  const [isZoomed, setIsZoomed] = useState(false);
  useEffect(() => subscribeZoom(domain => { studioDistanceRange.current = domain; setIsZoomed(domain != null); }), []);

  const handleResetZoom = useCallback(() => {
    publishZoom(null);
    window.dispatchEvent(new CustomEvent('trackapp:chart-zoom', { detail: null }));
  }, []);

  // ── Panel maximize (per-tab) ──────────────────────────────────────────────
  const [maximizedByTab, setMaximizedByTab] = useState({});
  const maximizedPanelId = maximizedByTab[activeTabId] ?? null;

  const setMaximizedPanelId = (updater) =>
    setMaximizedByTab(prev => ({
      ...prev,
      [activeTabId]: typeof updater === 'function' ? updater(prev[activeTabId] ?? null) : updater,
    }));

  // Escape restores from maximize for the active tab
  useEffect(() => {
    if (!maximizedPanelId) return;
    const handler = (e) => {
      if (e.key === 'Escape')
        setMaximizedByTab(prev => ({ ...prev, [activeTabId]: null }));
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [maximizedPanelId, activeTabId]); // eslint-disable-line react-hooks/exhaustive-deps

  const shouldShow = (panelId) => !maximizedPanelId || maximizedPanelId === panelId;
  const maxProps   = (panelId) => ({
    maximized:              maximizedPanelId === panelId,
    onMaximize:             () => setMaximizedPanelId(v => v === panelId ? null : panelId),
    maximizedContainerRef:  mainAreaRef,
    dragBounds:             '#analysis-panel-area',
  });

  // All current window layouts merged into one object — passed to every
  // WindowFrame so it can snap against its siblings during drag. Channel
  // panels are keyed by plain channel name (matching the `ch_${name}`
  // panel id) — selectedChannelNames holds driver-tagged compound keys, so
  // dedupe back down to the distinct names a panel actually exists for
  // (same-named picks from different drivers share one panel).
  const allLayouts = useMemo(() => {
    const channelNames = [...new Set(selectedChannelNames.map(k => parseChannelKey(k).name))];
    return {
      ...winLayout,
      ...Object.fromEntries(
        channelNames.map((name, i) => [`ch_${name}`, getChannelLayout(name, i)])
      ),
      ...Object.fromEntries(
        formulas.map((f, i) => [`fml_${f.id}`, winLayout[`fml_${f.id}`] ?? formulaDefault(i)])
      ),
    };
  }, [winLayout, selectedChannelNames, getChannelLayout, formulas]);

  // ── Collapsible chrome: display-space remap + lossless persistence ───
  // dispLayout remaps a stored rect for display; handleWindowUpdate inverse-maps
  // user drags/resizes so the canonical store always stays in expanded-space
  // for both the LAPS rail and the analysis header.
  const dispLayout = useCallback(
    (rect) => remapRect(rect, lapsCollapsed, analysisHeaderCollapsed, panelAreaBounds),
    [lapsCollapsed, analysisHeaderCollapsed, panelAreaBounds]
  );
  const handleWindowUpdate = useCallback(
    (id, rect) => updateWindow(
      id,
      inverseRemapRect(rect, lapsCollapsed, analysisHeaderCollapsed, panelAreaBounds),
    ),
    [updateWindow, lapsCollapsed, analysisHeaderCollapsed, panelAreaBounds]
  );
  const dispAllLayouts = useMemo(() => {
    if (!lapsCollapsed && !analysisHeaderCollapsed) return allLayouts;
    return Object.fromEntries(
      Object.entries(allLayouts).map(([k, v]) => [
        k,
        remapRect(v, lapsCollapsed, analysisHeaderCollapsed, panelAreaBounds),
      ])
    );
  }, [allLayouts, lapsCollapsed, analysisHeaderCollapsed, panelAreaBounds]);

  // ── Server feature flags ──────────────────────────────────────────────────
  const [enableAi, setEnableAi] = useState(true);
  // Dedicated kill switch for the first-class AI Debrief. This deliberately
  // defaults off if /config is unavailable so a temporary pause fails closed.
  const [enableAiDebrief, setEnableAiDebrief] = useState(false);
  // Trace-native coaching surfaces: the pre-analysis reflection prompt and
  // retrieved background reading in the chat. Off by default, admin-visible,
  // because both depend on migrations and a local embedding model.
  const [traceCoaching, setTraceCoaching] = useState(false);
  // Which Ollama model the coaching flows ask for, and how big a context to
  // request. Server-owned so changing models is a backend setting, not a
  // rebuild; empty values mean "use the built-in defaults".
  const [coachModel, setCoachModel] = useState({
    model: '', fallbackModel: '', numCtx: 0,
  });

  useEffect(() => {
    const applyConfig = (cfg) => {
      setEnableAi(cfg.enable_ai !== false);
      setEnableAiDebrief(cfg.enable_ai_debrief === true);
      setTraceCoaching(!!cfg.enable_trace_coaching);
      setCoachModel({
        model: cfg.coach_model || '',
        fallbackModel: cfg.coach_fallback_model || '',
        numCtx: cfg.coach_num_ctx || 0,
      });
    };
    if (appConfig) applyConfig(appConfig);
    else getAppConfig().then(applyConfig).catch(() => {});
  }, [appConfig]);

  // The AI Briefing panel (PromptPanel) is a debug surface — raw prompts,
  // model picker, unformatted output. Admin-only regardless of enable_ai;
  // regular users get AI through the inline Head-to-Head coach instead.
  const aiDebug = enableAi && !!currentUser?.is_admin;

  // One recap controller serves the first-class dialog and the Corner Metrics
  // panel. Closing either view leaves the stream running for the other.
  const sessionRecap = useSessionRecap({
    sessionId,
    corners,
    enabled: enableAi && enableAiDebrief,
    allowPromptDebug: aiDebug,
    traceCoaching,
    coachModel,
  });
  const [showSessionRecap, setShowSessionRecap] = useState(false);
  const [metricsFocusRequest, setMetricsFocusRequest] = useState(null);
  const handleMetricsFocusHandled = useCallback(() => setMetricsFocusRequest(null), []);
  const handleCloseSessionRecap = useCallback(() => setShowSessionRecap(false), []);
  const handleOpenSessionRecap = useCallback(() => {
    if (!enableAiDebrief) return;
    setShowSessionRecap(true);
    if (!sessionRecap.recap && !sessionRecap.busy) sessionRecap.generate();
  }, [enableAiDebrief, sessionRecap.busy, sessionRecap.generate, sessionRecap.recap]);
  const handleOpenRecapDeepDive = useCallback((cornerId = null) => {
    setShowSessionRecap(false);
    if (cornerId != null) {
      setMetricsFocusRequest(current => ({
        cornerId,
        requestId: (current?.requestId || 0) + 1,
      }));
    }
    if (isMobile) {
      setActiveMobilePanel('metrics');
      try { localStorage.setItem('mobile.activePanel', 'metrics'); }
      catch { /* localStorage unavailable */ }
    } else {
      updatePanelVisibility({ showMetrics: true });
    }
  }, [isMobile, updatePanelVisibility]);
  useEffect(() => {
    setShowSessionRecap(false);
    setMetricsFocusRequest(null);
  }, [sessionId]);

  // ── User preferences (panel layout + visibility persisted per user) ──────
  // prefsLoadedRef stays false in public mode, which also disables the save
  // effect below — anonymous viewers neither load nor persist personal prefs.
  const prefsLoadedRef = useRef(false);
  // When true, every new upload is auto-shared with all logged-in users
  // (comment permission). Read by the backend on each upload via
  // preferences["auto_share"]; the snake_case key must match.
  const [autoShare, setAutoShare] = useState(false);

  useEffect(() => {
    if (restricted) return;
    Promise.all([
      getUserPreferences(),
      getFormulas(),
    ]).then(([prefs, formulaList]) => {
      if (prefs.panelVisibility)      applyServerVisibility(prefs.panelVisibility);
      if (prefs.windowLayout)         applyServerLayout(prefs.windowLayout);
      setNeedsDefaultLayout(!prefs.panelVisibility && !prefs.windowLayout);
      if (prefs.selectedChannelNames) applyServerChannels(prefs.selectedChannelNames);
      if (prefs.valueChannelNames)    applyServerValueChannels(prefs.valueChannelNames);
      if (prefs.channelPanelConfigs)  applyServerChannelPanelConfigs(prefs.channelPanelConfigs);
      setAutoShare(!!prefs.auto_share);
      // Merge rather than overwrite: a color picked in the brief window before this
      // GET resolves is strictly newer than the server snapshot and must win.
      setLapColorsByServer(prev => {
        const server = prefs.lapColors || {};
        if (!prev) return server;
        const merged = { ...server };
        for (const sid of Object.keys(prev)) merged[sid] = { ...(server[sid] || {}), ...prev[sid] };
        return merged;
      });
      setFastestLapColor(prev => prev ?? (prefs.fastestLapColor ?? null));
      // Prebuilt built-in channels (Traction, Smoothness, …) ride the same path as
      // user formulas; merge them ahead of the user's saved library.
      setFormulas([...BUILTIN_FORMULAS, ...formulaList]);
    }).catch(() => {}).finally(() => { prefsLoadedRef.current = true; });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!prefsLoadedRef.current) return;
    const prefs = { panelVisibility, windowLayout: winLayout, selectedChannelNames, valueChannelNames, channelPanelConfigs, auto_share: autoShare, lapColors: lapColorsByServer || {}, fastestLapColor };
    const timer = setTimeout(() => saveUserPreferences(prefs).catch(() => {}), 800);
    return () => clearTimeout(timer);
  }, [panelVisibility, winLayout, selectedChannelNames, valueChannelNames, channelPanelConfigs, autoShare, lapColorsByServer, fastestLapColor]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Turn count hint & per-lap apex toggle ─────────────────────────────────
  const [turnCount,        setTurnCount]        = useState(null);
  const [sensitivity,      setSensitivity]      = useState('high'); // low|medium|high
  const [showPerLapApex,   setShowPerLapApex]   = useState(false);
  const [showTurnMarkers,  setShowTurnMarkers]  = useState(
    () => localStorage.getItem('trackmap.showTurnMarkers') !== 'false'
  );
  const redetectTimerRef   = useRef(null);
  const turnCountInitRef   = useRef(null);
  const redetectLoadingRef = useRef(false);

  // Display label for a compare-driver slot — used for picker tags and panel
  // titles. Primary channels aren't labeled (implicit default).
  const compareDriverLabel = useCallback((slot) =>
    cmpHooks[slot]?.session?.meta?.racer || `Driver ${slot + 2}`,
    [cmpHooks]
  );

  // Every option is tagged with `source` ('primary' or a compare-driver slot
  // 0/1/2) and a compound `key` (`${source}::${name}`) so that two sessions
  // with an identically-named channel don't collide in selection state.
  // Compare-driver entries also carry a `driverLabel` for picker display.
  const availableChannels = useMemo(() => {
    const primary = channelsFromSession(session, 'primary');
    const extra   = cmpHooks.flatMap((h, slot) => channelsFromSession(h.session, slot)
      .map(ch => ({ ...ch, driverLabel: compareDriverLabel(slot) })));
    return [...primary, ...extra];
  }, [session, cmpHooks, compareDriverLabel]);

  // Resolve a persisted selection entry (compound key, or a legacy plain
  // channel name from before driver-tagging shipped — which always meant
  // the primary session) against the current availableChannels list.
  const resolveChannel = useCallback((storedKey) => {
    const { source, name } = parseChannelKey(storedKey);
    return availableChannels.find(ch => ch.source === source && ch.name === name);
  }, [availableChannels]);

  const selectedChannels = useMemo(() =>
    selectedChannelNames.map(resolveChannel).filter(Boolean),
    [selectedChannelNames, resolveChannel]
  );

  const valueChannels = useMemo(() =>
    valueChannelNames.map(resolveChannel).filter(Boolean),
    [valueChannelNames, resolveChannel]
  );

  // Panels group same-named channel picks from different drivers into one
  // combined chart (matching how Speed/G/Power already merge drivers onto a
  // single chart) — e.g. picking Driver 1's "RPM" and Driver 2's "RPM" both
  // land in one panel. A channel picked from only one driver still gets its
  // own single-driver panel, tagged in the title when it's compare-driver-only.
  const channelPanelGroups = useMemo(() => {
    return groupChannelSelections(selectedChannels, compareDriverLabel);
  }, [selectedChannels, compareDriverLabel]);

  // The Power panel is in use when its desktop window is shown or, on mobile
  // (where the visibility flag never gets set), when its tab is the active
  // companion. Gates power-trace fetching and the panel's extra channels.
  const powerInUse = showPowerTrace || (isMobile && activeMobilePanel === 'power');

  // Additional signals live inside a base channel panel rather than becoming
  // top-level windows. Resolve persisted compound keys against this session,
  // and group same-named primary/comparison picks onto one axis/sub-chart.
  // The Power panel joins under its reserved key so its extras persist and
  // fetch exactly like a generic channel window's — but only while it's in
  // use, so a hidden panel doesn't keep its extra channels' traces loaded.
  const channelPanelExtras = useMemo(() => Object.fromEntries(
    [
      ...channelPanelGroups.map(group => group.name),
      ...(powerInUse ? [POWER_PANEL_KEY] : []),
    ].map(baseName => {
      const keys = channelPanelConfigs[baseName]?.additionalChannelKeys || [];
      return [baseName, resolvePanelExtraChannels(baseName, keys, resolveChannel)];
    })
  ), [channelPanelGroups, channelPanelConfigs, resolveChannel, powerInUse]);

  const channelPanelExtraGroups = useMemo(() => Object.fromEntries(
    Object.entries(channelPanelExtras).map(([baseName, channels]) => [
      baseName,
      groupChannelSelections(channels, compareDriverLabel),
    ])
  ), [channelPanelExtras, compareDriverLabel]);

  const activePanelExtraChannels = useMemo(() => {
    const byKey = new Map();
    Object.values(channelPanelExtras).flat().forEach(ch => byKey.set(ch.key, ch));
    return [...byKey.values()];
  }, [channelPanelExtras]);

  // Union of plotted + value channels — both need per-lap traces fetched. Deduped
  // by key so a channel shown as both a plot and a readout is fetched once,
  // while distinct drivers' picks of the same-named channel stay distinct.
  const tracedChannels = useMemo(() => {
    const byKey = new Map();
    [...selectedChannels, ...valueChannels, ...activePanelExtraChannels].forEach(ch => byKey.set(ch.key, ch));
    return [...byKey.values()];
  }, [selectedChannels, valueChannels, activePanelExtraChannels]);

  const selectedChannelsRef = useRef([]);
  const tracedChannelsRef   = useRef([]);
  const valueChannelNamesRef = useRef([]);
  const selectedLapsRef     = useRef(selectedLaps);
  const formulaTracesMapRef = useRef({});
  const cmpFormulaTracesRef = useRef([{}, {}, {}]);
  useEffect(() => { selectedChannelsRef.current = selectedChannels; }, [selectedChannels]);
  useEffect(() => { tracedChannelsRef.current   = tracedChannels; },   [tracedChannels]);
  useEffect(() => { valueChannelNamesRef.current = valueChannelNames; }, [valueChannelNames]);
  useEffect(() => { selectedLapsRef.current     = selectedLaps; },     [selectedLaps]);

  const isActivePanelExtra = useCallback((channel) => (
    activePanelExtraChannels.some(ch => ch.source === channel.source && ch.name === channel.name)
  ), [activePanelExtraChannels]);

  const isChannelUsedOutsidePanel = useCallback((channel, baseName) => (
    selectedChannels.some(ch => ch.source === channel.source && ch.name === channel.name)
    || valueChannels.some(ch => ch.source === channel.source && ch.name === channel.name)
    || Object.entries(channelPanelExtras).some(([name, channels]) => (
      name !== baseName && channels.some(ch => ch.source === channel.source && ch.name === channel.name)
    ))
  ), [selectedChannels, valueChannels, channelPanelExtras]);

  // ── Metrics ───────────────────────────────────────────────────────────────
  const [metrics,         setMetrics]         = useState(null);
  const [metricsLoading,  setMetricsLoading]  = useState(false);
  const [metricsError,    setMetricsError]    = useState(null);

  // Per-sector aggregate metrics (parallel to corner metrics), keyed off sectors.
  // Comparison-driver metrics live in cmpMetrics / cmpSectorMetrics (declared
  // alongside the comparison hooks above).
  const [sectorMetrics,  setSectorMetrics]  = useState(null);

  // ── Channel trace fetching ────────────────────────────────────────────────
  // `source` is 'primary' (default — every pre-existing 2-arg call site keeps
  // working unchanged) or a compare-driver slot. Primary fetches cache in
  // channelTracesMap as before; compare-driver fetches cache per-slot in
  // cmpChannelTraces so identically-named channels across sessions/loggers
  // never collide (each slot's map is independent).
  const storeChannelTrace = useCallback((source, channelName, lapIndex, trace) => {
    const merge = prev => ({
      ...prev,
      [channelName]: { ...(prev[channelName] || {}), [lapIndex]: trace },
    });
    if (source === 'primary') setChannelTracesMap(merge);
    else                      setAt(setCmpChannelTraces, source, merge);
  }, []);

  const fetchChannelTraceForLap = useCallback(async (lapIndex, channelName, source = 'primary') => {
    const sid = source === 'primary' ? sessionId : cmpSessionIds[source];
    if (!sid || !channelName) return;
    try {
      storeChannelTrace(source, channelName, lapIndex, await getChannelTrace(sid, lapIndex, channelName));
    } catch (e) {
      console.error('Failed to fetch channel trace', lapIndex, channelName, 'source', source, e);
      // A 404 means this channel simply isn't in this session — retrying can
      // never succeed. Callers decide whether to fetch by asking "is this lap
      // in the map yet?", so leaving the slot empty makes every render that
      // reaches them re-request it. Record an empty trace instead; consumers
      // already treat empty points exactly like a missing entry. Transient
      // failures (network, 5xx) are deliberately left unrecorded so a later
      // render still retries them.
      if (e?.status === 404) {
        storeChannelTrace(source, channelName, lapIndex, { lap_index: lapIndex, points: [] });
      }
    }
  }, [sessionId, cmpSessionIdKey, storeChannelTrace]); // eslint-disable-line react-hooks/exhaustive-deps

  // Backfill panel-local additions restored from tab/account preferences. The
  // direct toggle handler handles new clicks; this effect covers remounts and
  // the case where a compatible primary/comparison session arrives later.
  useEffect(() => {
    activePanelExtraChannels.forEach(ch => {
      const laps = ch.source === 'primary' ? selectedLaps : cmpSelectedLaps[ch.source];
      // Only fetch what isn't cached yet. Without this the effect re-requests
      // every trace on each run, and since its own writes re-render App it
      // never settles — one extra channel pinned a backend worker at ~10 req/s
      // for as long as the tab stayed open.
      const cached = ch.source === 'primary'
        ? channelTracesMap[ch.name]
        : cmpChannelTraces[ch.source]?.[ch.name];
      laps.forEach(idx => {
        if (idx !== TB_LAP_INDEX && !cached?.[idx]) {
          fetchChannelTraceForLap(idx, ch.name, ch.source);
        }
      });
    });
  }, [activePanelExtraChannels, selectedLaps, cmpSelectedLaps,
      channelTracesMap, cmpChannelTraces, fetchChannelTraceForLap]);

  const handlePanelExtraChannelToggle = useCallback((baseName, ch) => {
    // Unavailable saved entries stay dormant across sessions, but they should
    // not block a user from choosing channels that exist in this session.
    // Once the user edits the composition, persist the visible choices only.
    const current = (channelPanelExtras[baseName] || []).map(channel => channel.key);
    const exists = current.includes(ch.key);
    if (!exists && current.length >= MAX_EXTRA_CHANNELS) return;
    if (!exists) {
      const laps = ch.source === 'primary' ? selectedLapsRef.current : cmpSelectedLaps[ch.source];
      laps.forEach(idx => { if (idx !== TB_LAP_INDEX) fetchChannelTraceForLap(idx, ch.name, ch.source); });
    }
    const editableConfigs = {
      ...channelPanelConfigs,
      [baseName]: {
        ...(channelPanelConfigs[baseName] || {}),
        additionalChannelKeys: current,
      },
    };
    updateChannelPanelConfigs(togglePanelExtraChannel(editableConfigs, baseName, ch.key));
    if (exists && !isChannelUsedOutsidePanel(ch, baseName)) {
      if (ch.source === 'primary') {
        setChannelTracesMap(map => { const next = { ...map }; delete next[ch.name]; return next; });
      } else {
        setAt(setCmpChannelTraces, ch.source, map => { const next = { ...map }; delete next[ch.name]; return next; });
      }
    }
  }, [channelPanelConfigs, channelPanelExtras, cmpSelectedLaps, fetchChannelTraceForLap, updateChannelPanelConfigs, isChannelUsedOutsidePanel]);

  const handlePanelSplitToggle = useCallback((baseName) => {
    const split = channelPanelConfigs[baseName]?.split === true;
    updateChannelPanelConfigs(setPanelSplit(channelPanelConfigs, baseName, !split));
  }, [channelPanelConfigs, updateChannelPanelConfigs]);

  const handlePanelSharedScaleToggle = useCallback((baseName) => {
    const sharedScale = channelPanelConfigs[baseName]?.sharedScale === true;
    updateChannelPanelConfigs(setPanelSharedScale(channelPanelConfigs, baseName, !sharedScale));
  }, [channelPanelConfigs, updateChannelPanelConfigs]);

  const handlePanelColorModeChange = useCallback((baseName, colorMode) => {
    updateChannelPanelConfigs(setPanelColorMode(channelPanelConfigs, baseName, colorMode));
  }, [channelPanelConfigs, updateChannelPanelConfigs]);

  const handleChannelToggle = useCallback((ch) => {
    const exists = selectedChannelNames.includes(ch.key);
    if (exists) {
      // Keep the trace if the channel is still shown as a readout value.
      if (!valueChannelNamesRef.current.includes(ch.key) && !isActivePanelExtra(ch)) {
        if (ch.source === 'primary') {
          setChannelTracesMap(m => { const next = { ...m }; delete next[ch.name]; return next; });
        } else {
          setAt(setCmpChannelTraces, ch.source, m => { const next = { ...m }; delete next[ch.name]; return next; });
        }
      }
      // Filter by resolved identity, not exact string: a channel picked before
      // driver-tagging shipped may be stored as a bare name ("GPS Altitude")
      // alongside a newer compound-key pick ("primary::GPS Altitude") — both
      // resolve to this same channel, so both must go or the stale entry
      // keeps re-resolving the channel back into view (bug: panel wouldn't
      // stay closed for accounts with both forms present).
      const nextSelected = selectedChannelNames.filter(n => {
        const p = parseChannelKey(n);
        return !(p.source === ch.source && p.name === ch.name);
      });
      updateSelectedChannelNames(nextSelected);
      if (!nextSelected.some(key => parseChannelKey(key).name === ch.name)) {
        (channelPanelExtras[ch.name] || []).forEach(extra => {
          if (isChannelUsedOutsidePanel(extra, ch.name)) return;
          if (extra.source === 'primary') {
            setChannelTracesMap(map => { const next = { ...map }; delete next[extra.name]; return next; });
          } else {
            setAt(setCmpChannelTraces, extra.source, map => { const next = { ...map }; delete next[extra.name]; return next; });
          }
        });
        updateChannelPanelConfigs(removePanelConfig(channelPanelConfigs, ch.name));
      }
    } else {
      if (selectedChannelNames.length >= 8) return;
      const laps = ch.source === 'primary' ? selectedLapsRef.current : cmpSelectedLaps[ch.source];
      laps.forEach(idx => { if (idx !== TB_LAP_INDEX) fetchChannelTraceForLap(idx, ch.name, ch.source); });
      updateSelectedChannelNames([...selectedChannelNames, ch.key]);
    }
  }, [selectedChannelNames, updateSelectedChannelNames, fetchChannelTraceForLap, cmpSelectedLaps, isActivePanelExtra, channelPanelConfigs, channelPanelExtras, isChannelUsedOutsidePanel, updateChannelPanelConfigs]);

  // Closing a channel panel removes every entry it groups (e.g. a same-named
  // channel picked for both the primary driver and a compare driver) at once.
  // Can't just loop handleChannelToggle per entry: each call filters from the
  // same stale selectedChannelNames snapshot, and updateSelectedChannelNames
  // replaces the array outright (not a functional update) — so only the last
  // call in the loop survives, and the panel needed one click per remaining
  // entry to actually close.
  const handleCloseChannelGroup = useCallback((group) => {
    group.entries.forEach(ch => {
      if (!valueChannelNamesRef.current.includes(ch.key) && !isActivePanelExtra(ch)) {
        if (ch.source === 'primary') {
          setChannelTracesMap(m => { const next = { ...m }; delete next[ch.name]; return next; });
        } else {
          setAt(setCmpChannelTraces, ch.source, m => { const next = { ...m }; delete next[ch.name]; return next; });
        }
      }
    });
    updateSelectedChannelNames(selectedChannelNames.filter(n => {
      const p = parseChannelKey(n);
      return !group.entries.some(ch => p.source === ch.source && p.name === ch.name);
    }));
    (channelPanelExtras[group.name] || []).forEach(ch => {
      if (isChannelUsedOutsidePanel(ch, group.name)) return;
      if (ch.source === 'primary') {
        setChannelTracesMap(map => { const next = { ...map }; delete next[ch.name]; return next; });
      } else {
        setAt(setCmpChannelTraces, ch.source, map => { const next = { ...map }; delete next[ch.name]; return next; });
      }
    });
    updateChannelPanelConfigs(removePanelConfig(channelPanelConfigs, group.name));
  }, [selectedChannelNames, updateSelectedChannelNames, isActivePanelExtra, channelPanelConfigs, channelPanelExtras, isChannelUsedOutsidePanel, updateChannelPanelConfigs]);

  // Toggle a channel's value-readout (Readouts panel). Primary-only: the
  // panel renders one shared set of per-lap rows keyed to the primary
  // session's own selectedLaps, so a compare-driver channel has no
  // consistent row to render against (the picker hides the Value option
  // for non-primary channels to match).
  const handleValueChannelToggle = useCallback((ch) => {
    if (ch.source !== 'primary') return;
    const exists = valueChannelNames.includes(ch.key);
    if (exists) {
      if (!selectedChannelNames.includes(ch.key) && !isActivePanelExtra(ch)) {
        setChannelTracesMap(m => { const next = { ...m }; delete next[ch.name]; return next; });
      }
      // See handleChannelToggle: filter by resolved identity, not exact
      // string, so a stale pre-driver-tagging duplicate can't stick around.
      updateValueChannelNames(valueChannelNames.filter(n => {
        const p = parseChannelKey(n);
        return !(p.source === ch.source && p.name === ch.name);
      }));
    } else {
      if (valueChannelNames.length >= 8) return;
      selectedLapsRef.current.forEach(idx => { if (idx !== TB_LAP_INDEX) fetchChannelTraceForLap(idx, ch.name); });
      updateValueChannelNames([...valueChannelNames, ch.key]);
    }
  }, [valueChannelNames, selectedChannelNames, updateValueChannelNames, fetchChannelTraceForLap, isActivePanelExtra]);

  // Closing the Readouts panel clears every shown value channel at once.
  // Can't just loop handleValueChannelToggle per channel: each call computes
  // its removal from the same stale valueChannelNames snapshot, and
  // updateValueChannelNames replaces the array outright (not a functional
  // update) — so only the last call in the loop survives, and the panel
  // needed one click per remaining channel to actually close.
  const handleCloseReadouts = useCallback(() => {
    valueChannelNames.forEach(key => {
      const ch = resolveChannel(key);
      if (ch && !selectedChannelNames.includes(ch.key) && !isActivePanelExtra(ch)) {
        setChannelTracesMap(m => { const next = { ...m }; delete next[ch.name]; return next; });
      }
    });
    updateValueChannelNames([]);
  }, [valueChannelNames, selectedChannelNames, resolveChannel, updateValueChannelNames, isActivePanelExtra]);

  // ── Formula trace fetching ────────────────────────────────────────────────
  const fetchFormulaTraceForLap = useCallback(async (lapIndex, formula) => {
    if (!sessionId || !formula) return;
    try {
      const trace = await getFormulaTrace(sessionId, lapIndex, formula.expression, formula.name);
      setFormulaTracesMap(prev => ({
        ...prev,
        [formula.id]: { ...(prev[formula.id] || {}), [lapIndex]: trace, _error: null },
      }));
    } catch (e) {
      const msg = e.formulaError?.message || e.message || 'Formula evaluation failed';
      setFormulaTracesMap(prev => ({
        ...prev,
        [formula.id]: { ...(prev[formula.id] || {}), _error: msg },
      }));
    }
  }, [sessionId]);

  useEffect(() => { formulaTracesMapRef.current = formulaTracesMap; }, [formulaTracesMap]);

  // Single source of truth for fetching formula traces: whenever a session is
  // loaded, a formula panel is shown, the formula library resolves (built-ins
  // load on mount), or the lap selection changes, fetch any missing traces for
  // the visible formula panels. Reading the trace map via a ref keeps this from
  // re-running on every fetch result; the per-lap guard makes it idempotent, and
  // skipping a formula already in `_error` avoids retrying a failing expression.
  useEffect(() => {
    if (!sessionId || selectedLaps.length === 0) return;
    formulas.forEach(f => {
      if (!panelVisibility[`showFormula_${f.id}`]) return;
      const fmap = formulaTracesMapRef.current[f.id] || {};
      if (fmap._error) return;
      selectedLaps.forEach(idx => {
        if (idx !== TB_LAP_INDEX && !fmap[idx]) fetchFormulaTraceForLap(idx, f);
      });
    });
  }, [sessionId, formulas, panelVisibility, selectedLaps, fetchFormulaTraceForLap]);

  const fetchCmpFormulaTraceForLap = useCallback(async (slot, lapIndex, formula) => {
    const sid = cmpSessionIds[slot];
    if (!sid || !formula) return;
    try {
      const trace = await getFormulaTrace(sid, lapIndex, formula.expression, formula.name);
      setAt(setCmpFormulaTraces, slot, prev => ({
        ...prev,
        [formula.id]: { ...(prev[formula.id] || {}), [lapIndex]: trace, _error: null },
      }));
    } catch (e) {
      const msg = e.formulaError?.message || e.message || 'Formula evaluation failed';
      setAt(setCmpFormulaTraces, slot, prev => ({
        ...prev,
        [formula.id]: { ...(prev[formula.id] || {}), _error: msg },
      }));
    }
  }, [cmpSessionIdKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { cmpFormulaTracesRef.current = cmpFormulaTraces; }, [cmpFormulaTraces]);

  // Same backfill for the comparison drivers. A formula referencing a channel
  // one driver's logger lacks fails for that slot alone, so the `_error` guard
  // (which also stops a failing expression retrying) is per slot — a comparison
  // session missing `lat_g` must not blank the primary's trace.
  useEffect(() => {
    formulas.forEach(f => {
      if (!panelVisibility[`showFormula_${f.id}`]) return;
      cmpSessionIds.forEach((sid, slot) => {
        if (!sid) return;
        const fmap = cmpFormulaTracesRef.current[slot]?.[f.id] || {};
        if (fmap._error) return;
        cmpSelectedLaps[slot].forEach(idx => {
          if (idx !== TB_LAP_INDEX && !fmap[idx]) fetchCmpFormulaTraceForLap(slot, idx, f);
        });
      });
    });
  }, [cmpSessionIdKey, formulas, panelVisibility, cmpSelectedLaps, fetchCmpFormulaTraceForLap]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Power trace fetching ────────────────────────────────────────────────────
  const fetchPowerTraceForLap = useCallback(async (lapIndex) => {
    const params = vehicleParamsRef.current;
    if (!sessionId || !params) return;
    try {
      const trace = await getPowerTrace(sessionId, lapIndex, params);
      setPowerTracesMap(prev => ({ ...prev, [lapIndex]: trace }));
    } catch (e) {
      console.error('Failed to fetch power trace', lapIndex, e);
    }
  }, [sessionId]);

  const fetchCmpPowerTraceForLap = useCallback(async (slot, lapIndex) => {
    const sid    = cmpSessionIds[slot];
    const params = cmpVehicleParamsRef.current[slot];
    if (!sid || !params) return;
    try {
      const trace = await getPowerTrace(sid, lapIndex, params);
      setAt(setCmpPowerTraces, slot, prev => ({ ...prev, [lapIndex]: trace }));
    } catch (e) {
      console.error('Failed to fetch power trace', lapIndex, 'slot', slot, e);
    }
  }, [cmpSessionIdKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-load each session's car params (saved profile or defaults).
  useEffect(() => {
    if (!sessionId) { setVehicleParams(null); return; }
    getSessionVehicle(sessionId)
      .then(v => setVehicleParams({ ...v, include_grade: true }))
      .catch(() => {});
  }, [sessionId]);

  useEffect(() => {
    cmpSessionIds.forEach((sid, slot) => {
      if (!sid) return;
      getSessionVehicle(sid)
        .then(v => setAt(setCmpVehicleParams, slot, { ...v, include_grade: true }))
        .catch(() => {});
    });
  }, [cmpSessionIdKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // A param change invalidates every cached power trace for that driver.
  useEffect(() => { setPowerTracesMap({}); }, [vehicleParams]);
  useEffect(() => { setCmpPowerTraces([{}, {}, {}]); }, [cmpVehicleParams]);

  // Fetch power for any selected lap not yet loaded (panel in use + params
  // ready).
  useEffect(() => {
    if (!powerInUse || !vehicleParams) return;
    selectedLaps.forEach(idx => {
      if (idx !== TB_LAP_INDEX && !powerTracesMap[idx]) fetchPowerTraceForLap(idx);
    });
  }, [powerInUse, vehicleParams, selectedLaps, powerTracesMap, fetchPowerTraceForLap]);

  useEffect(() => {
    if (!powerInUse) return;
    cmpSessionIds.forEach((sid, slot) => {
      if (!sid || !cmpVehicleParams[slot]) return;
      cmpSelectedLaps[slot].forEach(idx => {
        if (idx !== TB_LAP_INDEX && !cmpPowerTraces[slot][idx]) fetchCmpPowerTraceForLap(slot, idx);
      });
    });
  }, [powerInUse, cmpVehicleParams, cmpSelectedLaps, cmpPowerTraces, cmpSessionIdKey, fetchCmpPowerTraceForLap]); // eslint-disable-line react-hooks/exhaustive-deps


  // ── Formula CRUD handlers ─────────────────────────────────────────────────
  const handleFormulaSave = useCallback((saved) => {
    setFormulas(prev => {
      const exists = prev.find(f => f.id === saved.id);
      return exists ? prev.map(f => f.id === saved.id ? saved : f) : [...prev, saved];
    });
    // Clear stale traces; the backfill effect re-fetches with the new expression
    // (the changed `formulas` identity re-triggers it for any visible panel).
    setFormulaTracesMap(prev => { const n = { ...prev }; delete n[saved.id]; return n; });
    setCmpFormulaTraces(prev => prev.map(m => { const n = { ...m }; delete n[saved.id]; return n; }));
    setFormulaEditorOpen(null);
  }, []);

  const handleFormulaDelete = useCallback(async (formula) => {
    if (isBuiltinFormula(formula)) return;   // built-ins are read-only
    if (!window.confirm(`Delete formula "${formula.name}"?`)) return;
    try {
      await deleteFormula(formula.id);
      setFormulas(prev => prev.filter(f => f.id !== formula.id));
      setFormulaTracesMap(prev => { const n = { ...prev }; delete n[formula.id]; return n; });
      setCmpFormulaTraces(prev => prev.map(m => { const n = { ...m }; delete n[formula.id]; return n; }));
      updatePanelVisibility({ [`showFormula_${formula.id}`]: false });
    } catch (e) {
      console.error('Failed to delete formula', e);
    }
  }, [updatePanelVisibility]);

  const handleFormulaShow = useCallback((formula) => {
    // The backfill effect fetches traces on this visibility change.
    updatePanelVisibility({ [`showFormula_${formula.id}`]: true });
  }, [updatePanelVisibility]);

  const handleFormulaHide = useCallback((formula) => {
    updatePanelVisibility({ [`showFormula_${formula.id}`]: false });
  }, [updatePanelVisibility]);

  const handleUnitPrefsChange = useCallback((newPrefs) => {
    setUnitPrefs(newPrefs);
    saveUnitPrefs(newPrefs);
  }, []);

  // ── LLM Briefing ──────────────────────────────────────────────────────────
  const [briefing,      setBriefing]      = useState(null);
  const [promptLoading, setPromptLoading] = useState(false);
  const [promptError,   setPromptError]   = useState(null);
  const [capabilities,  setCapabilities]  = useState(null);  // logger channel tiers

  // Fetch logger capabilities (channel tiers) whenever the session changes.
  useEffect(() => {
    if (!sessionId) { setCapabilities(null); return; }
    let cancelled = false;
    getCapabilities(sessionId)
      .then(caps => { if (!cancelled) setCapabilities(caps); })
      .catch(() => { if (!cancelled) setCapabilities(null); });
    return () => { cancelled = true; };
  }, [sessionId]);

  // ── Track name prompt (shown when the backend couldn't resolve a name) ────
  const [needsTrackName,    setNeedsTrackName]    = useState(false);
  const [trackNameDraft,    setTrackNameDraft]    = useState('');
  const trackNameInputRef = useRef(null);

  // ── Session 1 init ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!session) return;
    setSelectedLaps([]);
    const detectedCorners = session.detected_corners || [];
    setCorners(detectedCorners);
    const initCount = detectedCorners.length;
    turnCountInitRef.current = initCount;
    setTurnCount(initCount);
    setSensitivity(session.corner_sensitivity || 'high');
    setMetrics(null);
    setSectorMetrics(null);
    resetAllCmpMetrics();
    setBriefing(null);
    setCapabilities(null);
    setChannelTracesMap({});
    setPowerTracesMap({});
    setFormulaTracesMap({});   // stale traces are keyed to the previous session
    formulaTracesMapRef.current = {};

    // A copied view link (?laps=…) pins the exact selection; otherwise
    // auto-select per the current lap-selection mode — collapsed to best-lap if
    // a comparison session already arrived while this one was still loading.
    const pending = pendingLapRestoreRef.current.get(session.session_id);
    if (pending) pendingLapRestoreRef.current.delete(session.session_id);
    const collapse = pendingCollapsePrimaryRef.current;
    pendingCollapsePrimaryRef.current = false;
    const restored = pending?.filter(i => i < (session.laps || []).length);
    const toLoad = restored?.length
      ? restored
      : computeLapIndices(collapse ? { type: 'best', count: 1 } : lapSelectionMode,
                          session.laps, null);
    setSelectedLaps(toLoad);
    toLoad.forEach(idx => fetchTrace(idx));

    // Re-fetch traces for any persisted primary-sourced channels that exist
    // in this session (both plotted channels and value-readout channels).
    // Compare-driver-sourced selections are backfilled by the cmpHooks
    // session-loaded effect instead.
    const sessionChannelSet = new Set(
      (session.channels || []).map(n => n.toLowerCase())
    );
    tracedChannelsRef.current
      .filter(({ source, name }) => source === 'primary' && sessionChannelSet.has(name.toLowerCase()))
      .forEach(({ name }) => toLoad.forEach(idx => fetchChannelTraceForLap(idx, name)));

    const tid   = slugify(session.meta.session_name || 'track');
    const tname = session.meta.session_name;
    setTrackId(tid);
    setTrackName(tname);
    setTrackElevationProfile([]);

    // If the backend couldn't identify the track, ask the user to name it.
    if (session.meta.needs_track_name) {
      setTrackNameDraft('');
      setNeedsTrackName(true);
    }

    // `corners_source` is which branch of the backend's _corners_for_session ran;
    // "detected" means no stored definition existed for this circuit.
    const freshCorners = (session.detected_corners || []).length > 0
      && (session.corners_source ?? 'detected') === 'detected';

    // Load any persisted sectors for this track; default to 10 equal otherwise.
    getTrack(tid).then(tr => {
      setTrackStartFinish(tr?.start_finish || null);
      setTrackElevationProfile(tr?.elevation_profile || []);
      const persistedSectors = tr?.sectors;
      const initialSectors   = (persistedSectors && persistedSectors.length > 0)
        ? persistedSectors
        : defaultSectors(10);
      setSectors(initialSectors);
      // Only seed a track that has no definition yet. When the backend served
      // stored or GPS-matched corners it is handing back what is already on disk,
      // so writing them again is a no-op that risks stamping one circuit's geometry
      // onto another's file whenever session_name and the real track disagree.
      if (freshCorners) {
        persistCorners(tid, tname, session.detected_corners, initialSectors);
      }
    }).catch(() => {
      setTrackStartFinish(null);
      setTrackElevationProfile([]);
      const initialSectors = defaultSectors(10);
      setSectors(initialSectors);
      if (freshCorners) {
        persistCorners(tid, tname, session.detected_corners, initialSectors);
      }
    });

    if (session.has_video || session.has_rendered_video) {
      updatePanelVisibility({ showVideo: true });
      // A requested moment wins over the best-lap default, which would otherwise
      // overwrite it a moment later. The 300 ms wait is for <video> metadata.
      const pending = pendingVideoSeekRef.current;
      pendingVideoSeekRef.current = null;
      if (pending != null) {
        setTimeout(() => videoRef.current?.seekTo(pending), 300);
      } else {
        const timed = (session.laps || []).filter(l => !l.is_outlap && !l.is_inlap);
        const firstLap = timed.sort((a, b) => a.lap_time_seconds - b.lap_time_seconds)[0];
        if (firstLap != null) {
          setTimeout(() => videoRef.current?.seekTo(firstLap.start_time), 300);
        }
      }
    }
  }, [session]);

  // ── Comparison-driver init ────────────────────────────────────────────────
  // When a comparison session newly loads into a slot, auto-select its best lap.
  // The first comparison opens a clean head-to-head, so Driver 1's selection is
  // collapsed to its best lap (best-vs-best by default).
  const prevCmpIdsRef = useRef([null, null, null]);
  useEffect(() => {
    const prev   = prevCmpIdsRef.current;
    const hadAny = prev.some(Boolean);
    cmpHooks.forEach((h, slot) => {
      const id = h.sessionId;
      if (!id || id === prev[slot] || !h.session) return;
      // Newly loaded slot — reset its per-slot state and select its best lap
      // (or the exact laps a copied view link pinned for this session).
      setAt(setCmpMetrics,       slot, null);
      setAt(setCmpSectorMetrics, slot, null);
      setAt(setCmpFormulaTraces, slot, {});   // traces keyed to the old session
      const pending = pendingLapRestoreRef.current.get(id);
      if (pending) pendingLapRestoreRef.current.delete(id);
      const restored = pending?.filter(i => i < (h.session.laps || []).length);
      const toLoad = restored?.length
        ? restored
        : computeLapIndices({ type: 'best', count: 1 }, h.session.laps, null);
      setAt(setCmpSelectedLaps, slot, toLoad);
      const slotChannelSet = new Set((h.session.channels || []).map(n => n.toLowerCase()));
      toLoad.forEach(idx => {
        h.fetchTrace(idx);
        // Re-fetch traces for any persisted channels tagged to this slot.
        tracedChannelsRef.current
          .filter(ch => ch.source === slot && slotChannelSet.has(ch.name.toLowerCase()))
          .forEach(ch => fetchChannelTraceForLap(idx, ch.name, slot));
      });
      if (h.session.has_video || h.session.has_rendered_video) {
        updatePanelVisibility({ showVideo: true });
      }
    });
    // Collapse driver 1 to its best lap the first time any comparison appears —
    // unless a copied view link pinned Driver 1's selection, which wins once.
    const haveAny = cmpHooks.some(h => h.sessionId);
    if (!hadAny && haveAny && restoredPrimaryRef.current) {
      restoredPrimaryRef.current = false;
    } else if (!hadAny && haveAny) {
      // The closure's `session` is current for this commit; sessionRef lags one
      // effect pass (it's updated by a later-defined effect), which matters when
      // a parallel deep-link lands both sessions in the same commit.
      const sess1 = session || sessionRef.current;
      if (sess1) {
        const toLoad1 = computeLapIndices({ type: 'best', count: 1 }, sess1.laps, null);
        setSelectedLaps(toLoad1);
        const sess1ChannelSet = new Set((sess1.channels || []).map(n => n.toLowerCase()));
        toLoad1.forEach(idx => {
          fetchTrace(idx);
          tracedChannelsRef.current
            .filter(ch => ch.source === 'primary' && sess1ChannelSet.has(ch.name.toLowerCase()))
            .forEach(ch => fetchChannelTraceForLap(idx, ch.name));
        });
      } else {
        // Primary still loading (parallel deep-link) — collapse when it lands.
        pendingCollapsePrimaryRef.current = true;
      }
    }
    prevCmpIdsRef.current = cmpHooks.map(h => h.sessionId);
  }, [cmpSessionIdKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Rename session ────────────────────────────────────────────────────────
  const handleVideoDeleted = useCallback(() => {
    setSession(prev => prev ? {
      ...prev,
      has_video: false,
      has_rendered_video: false,
      hls_url: null,
      rendered_video_path: null,
    } : prev);
    updatePanelVisibility({ showVideo: false });
  }, []);

  const handleRenameSession = useCallback(async (newName) => {
    if (!sessionId) return;
    await patchSessionMeta(sessionId, { session_name: newName });
    setSession(prev => prev ? {
      ...prev,
      meta: { ...prev.meta, session_name: newName },
    } : prev);
  }, [sessionId]);

  // Per-session label (e.g. "Novice 1") — distinct from the track name.
  const handleRenameLabel = useCallback(async (newLabel) => {
    if (!sessionId) return;
    await patchSessionMeta(sessionId, { session_label: newLabel });
    setSession(prev => prev ? {
      ...prev,
      meta: { ...prev.meta, session_label: newLabel },
    } : prev);
  }, [sessionId]);

  // ── Track name prompt submit ──────────────────────────────────────────────
  const handleTrackNameSubmit = useCallback(async () => {
    const name = trackNameDraft.trim();
    if (!name) return;
    setNeedsTrackName(false);
    // Clear the flag from the local session object so that when handleRenameSession
    // calls setSession (which triggers the session init effect), the effect no
    // longer sees needs_track_name=true and re-opens the dialog.
    setSession(prev => prev ? {
      ...prev,
      meta: { ...prev.meta, needs_track_name: false },
    } : prev);
    await handleRenameSession(name);
    // Also update local trackName so the map / tabs pick it up immediately.
    setTrackName(name);
  }, [trackNameDraft, handleRenameSession, setSession]);

  // ── Lap toggle session 1 ──────────────────────────────────────────────────
  const handleToggleLap = useCallback((lapIndex) => {
    setSelectedLaps(prev => {
      if (prev.includes(lapIndex)) return prev.filter(i => i !== lapIndex);
      // TB sentinel never triggers per-lap fetches/video-seek — its trace is
      // owned by the TB hook and refreshed on corner changes.
      if (lapIndex !== TB_LAP_INDEX) {
        fetchTrace(lapIndex);
        tracedChannelsRef.current
          .filter(ch => ch.source === 'primary')
          .forEach(ch => fetchChannelTraceForLap(lapIndex, ch.name));
        // Visible formula panels are fetched by the formula-trace backfill effect,
        // which fires on this selectedLaps change.
        if (session?.has_video) {
          const lap = session.laps.find(l => l.index === lapIndex);
          if (lap != null) videoRef.current?.seekTo(lap.start_time);
        }
      }
      return [...prev, lapIndex];
    });
  }, [fetchTrace, fetchChannelTraceForLap, session]);

  // ── Add / remove comparison drivers ──────────────────────────────────────
  const handleAddSession = useCallback((sid) => {
    const freeSlot = cmpSessionIds.findIndex(id => !id);
    if (freeSlot < 0) return;
    cmpLoad[freeSlot](sid);
  }, [cmpSessionIdKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRemoveSessionCmp = useCallback((slot) => {
    cmpReset[slot]();
    setAt(setCmpSelectedLaps,  slot, []);
    setAt(setCmpMetrics,       slot, null);
    setAt(setCmpMetricsError,  slot, null);
    setAt(setCmpSectorMetrics, slot, null);
    setAt(setCmpVehicleParams, slot, null);
    setAt(setCmpPowerTraces,   slot, {});
    setAt(setCmpFormulaTraces, slot, {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Re-align a session's laps to the track's canonical start/finish ──────
  // For sessions whose logger beacon sat elsewhere on the circuit (see
  // utils/sfAlignment.js) — the backend rotates the lap boundaries, then the
  // session is reloaded so every trace/metric reflects the new laps.
  // With undo=true, restores the original (device) boundaries instead.
  const handleRealignSession = useCallback(async (slot, undo = false) => {
    const sid = slot === 'primary' ? sessionId : cmpSessionIds[slot];
    if (!sid || realigningSlot !== null) return;
    setRealigningSlot(slot);
    try {
      await (undo ? unalignSessionSf(sid) : realignSessionSf(sid));
      if (slot === 'primary') {
        // The [session] init effect re-selects laps and refetches traces.
        await loadSession(sid);
      } else {
        setAt(setCmpMetrics,       slot, null);
        setAt(setCmpSectorMetrics, slot, null);
        const data = await cmpLoad[slot](sid);
        // The slot-init effect skips same-id reloads — re-select best lap here.
        const toLoad = computeLapIndices({ type: 'best', count: 1 }, data?.laps || [], null);
        setAt(setCmpSelectedLaps, slot, toLoad);
        toLoad.forEach(idx => cmpFetch[slot](idx));
      }
    } catch (e) {
      console.error(undo ? 'Un-align failed' : 'Re-align failed', e);
      alert(`${undo ? 'Un-align' : 'Re-align'} failed: ${e.message}`);
    } finally {
      setRealigningSlot(null);
    }
  }, [sessionId, realigningSlot, loadSession, cmpSessionIdKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Copy view link — URL that restores the loaded sessions + selected laps.
  // ?laps= segments are pipe-separated per driver [primary, ...compare] and must
  // stay aligned with the compacted ?compare= list. Colors aren't serialized:
  // they derive deterministically from selection order + palette.
  const handleCopyViewLink = useCallback(() => {
    if (!sessionId) return;
    // Keep the current path — in prod the SPA is served at /app, while / is the
    // static landing page; a hard-coded '/' link would never reach the app.
    const url = new URL(window.location.pathname, window.location.origin);
    url.searchParams.set('session', sessionId);
    const slots = cmpSessionIds
      .map((id, i) => ({ id, laps: cmpSelectedLaps[i] }))
      .filter(s => s.id);
    if (slots.length) url.searchParams.set('compare', slots.map(s => s.id).join(','));
    const segs = [selectedLaps, ...slots.map(s => s.laps)]
      .map(sel => (sel || []).filter(i => i >= 0).join(','));  // drops TB_LAP_INDEX (-1)
    if (segs.some(Boolean)) url.searchParams.set('laps', segs.join('|'));
    navigator.clipboard?.writeText(url.toString());
  }, [sessionId, cmpSessionIdKey, selectedLaps, cmpSelectedLaps]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Go to leaderboard, pre-selecting this session's track ────────────────
  const handleGoToLeaderboard = useCallback(async () => {
    const sid = sessionId;
    let trackId = null;
    if (sid) {
      try {
        const res = await getSessionLeaderboardTrack(sid);
        trackId = res.track_id || null;
      } catch {
        // silently ignore — leaderboard will fall back to most-laps default
      }
    }
    setLeaderboardPageParams(trackId ? { trackId } : null);
    setView('leaderboard');
  }, [sessionId]);

  const handleGoToProgress = useCallback(async () => {
    const sid = sessionId;
    let trackId = null;
    if (sid) {
      try {
        const res = await getSessionLeaderboardTrack(sid);
        trackId = res.track_id || null;
      } catch {
        // silently ignore — Progress opens on the Career tab if no track resolves
      }
    }
    setProgressPageParams(trackId ? { trackId } : null);
    setView('progress');
  }, [sessionId]);

  const handleGoToChannelStats = useCallback(async () => {
    const sid = sessionId;
    let trackId = null;
    if (sid) {
      try {
        const res = await getSessionLeaderboardTrack(sid);
        trackId = res.track_id || null;
      } catch {
        // silently ignore — Channel Stats opens on its track picker if none resolves
      }
    }
    setChannelStatsPageParams(trackId ? { trackId } : null);
    setView('channelstats');
  }, [sessionId]);

  const handleGoToSearch = useCallback(async () => {
    const sid = sessionId;
    let trackId = null;
    if (sid) {
      try {
        const res = await getSessionLeaderboardTrack(sid);
        trackId = res.track_id || null;
      } catch {
        // silently ignore — Search opens on its track picker if none resolves
      }
    }
    setSearchPageParams(trackId ? { trackId } : null);
    setView('search');
  }, [sessionId]);

  const handleGoToSimLab = useCallback(async () => {
    const sid = sessionId;
    let trackId = null;
    if (sid) {
      try {
        const res = await getSessionLeaderboardTrack(sid);
        trackId = res.track_id || null;
      } catch {
        // silently ignore — the Lab opens on its own track picker if none resolves
      }
    }
    setSimLabInitialTrackId(trackId);
    setView('simlab');
  }, [sessionId]);

  // ── "Sessions" — the shared destination behind AppNav's persistent link and
  // every analytics page's onBack. With a session loaded, setView('sessions')
  // would land back on the analysis view, so clear the loaded session the same
  // way the header's Session List action does (drop the URL params and reload).
  const handleGoToSessions = useCallback(() => {
    if (session) {
      // Reload to drop the loaded session. Strip every param the URL-sync
      // effect owns, not just the session ones: coming here from Video Studio
      // (or Leaderboard/Progress) with a session still loaded, the URL carries
      // `?view=studio`, and the deep-link effect would land the reload right
      // back on that page.
      const url = new URL(window.location.href);
      OWNED_URL_PARAMS.forEach(k => url.searchParams.delete(k));
      history.replaceState(null, '', url);
      window.location.reload();
      return;
    }
    setView('sessions');
  }, [session]);

  // ── "Car Physics" — opens Settings straight onto the current session's car
  // (Cars tab, auto-selected), reproducing the old direct-from-session
  // Vehicle Parameters shortcut on top of the consolidated Cars flow. If the
  // session's car was never saved as a user_cars row, car_id is null and the
  // Cars tab just opens without an auto-selected editor.
  const handleOpenCarPhysics = useCallback(() => {
    setSettingsInitialTab('cars');
    setSettingsInitialCarId(vehicleParams?.car_id ?? null);
    setSettingsAutoOpenPhysics(true);
    setShowSettingsModal(true);
  }, [vehicleParams]);

  // Desktop-only shortcut from the Session menu to the existing sync tab.
  // AccountSettings owns the actual pairing/upload UI; this only selects it.
  const handleOpenServerSync = useCallback(() => {
    setSettingsInitialTab('sync');
    setSettingsInitialCarId(null);
    setSettingsAutoOpenPhysics(false);
    setShowSettingsModal(true);
  }, []);

  // ── Guest sandbox: open a demo session (fresh upload or browser-local list) ─
  const handleOpenGuestSession = useCallback((id, name, date) => {
    setShareToken(id);                       // the demo session's id is its read token
    addGuestSession({ id, name, date });     // remember it in this browser
    loadSession(id).then(data => {
      if (data) {
        setCanComment(data.can_comment !== false);
        setIsOwner(data.is_owner !== false);
      }
    });
  }, [loadSession]);

  // ── Compare two leaderboard laps ──────────────────────────────────────────
  // Loads idA as the baseline (primary driver) and each id in compareIds into a
  // comparison slot (up to MAX_COMPARE). Best laps are auto-selected by the
  // session-init effects, and the address-bar effect mirrors the first
  // comparison id as ?session=A&compare=B for sharing.
  const autoCompareRef = useRef(false);
  const handleCompareSessions = useCallback(async (idA, compareIds = []) => {
    // Accept a single id (legacy two-driver call) or an array.
    const ids = (Array.isArray(compareIds) ? compareIds : [compareIds])
      .filter(Boolean)
      .slice(0, MAX_COMPARE);
    setPendingSessionId(idA);
    setView('sessions');
    autoCompareRef.current = true;   // arm the auto-briefing effect
    updatePanelVisibility({ showDeltaTrace: true });
    const data = await loadSession(idA);
    if (data) {
      setCanComment(data.can_comment !== false);
      setIsOwner(data.is_owner !== false);
    }
    setPendingSessionId(null);
    // Clear any previously overlaid comparison drivers, then load the picked
    // references into slots 0..n.
    cmpReset.forEach(reset => reset());
    await Promise.all(ids.map((id, slot) => cmpLoad[slot](id)));
  }, [loadSession]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Lap toggle for a comparison driver ────────────────────────────────────
  const handleToggleLapCmp = useCallback((slot, lapIndex) => {
    setAt(setCmpSelectedLaps, slot, prev =>
      prev.includes(lapIndex)
        ? prev.filter(i => i !== lapIndex)
        : (
            cmpFetch[slot](lapIndex),
            tracedChannelsRef.current
              .filter(ch => ch.source === slot)
              .forEach(ch => fetchChannelTraceForLap(lapIndex, ch.name, slot)),
            [...prev, lapIndex]
          )
    );
  }, [fetchChannelTraceForLap]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Custom lap colors (persisted server-side, see lapColorsByServer above) ──
  // Coloring the session's fastest lap (in the user's own session) sets the
  // sticky global fastest-lap color instead of a per-session entry; every
  // other lap keeps the plain per-session behavior.
  const handleColorChange1 = useCallback((lapIndex, color) => {
    const lap = session?.laps?.find(l => l.index === lapIndex);
    if (isOwner && lap && isBestLap(lap, getBestLapTime(session.laps))) {
      setFastestLapColor(color);
      return;
    }
    if (!sessionId) return;
    setLapColorsByServer(prev => ({
      ...(prev || {}),
      [sessionId]: { ...(prev?.[sessionId] || {}), [lapIndex]: color },
    }));
  }, [sessionId, session, isOwner]);
  const handleColorChangeCmp = useCallback((slot, lapIndex, color) => {
    const sid = cmpSessionIds[slot];
    if (!sid) return;
    setLapColorsByServer(prev => ({
      ...(prev || {}),
      [sid]: { ...(prev?.[sid] || {}), [lapIndex]: color },
    }));
  }, [cmpSessionIdKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Corner handlers ───────────────────────────────────────────────────────
  const handleCornerMove = useCallback(async (cornerId, lat, lng) => {
    if (!sessionId) return;
    try {
      const snapped = await snapCorner(sessionId, lat, lng);
      setCorners(prev => {
        const u = prev.map(c => c.id === cornerId
          ? mergeSnappedCorner(c, snapped)
          : c);
        persistCorners(trackId, trackName, u, sectors); return u;
      });
      setMetrics(null); resetAllCmpMetrics();
    } catch (err) {
      // Leave the canonical definition untouched if telemetry cannot support
      // the requested marker position.
      console.warn('Corner move could not be snapped to telemetry:', err);
    }
  }, [sessionId, trackId, trackName, sectors]);

  const handleCornerLabelChange = useCallback((cornerId, label) => {
    setCorners(prev => {
      const u = prev.map(c => c.id === cornerId ? { ...c, label } : c);
      persistCorners(trackId, trackName, u, sectors); return u;
    });
  }, [trackId, trackName, sectors]);

  const handleCornerAdd = useCallback(async (lat, lon) => {
    // Snap the click to the racing line so the new corner gets a real
    // entry/apex/exit window (per-corner metrics need one). If snapping fails
    // (no session, click off-line), fall back to the old point corner — the
    // admin can still drag it into place.
    let snapped = null;
    if (sessionId) {
      try {
        snapped = await snapCorner(sessionId, lat, lon);
      } catch (err) {
        console.warn('Corner snap failed, adding point corner:', err);
      }
    }
    setCorners(prev => {
      const { id, label } = nextTurnId(prev);
      const nc = snapped ? { ...snapped, id, label } : {
        id, label, lat, lon, apex_lat: lat, apex_lon: lon,
        entry_lat: lat, entry_lon: lon, exit_lat: lat, exit_lon: lon,
        direction: 'left', confidence: 1.0, detected_in_laps: [],
        entry_dist_m: 0, exit_dist_m: 0,
      };
      const u = [...prev, nc];
      persistCorners(trackId, trackName, u, sectors); return u;
    });
    setMetrics(null); resetAllCmpMetrics();
  }, [sessionId, trackId, trackName, sectors]);

  const handleCornerRemove = useCallback((cornerId) => {
    setCorners(prev => {
      const u = prev.filter(c => c.id !== cornerId);
      persistCorners(trackId, trackName, u, sectors); return u;
    });
    setMetrics(null); resetAllCmpMetrics();
  }, [trackId, trackName, sectors]);

  // Reorder corners by position along the lap and relabel "Turn 1..N", so
  // manually added turns pick up their conventional number instead of being
  // appended at the end. Corner ids stay stable (they key stored analytics);
  // only the array order and labels change.
  const handleRenumberTurns = useCallback(() => {
    const trace = Object.entries(lapTraces)
      .filter(([k]) => Number(k) >= 0)               // skip theoretical-best slot
      .map(([, t]) => t)
      .find(t => t?.points?.length > 1);
    if (!trace) return;
    const pts = trace.points;
    const coslat = Math.cos((pts[0].lat * Math.PI) / 180);
    const cum = new Array(pts.length);
    cum[0] = 0;
    for (let i = 1; i < pts.length; i++) {
      const dlat = pts[i].lat - pts[i - 1].lat;
      const dlon = (pts[i].lon - pts[i - 1].lon) * coslat;
      cum[i] = cum[i - 1] + Math.hypot(dlat, dlon);
    }
    // Trace runs S/F → S/F, so distance from the trace start orders corners by
    // lap position, including ones near the line.
    const posOf = (c) => {
      const alat = c.apex_lat ?? c.lat, alon = c.apex_lon ?? c.lon;
      let best = 0, bd = Infinity;
      for (let i = 0; i < pts.length; i++) {
        const dlat = pts[i].lat - alat;
        const dlon = (pts[i].lon - alon) * coslat;
        const d = dlat * dlat + dlon * dlon;
        if (d < bd) { bd = d; best = i; }
      }
      return cum[best];
    };
    setCorners(prev => {
      const u = [...prev]
        .sort((a, b) => posOf(a) - posOf(b))
        .map((c, i) => ({ ...c, label: `Turn ${i + 1}` }));
      persistCorners(trackId, trackName, u, sectors);
      return u;
    });
  }, [lapTraces, trackId, trackName, sectors]);

  // ── Sector handlers ───────────────────────────────────────────────────────
  // Sectors are persisted alongside corners via the same /tracks endpoint.
  const handleSectorsChange = useCallback((newSectors) => {
    setSectors(newSectors);
    persistCorners(trackId, trackName, corners, newSectors);
  }, [trackId, trackName, corners]);

  // ── Lap selection mode ────────────────────────────────────────────────────
  const handleLapSelectionModeChange = useCallback((mode) => {
    setLapSelectionMode(mode);
    if (!session) return;
    const indices = computeLapIndices(mode, session.laps, sectorTimesData);
    setSelectedLaps(prev => {
      // Fetch base + channel traces for newly-added laps so plotted channels and
      // Readouts populate. Formula panels are handled by the backfill effect.
      indices.filter(i => !prev.includes(i) && i !== TB_LAP_INDEX).forEach(i => {
        fetchTrace(i);
        tracedChannelsRef.current
          .filter(ch => ch.source === 'primary')
          .forEach(ch => fetchChannelTraceForLap(i, ch.name));
      });
      return indices;
    });
  }, [session, sectorTimesData, fetchTrace, fetchChannelTraceForLap]);

  // Re-apply sector-based selection when sector times data arrives.
  // Uses refs to avoid stale closures without re-triggering on every render.
  const lapSelectionModeRef = useRef(lapSelectionMode);
  useEffect(() => { lapSelectionModeRef.current = lapSelectionMode; }, [lapSelectionMode]);
  const sessionRef = useRef(session);
  useEffect(() => { sessionRef.current = session; }, [session]);

  useEffect(() => {
    const mode = lapSelectionModeRef.current;
    if (mode.type !== 'sector_best' || !sectorTimesData || !sessionRef.current) return;
    const indices = computeLapIndices(mode, sessionRef.current.laps, sectorTimesData);
    if (indices.length === 0) return;
    setSelectedLaps(prev => {
      indices.filter(i => !prev.includes(i)).forEach(i => fetchTrace(i));
      return indices;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectorTimesData]);

  // ── Theoretical Best — recompute when session or sectors change ──────────
  useEffect(() => {
    if (!sessionId) return;
    if (!sectors || sectors.length === 0) {
      fetchTb(null);
      return;
    }
    const t = setTimeout(() => { fetchTb(sectors); }, 400);
    return () => clearTimeout(t);
  }, [sessionId, sectors, fetchTb]);

  // ── Sector split times table — recompute when session or sectors change ───
  useEffect(() => {
    if (!sessionId || !sectors || sectors.length === 0) {
      setPrimarySectorTimesState(null);
      return;
    }
    let cancelled = false;
    setPrimarySectorTimesState({
      sessionId, data: null, loading: true, error: null,
    });
    const t = setTimeout(() => {
      getSectorTimes(sessionId, sectors)
        .then(data => {
          if (!cancelled) setPrimarySectorTimesState({
            sessionId, data, loading: false, error: null,
          });
        })
        .catch(error => {
          if (cancelled) return;
          setPrimarySectorTimesState({
            sessionId, data: null, loading: false,
            error: error?.message || 'Split times unavailable',
          });
        });
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [sessionId, sectors]);

  // Comparison sessions use the primary track's current sectors. Keep each
  // slot independently identifiable so a late response can never populate a
  // newly replaced comparison session.
  useEffect(() => {
    const ids = [...cmpSessionIds];
    if (!sectors || sectors.length === 0) {
      setCmpSectorTimesState([null, null, null]);
      return undefined;
    }

    let cancelled = false;
    setCmpSectorTimesState(ids.map(id => id ? {
      sessionId: id, data: null, loading: true, error: null,
    } : null));

    const t = setTimeout(async () => {
      const results = await Promise.all(ids.map(async id => {
        if (!id) return null;
        try {
          return { sessionId: id, data: await getSectorTimes(id, sectors), loading: false, error: null };
        } catch (error) {
          return {
            sessionId: id, data: null, loading: false,
            error: error?.message || 'Split times unavailable',
          };
        }
      }));
      if (!cancelled) setCmpSectorTimesState(results);
    }, 400);

    return () => { cancelled = true; clearTimeout(t); };
  }, [cmpSessionIdKey, sectors]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Redetect corners ──────────────────────────────────────────────────────
  // Debounced re-run. `syncCount` updates the Turns input to the geometric
  // result (used when sensitivity / merge change, which deliberately drop any
  // forced count and let the detector decide).
  const runRedetect = useCallback((opts) => {
    if (!sessionId) return;
    if (redetectTimerRef.current) clearTimeout(redetectTimerRef.current);
    redetectTimerRef.current = setTimeout(async () => {
      if (redetectLoadingRef.current) return;
      redetectLoadingRef.current = true;
      try {
        const result = await redetectCorners(sessionId, {
          targetCount: opts.targetCount ?? null,
          sensitivity: opts.sensitivity,
        });
        setCorners(result);
        setMetrics(null); resetAllCmpMetrics();
        if (opts.syncCount) {
          turnCountInitRef.current = result.length;
          setTurnCount(result.length);
        }
        if (trackId) persistCorners(trackId, trackName, result, sectors);
      } catch (err) {
        console.error('Corner redetect failed:', err);
      } finally {
        redetectLoadingRef.current = false;
      }
    }, 600);
  }, [sessionId, trackId, trackName, sectors]);

  const handleTurnCountChange = useCallback((v) => {
    if (!Number.isFinite(v) || v <= 0) return;
    turnCountInitRef.current = v;
    setTurnCount(v);
    runRedetect({ targetCount: v, sensitivity, syncCount: false });
  }, [runRedetect, sensitivity]);

  const handleSensitivityChange = useCallback((s) => {
    setSensitivity(s);
    runRedetect({ targetCount: null, sensitivity: s, syncCount: true });
  }, [runRedetect]);

  // ── Metrics ───────────────────────────────────────────────────────────────
  // Returns the freshly computed metrics (or null) so callers — notably
  // handleBuildPrompt — can use the value immediately without waiting on state.
  const handleCalculateMetrics = useCallback(async (revealPanel = true) => {
    if (!sessionId || selectedLaps.length === 0 || corners.length === 0) return null;
    setMetricsLoading(true); setMetricsError(null);
    if (revealPanel) updatePanelVisibility({ showMetrics: true });
    try {
      const includeTb = selectedLaps.includes(TB_LAP_INDEX);
      const realLapIndices = selectedLaps.filter(i => i !== TB_LAP_INDEX);
      const m = await computeMetrics(sessionId, realLapIndices, corners, includeTb);
      setMetrics(m);
      if (sectors?.length) {
        getSectorMetrics(sessionId, sectors).then(setSectorMetrics).catch(() => setSectorMetrics(null));
      }
      return m;
    } catch (err) {
      setMetricsError(err.message || 'Metrics calculation failed');
      return null;
    } finally {
      setMetricsLoading(false);
    }
  }, [sessionId, selectedLaps, corners, sectors]);

  const handleCalculateMetricsCmp = useCallback(async (slot, revealPanel = true) => {
    const sid = cmpSessionIds[slot];
    const sel = cmpSelectedLaps[slot];
    if (!sid || sel.length === 0 || corners.length === 0) return null;
    setAt(setCmpMetricsLoading, slot, true);
    setAt(setCmpMetricsError, slot, null);
    if (revealPanel) updatePanelVisibility({ showMetrics: true });
    try {
      const m = await computeMetrics(sid, sel, corners);
      setAt(setCmpMetrics, slot, m);
      if (sectors?.length) {
        getSectorMetrics(sid, sectors)
          .then(sm => setAt(setCmpSectorMetrics, slot, sm))
          .catch(() => setAt(setCmpSectorMetrics, slot, null));
      }
      return m;
    } catch (err) {
      setAt(setCmpMetricsError, slot, err.message || 'Metrics calculation failed');
      return null;
    } finally {
      setAt(setCmpMetricsLoading, slot, false);
    }
  }, [cmpSessionIdKey, cmpSelectedLaps, corners, sectors]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-calculate corner metrics while the Corner Metrics panel is open ──
  // The compute is fast, so there is no Calculate button: any change to the
  // session, lap selection, or corner definitions recomputes after a short
  // debounce (rapid lap toggling coalesces into one call). The signature ref
  // stops repeat runs for identical inputs — including after a failure, so a
  // persistent error can't retry-loop (it stays visible until inputs change).
  const autoCalcSigRef = useRef({});
  useEffect(() => {
    if (!showMetrics || corners.length === 0) return;
    const jobs = [];
    if (sessionId && selectedLaps.length > 0) {
      jobs.push({ key: 'primary', sig: JSON.stringify([sessionId, selectedLaps, corners]),
                  run: () => handleCalculateMetrics(false) });
    }
    cmpSessionIds.forEach((sid, slot) => {
      const sel = cmpSelectedLaps[slot];
      if (sid && sel?.length > 0) {
        jobs.push({ key: `cmp${slot}`, sig: JSON.stringify([sid, sel, corners]),
                    run: () => handleCalculateMetricsCmp(slot, false) });
      }
    });
    const pending = jobs.filter(j => autoCalcSigRef.current[j.key] !== j.sig);
    if (pending.length === 0) return;
    const t = setTimeout(() => {
      pending.forEach(j => { autoCalcSigRef.current[j.key] = j.sig; j.run(); });
    }, 400);
    return () => clearTimeout(t);
  }, [showMetrics, sessionId, selectedLaps, corners, cmpSessionIdKey, cmpSelectedLaps,  // eslint-disable-line react-hooks/exhaustive-deps
      handleCalculateMetrics, handleCalculateMetricsCmp]);

  // ── Build LLM Prompt ──────────────────────────────────────────────────────
  // `headToHeadPayload` accompanies mode 'headtohead': the Head-to-Head panel
  // builds it from its own (freshest) derived data and passes it through.
  // `silent` skips the AI Briefing panel entirely and returns the briefing to
  // the caller (errors thrown) — used by the inline Head-to-Head AI coach.
  const handleBuildPrompt = useCallback(async (coachingMode = 'goal', headToHeadPayload = null, { silent = false } = {}) => {
    if (selectedLaps.length === 0 || corners.length === 0) {
      if (silent) throw new Error('Select laps to enable coaching');
      return;
    }
    if (!silent) {
      setPromptLoading(true); setPromptError(null); updatePanelVisibility({ showPromptPanel: true }); setBriefing(null);
    }

    // Metrics are an input to the briefing, so recompute them from the current
    // lap selection first — the user shouldn't have to remember to press
    // Calculate after changing laps. Fall back to the last-computed metrics if
    // the recompute fails (no worse than before).
    // Pass revealPanel=false so a build doesn't force the Corner Metrics panel
    // open — it only refreshes if the user already had it showing.
    const freshMetrics = (await handleCalculateMetrics(false)) || metrics;
    if (!freshMetrics) {
      if (silent) throw new Error('Could not compute metrics for the selected laps.');
      setPromptError('Could not compute metrics for the selected laps.');
      setPromptLoading(false);
      return;
    }
    // The AI briefing's comparison mode is two-driver: it uses the first
    // comparison slot (Driver 2) as the reference. Additional overlaid drivers
    // are visual-only and do not feed the briefing. The head-to-head builder
    // ignores driver2 entirely (its data rides in `head_to_head`), so the
    // silent inline-coach path skips the comparison recompute.
    const cmp0Session = cmp0.session;
    let freshMetrics2 = cmpMetrics[0];
    if (cmp0Session && !silent) {
      freshMetrics2 = (await handleCalculateMetricsCmp(0, false)) || cmpMetrics[0];
    }

    const profile = driverProfile || {};
    const experienceLabel = EXPERIENCE_LABELS[profile.experience] || profile.experience || 'HPDE Intermediate';

    const timed1   = session?.laps.filter(l => !l.is_outlap && !l.is_inlap) || [];
    const bestLap1 = timed1.length ? [...timed1].sort((a, b) => a.lap_time_seconds - b.lap_time_seconds)[0] : null;
    const avgSec1  = timed1.length ? timed1.reduce((s, l) => s + l.lap_time_seconds, 0) / timed1.length : 0;
    const avgStr1  = avgSec1 ? `${Math.floor(avgSec1 / 60)}:${(avgSec1 % 60).toFixed(3).padStart(6, '0')}` : '';

    const payload = {
      track_name: trackName || session?.meta?.session_name || 'Unknown Track',
      session_id: sessionId,
      sectors,
      corners,
      // 'corner' (top-3 corners by estimated loss) or 'sector' (sector-anchored
      // plan by measured loss). Ignored server-side for the comparison case.
      mode: coachingMode,
      driver1: {
        name: profile.name || 'Driver',
        experience: experienceLabel,
        car: profile.car || session?.meta?.vehicle || '',
        notes: profile.notes || '',
      },
      metrics1:   freshMetrics,
      lap_count1: selectedLaps.length,
      best_lap1:  bestLap1?.lap_time_str || '',
      avg_lap1:   avgStr1,
    };

    if (cmp0Session && freshMetrics2) {
      const timed2   = cmp0Session.laps.filter(l => !l.is_outlap && !l.is_inlap);
      const bestLap2 = timed2.length
        ? [...timed2].sort((a, b) => a.lap_time_seconds - b.lap_time_seconds)[0]
        : null;
      payload.driver2    = { name: cmp0Session.meta?.racer || 'Driver 2', experience: '', car: cmp0Session.meta?.vehicle || '', notes: '' };
      payload.metrics2   = freshMetrics2;
      payload.lap_count2 = cmpSelectedLaps[0].length;
      payload.best_lap2  = bestLap2?.lap_time_str || '';
    }
    if (coachingMode === 'headtohead' && headToHeadPayload) {
      payload.head_to_head = headToHeadPayload;
    }

    if (silent) return buildBriefing(payload);

    try {
      setBriefing(await buildBriefing(payload));
    } catch (err) {
      setPromptError(err.message || 'Prompt generation failed');
    } finally {
      setPromptLoading(false);
    }
  }, [metrics, cmpMetrics, handleCalculateMetrics, handleCalculateMetricsCmp, driverProfile, session, cmp0.session, sessionId, selectedLaps, cmpSelectedLaps, corners, sectors, trackName]);

  // ── Auto-run the comparison briefing after a leaderboard "Compare" ─────────
  // handleCompareSessions sets autoCompareRef; once both sessions, corners and
  // best laps are ready, compute metrics for both drivers, then build the
  // (deterministic) comparison briefing once. Cleared after it fires.
  useEffect(() => {
    if (!autoCompareRef.current) return;
    if (!(session && cmp0.session && corners.length && selectedLaps.length && cmpSelectedLaps[0].length)) return;
    if (!metrics && !metricsLoading) handleCalculateMetrics();
    if (!cmpMetrics[0] && !cmpMetricsLoading[0]) handleCalculateMetricsCmp(0);
    if (metrics && cmpMetrics[0]) {
      autoCompareRef.current = false;
      if (aiDebug) handleBuildPrompt('corner');
    }
  }, [session, cmp0.session, corners, selectedLaps, cmpSelectedLaps, metrics, cmpMetrics,
      metricsLoading, cmpMetricsLoading, aiDebug,
      handleCalculateMetrics, handleCalculateMetricsCmp, handleBuildPrompt]);

  // Account Settings modal (profile, preferences, cars/vehicle physics, groups,
  // following, security, API keys) — shared by the analytics full-page views
  // below (which early-return before the upload/analysis modal mounts) so
  // AppNav's "User Settings" link actually opens something everywhere.
  const settingsModal = showSettingsModal && (
    <AccountSettings
      onClose={() => {
        setShowSettingsModal(false);
        setSettingsAutoOpenPhysics(false);
        setSettingsInitialCarId(null);
      }}
      onSave={(profile, updatedUser) => {
        setDriverProfile(profile);
        if (updatedUser && onUpdateUser) onUpdateUser(updatedUser);
      }}
      currentUser={currentUser}
      capabilities={productCapabilities}
      initialTab={settingsInitialTab}
      unitPrefs={unitPrefs}
      onUnitPrefsChange={handleUnitPrefsChange}
      autoShare={productCapabilities.session_sharing ? autoShare : false}
      onAutoShareChange={productCapabilities.session_sharing ? setAutoShare : null}
      initialCarId={settingsInitialCarId}
      autoOpenPhysics={settingsAutoOpenPhysics}
      liveSessionCarId={vehicleParams?.car_id ?? null}
      liveValue={vehicleParams}
      onLiveChange={setVehicleParams}
      sessionId={sessionId}
      racer={session?.meta?.racer}
      onMetaSaved={({ vehicle, racer }) => {
        setSession(prev => prev ? {
          ...prev,
          meta: { ...prev.meta, vehicle, racer },
        } : prev);
        setVehicleParams(prev => prev ? { ...prev, vehicle_name: vehicle } : prev);
      }}
    />
  );

  // Shared handlers for AppNav's cross-page links / user settings / log-off.
  // onGoToSimLab is null for non-admins — that null IS the admin gate for the
  // Similarity Lab item (AppNav hides it when the prop is falsy).
  const openStudioFromAnalysis = () => {
    const drivers = [{ sessionId, session, selected: selectedLaps }, ...cmpHooks.map((h, i) => ({ sessionId: h.sessionId, session: h.session, selected: cmpSelectedLaps[i] }))];
    const seed = drivers.filter(d => d.sessionId && d.session).map((d, index) => {
      const timed = d.session.laps.filter(l => !l.is_outlap && !l.is_inlap && l.lap_time_seconds > 0);
      const chosen = d.selected.filter(i => i >= 0);
      const first = chosen.length ? Math.min(...chosen) : [...timed].sort((a,b) => a.lap_time_seconds-b.lap_time_seconds)[0]?.index;
      const last = chosen.length ? Math.max(...chosen) : first;
      return {session_id: d.sessionId, label: d.session.meta?.racer || `Driver ${index+1}`, laps: first == null ? [] : Array.from({length: last-first+1}, (_, i) => first+i)};
    });
    if(seed[0]) seed[0].distanceRange = studioDistanceRange.current;
    setStudioSeed(seed); setView('studio');
  };
  const pageNavProps = {
    onGoToStudio: restricted ? null : () => { setStudioSeed(null); setView('studio'); },
    onGoToLeaderboard:  productCapabilities.public_leaderboards ? () => setView('leaderboard') : null,
    onGoToProgress:     productCapabilities.cross_session_analytics ? () => setView('progress') : null,
    onGoToChannelStats: productCapabilities.cross_session_analytics ? () => setView('channelstats') : null,
    onGoToSearch:       productCapabilities.cross_session_analytics ? () => setView('search') : null,
    onGoToSimLab:       currentUser?.is_admin ? () => setView('simlab') : null,
    onGoToIssues:       currentUser?.is_admin ? () => { setIssuesInitialId(null); setView('issues'); } : null,
    openIssueCount,
    onReportIssue:      issueReport.available ? () => issueReport.openIssueReport() : null,
    onOpenUserSettings: () => { setSettingsInitialTab('profile'); setShowSettingsModal(true); },
    onLogout: productCapabilities.user_accounts ? onLogout : null,
    driverProfile,
    currentUser,
    onStartMasquerade: productCapabilities.user_accounts ? onRequestMasquerade : null,
    onStopMasquerade: productCapabilities.user_accounts ? onStopMasquerade : null,
  };

  // Rendered by BOTH the full-page views and the analysis view. The leaderboard
  // (and every other full-page view) returns early, so a modal declared only at
  // the bottom of the analysis tree would never mount there — the button would
  // set state and nothing would appear.
  const driverName1    = session?.meta?.racer || driverProfile?.name || currentUser?.display_name || 'Driver 1';

  // Every entry point funnels through the same rule (utils/lap3dSelection): one
  // lap per driver first, then the fastest fill, capped at four. From the
  // analysis page with a single session that is simply "the four fastest laps
  // you have ticked".
  //
  // Deliberately a plain function, not a useCallback: as a hook below the
  // full-page views' early returns it ran on the analysis view and not on the
  // others, which is React error #310 ("rendered more hooks than during the
  // previous render"). It now sits ABOVE those early returns, because the 3D
  // panel's auto-seed effect below has to call it and effects may not live
  // after a conditional return. It reads only state declared further up, so the
  // move is safe; keep it here.
  // Every loaded driver with their ticked laps, shared by every 3D entry point.
  const build3DDrivers = () => {
    // Lap time comes from the session's own lap list first and the trace only
    // as a fallback. Traces are fetched lazily, so keying off them meant a lap
    // that was ticked but not yet loaded looked timeless and got dropped.
    const lapEntry = (sess, traces, sel, i, palette, custom) => ({
      lapIndex: i,
      timeS: sess?.laps?.[i]?.lap_time_seconds ?? traces?.[i]?.lap_time_seconds,
      label: `Lap ${lapDisplayNumber(sess?.laps, i, traces?.[i]?.lap_number)}`,
      color: getLapColor(i, sel, palette, custom),
    });
    const drivers = [];
    if (session?.session_id) {
      const sel = (selectedLaps || []).filter(i => i !== TB_LAP_INDEX);
      drivers.push({
        sessionId: session.session_id,
        name: driverName1 || session.meta?.racer || 'Driver',
        car: session.meta?.vehicle,
        laps: sel.map(i => lapEntry(session, lapTraces, selectedLaps, i, LAP_COLORS, customColors1)),
      });
    }
    // A comparison driver's ticked laps live in App state, NOT on its useSession
    // hook — the hook has no selectedLaps at all. Reading d.selectedLaps here
    // silently yielded [] for every comparison driver, so the 3D view only ever
    // showed the first session however many were loaded.
    cmpHooks.forEach((d, slot) => {
      if (!d.session?.session_id) return;
      const sel = (cmpSelectedLaps[slot] || []).filter(i => i !== TB_LAP_INDEX);
      drivers.push({
        sessionId: d.session.session_id,
        name: compareDriverLabel(slot),
        car: d.session.meta?.vehicle,
        laps: sel.map(i => lapEntry(
          d.session, d.lapTraces, cmpSelectedLaps[slot],
          i, COMPARE_PALETTES[slot], cmpCustomColors[slot],
        )),
      });
    });
    return drivers;
  };

  // What the 3D panel would race if it (re)synced now — the same rule as
  // "Compare in 3D", so the panel and the modal never disagree about what the
  // current selection means.
  const lap3dFromSelection = () => toLap3dRequest(pickLap3dLaps(build3DDrivers()).picked);

  // The reference lap's own trace, for the 3D panel's cursor sync. Driver 1's
  // traces live here; a comparison driver's live on its own useSession hook, so
  // both are searched — the 3D reference car can be any of the racing laps.
  // The whole trace, not just its points: the sync needs start_time to rebuild
  // the same session-relative clock the traces put on the shared cursor.
  const lap3dLapTrace = (sessionId, lapIndex) => {
    if (sessionId === session?.session_id) return lapTraces?.[lapIndex] || null;
    for (const d of cmpHooks) {
      if (d?.session?.session_id === sessionId) return d.lapTraces?.[lapIndex] || null;
    }
    return null;
  };

  // What the panel should be racing, and a stable identity for it.
  const lap3dWanted = lap3dFromSelection();
  const lap3dWantedKey = lap3dWanted.map((l) => `${l.session_id}:${l.lap_index}`).join(',');
  const lap3dSeenKey = useRef(null);

  // The panel follows the lap selection. Rebuilding is a fetch plus a three.js
  // teardown, but it lands fast enough that asking the user to press a button
  // was just friction.
  //
  // It tracks *changes* to the selection rather than equality with it, which is
  // what lets a lap set docked over from the modal survive: docking pins a set
  // that may differ from what is ticked, and re-deriving on equality would throw
  // it away immediately. It stays until the lap list is next touched.
  //
  // Debounced, because ticking three laps is three renders and each one would
  // otherwise tear the scene down and refetch mid-build.
  useEffect(() => {
    if (!showLap3D || !session?.session_id || !lap3dWanted.length) return undefined;
    if (lap3dSeenKey.current === lap3dWantedKey) return undefined;
    const t = setTimeout(() => {
      lap3dSeenKey.current = lap3dWantedKey;
      setLap3dPanelLaps(lap3dWanted);
    }, 400);
    return () => clearTimeout(t);
    // lap3dWanted is rebuilt every render; the key is its stable identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showLap3D, lap3dWantedKey, session?.session_id]);

  // Publish what's on screen for "Report a problem". A ref write in the
  // provider, so this costs nothing per render; the dialog reads it on open.
  issueReport.setPageContext({
    view,
    sessionId: sessionId || null,
    sessionLabel: session?.meta?.session_label || null,
    trackName: session?.meta?.session_name || null,
    selectedLaps,
    compareSessionIds: cmpSessionIds.filter(Boolean),
    adminModeActive: currentUser?.is_admin ? adminModeActive : undefined,
    masquerading: !!currentUser?.masquerade,
    mobile: isMobile,
    edition: appConfig?.edition,
  });

  // Open-issue badge for admins: one cheap fetch per page load (the Issues
  // page keeps it current while it's open).
  useEffect(() => {
    if (!currentUser?.is_admin || restricted) return undefined;
    let cancelled = false;
    listIssues({ status: 'open', limit: 1 })
      .then(page => { if (!cancelled) setOpenIssueCount(page.counts?.open ?? 0); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [currentUser?.is_admin, restricted]);

  // Docking is offered only where there is a panel layout to dock into. The
  // leaderboard and the other full-page views share this same modal but have no
  // WindowFrames, so the button would set state nothing could render.
  const canDockLap3d = view === 'sessions' && !!session?.session_id;
  const lap3dModal = lap3dLaps && (
    <Suspense fallback={null}>
      <Lap3DModal
        laps={lap3dLaps}
        onClose={() => setLap3dLaps(null)}
        onDock={canDockLap3d ? (laps) => { openLap3dPanel(laps); setLap3dLaps(null); } : null}
      />
    </Suspense>
  );

  const renderAppPage = (current, helpPage, onGoToSessions, content) => (
    <div className="appShell">
      <AppHeader
        current={current}
        helpPage={helpPage}
        onGoToSessions={onGoToSessions}
        adminModeActive={['sessions', 'studio'].includes(current) ? adminModeActive : undefined}
        onToggleAdminMode={['sessions', 'studio'].includes(current) && currentUser?.is_admin ? toggleAdminMode : undefined}
        {...pageNavProps}
      />
      <div className="appShellContent">{content}</div>
      {settingsModal}
      {lap3dModal}
    </div>
  );

  if (view === 'studio' && !restricted) {
    return renderAppPage('studio', 'video-studio', handleGoToSessions, (
      <Suspense fallback={<div style={{padding: 24}}>Loading Video Studio…</div>}>
        <VideoStudio seed={studioSeed} onSeedConsumed={() => setStudioSeed(null)} userId={currentUser?.id || 'desktop'} isAdmin={!!currentUser?.is_admin} adminModeActive={adminModeActive} />
      </Suspense>
    ));
  }

  // ── Leaderboard full-page view (available from both session-list and analysis) ──
  if (view === 'leaderboard' && !publicMode) {
    const goToSessions = () => { setLeaderboardPageParams(null); handleGoToSessions(); };
    return renderAppPage('leaderboard', 'leaderboard', goToSessions, (
      <LeaderboardPage
        onOpen3D={setLap3dLaps}
        {...pageNavProps}
        onBack={goToSessions}
        initialParams={leaderboardPageParams}
        onParamsChange={setLeaderboardPageParams}
        onLoadSession={(sid) => {
          setPendingSessionId(sid);
          setView('sessions');
          loadSession(sid).then(data => {
            if (data) {
              setCanComment(data.can_comment !== false);
              setIsOwner(data.is_owner !== false);
            }
            setPendingSessionId(null);
          });
        }}
        onCompareSessions={handleCompareSessions}
        unitPrefs={unitPrefs}
        currentUser={currentUser}
      />
    ));
  }

  // ── Progress full-page view (career rollup + per-track field comparison) ──
  if (view === 'progress' && !publicMode) {
    const goToSessions = () => { setProgressPageParams(null); handleGoToSessions(); };
    return renderAppPage('progress', 'progress', goToSessions, (
      <ProgressPage
        {...pageNavProps}
        onBack={goToSessions}
        initialParams={progressPageParams}
        onParamsChange={setProgressPageParams}
        onLoadSession={(sid) => {
          setPendingSessionId(sid);
          setView('sessions');
          loadSession(sid).then(data => {
            if (data) {
              setCanComment(data.can_comment !== false);
              setIsOwner(data.is_owner !== false);
            }
            setPendingSessionId(null);
          });
        }}
        onCompareSessions={handleCompareSessions}
        currentUser={currentUser}
        unitPrefs={unitPrefs}
      />
    ));
  }

  // ── Channel Statistics full-page view (descriptive stats across sessions) ──
  if (view === 'channelstats' && !publicMode) {
    const goToSessions = () => { setChannelStatsPageParams(null); handleGoToSessions(); };
    return renderAppPage('channelstats', 'channelstats', goToSessions, (
      <ChannelStatsPage
        {...pageNavProps}
        onBack={goToSessions}
        initialParams={channelStatsPageParams}
        onParamsChange={setChannelStatsPageParams}
        onLoadSession={(sid) => {
          setPendingSessionId(sid);
          setView('sessions');
          loadSession(sid).then(data => {
            if (data) {
              setCanComment(data.can_comment !== false);
              setIsOwner(data.is_owner !== false);
            }
            setPendingSessionId(null);
          });
        }}
        currentUser={currentUser}
        unitPrefs={unitPrefs}
      />
    ));
  }

  // ── Session Report full-page view (sections over a selection of sessions) ──
  if (view === 'report' && !publicMode) {
    const goToSessions = () => { setReportPageParams(null); handleGoToSessions(); };
    return renderAppPage('report', 'report', goToSessions, (
      <Suspense fallback={<div className="appShellContent" />}>
        <ReportPage
          {...pageNavProps}
          onBack={goToSessions}
          initialParams={reportPageParams}
          onParamsChange={setReportPageParams}
          onLoadSession={(sid) => {
            setPendingSessionId(sid);
            setView('sessions');
            loadSession(sid).then(data => {
              if (data) {
                setCanComment(data.can_comment !== false);
                setIsOwner(data.is_owner !== false);
              }
              setPendingSessionId(null);
            });
          }}
          currentUser={currentUser}
          unitPrefs={unitPrefs}
        />
      </Suspense>
    ));
  }

  // ── Search full-page view (cross-session sector / point queries) ──────────
  if (view === 'search' && !publicMode) {
    const goToSessions = () => { setSearchPageParams(null); handleGoToSessions(); };
    return renderAppPage('search', 'search', goToSessions, (
      <SearchPage
        {...pageNavProps}
        onBack={goToSessions}
        initialParams={searchPageParams}
        onParamsChange={setSearchPageParams}
        onLoadSession={(sid) => {
          setPendingSessionId(sid);
          setView('sessions');
          loadSession(sid).then(data => {
            if (data) {
              setCanComment(data.can_comment !== false);
              setIsOwner(data.is_owner !== false);
            }
            setPendingSessionId(null);
          });
        }}
        currentUser={currentUser}
        unitPrefs={unitPrefs}
      />
    ));
  }

  // ── Similarity Lab (admin-only debug view over the corner vectors) ────────
  if (view === 'simlab' && !publicMode && !!currentUser?.is_admin) {
    const goToSessions = () => { setSimLabInitialTrackId(null); handleGoToSessions(); };
    return renderAppPage('simlab', 'simlab', goToSessions, (
      <SimilarityLabPage
        {...pageNavProps}
        onBack={goToSessions}
        initialTrackId={simLabInitialTrackId}
        onTrackChange={setSimLabInitialTrackId}
        onLoadSession={(sid, seekCsvTime = null) => {
          // Video-search hits carry the moment that matched. VideoPanel's seekTo
          // takes telemetry time and adds the sync lead offset itself, so this is
          // csv_time rather than the hit's video_time.
          pendingVideoSeekRef.current = seekCsvTime;
          setPendingSessionId(sid);
          setView('sessions');
          loadSession(sid).then(data => {
            if (data) {
              setCanComment(data.can_comment !== false);
              setIsOwner(data.is_owner !== false);
            }
            setPendingSessionId(null);
          });
        }}
        currentUser={currentUser}
      />
    ));
  }

  // ── Issue Reports (admin-only triage queue) ──────────────────────────────
  if (view === 'issues' && !publicMode && !!currentUser?.is_admin) {
    const goToSessions = () => { setIssuesInitialId(null); handleGoToSessions(); };
    return renderAppPage('issues', 'issues', goToSessions, (
      <IssuesPage
        initialIssueId={issuesInitialId}
        onIssueCountsChange={counts => setOpenIssueCount(counts?.open ?? null)}
        onLoadSession={(sid) => {
          setPendingSessionId(sid);
          setView('sessions');
          loadSession(sid).then(data => {
            if (data) {
              setCanComment(data.can_comment !== false);
              setIsOwner(data.is_owner !== false);
            }
            setPendingSessionId(null);
          });
        }}
      />
    ));
  }

  // ── Upload guard ──────────────────────────────────────────────────────────
  if (!session) {
    // Guest sandbox: show the drop zone + browser-local upload list.
    if (guestMode) {
      return <GuestHome onOpenSession={handleOpenGuestSession} />;
    }

    // Public share-link mode never shows the upload/session-list UI — just a
    // loading state until the shared session resolves (or an error if it failed).
    if (publicMode) {
      return (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          minHeight: '100vh', background: '#0f1117', color: '#9ca3af',
          textAlign: 'center', padding: '2rem',
        }}>
          {error ? `Could not load this shared session: ${error}` : 'Loading shared session…'}
        </div>
      );
    }

    return renderAppPage('sessions', 'upload', handleGoToSessions, (
        <UploadPanel
          onUpload={(f) => { setCanComment(true); upload(f).then(noteUploaded); }}
          onUploadVideo={(f) => { setCanComment(true); uploadMov(f).then(noteUploaded); }}
          onUploadXrk={(f) => { setCanComment(true); uploadXrk(f).then(noteUploaded); }}
          onUploadMotec={(f) => { setCanComment(true); uploadMotec(f).then(noteUploaded); }}
          onUploadSmartycam3={(f) => { setCanComment(true); uploadSmartycam3(f).then(noteUploaded); }}
          onLoadSession={(sessionId, sessionCanComment, sessionIsOwner) => {
            setCanComment(sessionCanComment !== false);
            setIsOwner(sessionIsOwner !== false);
            loadSession(sessionId);
          }}
          loading={loading}
          error={error}
          multiSessionNotice={multiSessionNotice}
          onClearMultiSessionNotice={() => setMultiSessionNotice('')}
          uploadProgress={uploadProgress}
          duplicateInfo={duplicateInfo}
          onForceUpload={() => { setCanComment(true); forceUpload().then(noteUploaded); }}
          uploadSignal={uploadSignal}
          onClearDuplicate={clearDuplicate}
          {...pageNavProps}
          unitPrefs={unitPrefs}
          currentUser={currentUser}
          adminModeActive={adminModeActive}
          sessionSharing={productCapabilities.session_sharing}
          desktopSync={productCapabilities.sync_to_server}
          onOpenServerSync={handleOpenServerSync}
          onOpenReport={productCapabilities.cross_session_analytics
            ? (params) => { setReportPageParams(params); setView('report'); }
            : null}
        />
    ));
  }


  const open3D = (drivers) => {
    const { picked } = pickLap3dLaps(drivers);
    if (picked.length) setLap3dLaps(toLap3dRequest(picked));
  };

  // "View in 3D" sits with the first session and races that session's own laps.
  // build3DDrivers puts the primary first, so this is a slice — guarded, so that
  // with no primary loaded it takes nothing rather than the first comparison.
  const open3DFromSelection = () =>
    open3D(build3DDrivers().slice(0, session?.session_id ? 1 : 0));

  // "Compare in 3D": every driver's ticked laps. pickLap3dLaps caps the field at
  // four, taking one lap per driver before it fills the rest fastest-first, so a
  // driver with several quick laps can't crowd anyone off the track.
  const open3DCompare = () => open3D(build3DDrivers());

  // Show the panel. A lap set is only passed by the modal's "dock" button, which
  // hands over its own rather than re-deriving one — docking must keep racing
  // exactly what you were already watching. Opening it from the View menu passes
  // nothing and lets the seeding effect fill it, so first-open and restore-from-
  // saved-layout go through one path instead of two that can disagree.
  const openLap3dPanel = (laps) => {
    if (laps?.length) {
      setLap3dPanelLaps(laps);
      // Mark the current selection as already applied. Without this, docking a
      // race from the leaderboard (whose laps need not be ticked anywhere) would
      // be overwritten by the follow-the-selection effect a moment later.
      lap3dSeenKey.current = lap3dWantedKey;
    }
    updatePanelVisibility({ showLap3D: true });
  };

  // Has the panel drifted from the ticked selection? With the panel following the
  // selection this is normally false; it goes true only when a race was docked
  // over from the modal, where "back to my selection" is a real thing to want.
  const lap3dPanelStale = showLap3D && !!lap3dPanelLaps && lap3dWanted.length > 0
    && lap3dWantedKey !== lap3dPanelLaps.map((l) => `${l.session_id}:${l.lap_index}`).join(',');
  // The corridor is anchored to the Analysis page's current canonical track,
  // whether its cohort is this outing or the signed-in driver's track history.
  // A docked leaderboard/modal race may contain no current-session lap; in that
  // case it keeps the existing comparison payload untouched.
  const lap3dCorridorSessionId = sessionId && lap3dPanelLaps?.some(
    (lap) => lap.session_id === sessionId,
  ) ? sessionId : null;

  const can3D = (selectedLaps || []).some(i => i !== TB_LAP_INDEX
    && Number.isFinite(lapTraces?.[i]?.lap_time_seconds));

  // Comparison is possible on any driver's selection, not just the first one's:
  // deselecting every lap on session 1 must not disable a comparison that the
  // other drivers can still supply.
  const canCompare3D = build3DDrivers()
    .some((d) => d.laps.some((l) => Number.isFinite(l.timeS)));

  // S/F-origin mismatch detection (utils/sfAlignment.js): each driver's offset
  // is measured against the track's canonical start/finish; when none is stored
  // fall back to Driver 1's own lap-start point (warn-only — can't say which
  // side is wrong, so no re-align without a canonical reference).
  const sfReference = trackStartFinish
    || (session ? lapStartPoint(session.laps, lapTraces) : null);
  const primarySfOffsetM = session && trackStartFinish
    ? sfOffsetM(session.laps, lapTraces, trackStartFinish)
    : null;
  const canRealignPrimary = !!trackStartFinish && (isOwner || !!currentUser?.is_admin);
  const primarySfRealigned = !!session?.sf_realigned;
  const canUnalignPrimary = isOwner || !!currentUser?.is_admin;

  // Active comparison drivers (loaded slots only). One rich descriptor per slot,
  // consumed by the lap selector, chart overlays, metrics tabs and video panel.
  const compareDrivers = cmpHooks.map((h, slot) => {
    if (!h.session) return null;
    return {
      sfOffsetM:     sfOffsetM(h.session.laps, h.lapTraces, sfReference),
      sfRealigned:   !!h.session.sf_realigned,
      canRealign:    !!trackStartFinish
                       && ((h.session.is_owner !== false) || !!currentUser?.is_admin),
      canUnalign:    (h.session.is_owner !== false) || !!currentUser?.is_admin,
      onRealign:     () => handleRealignSession(slot),
      onUnalign:     () => handleRealignSession(slot, true),
      realigning:    realigningSlot === slot,
      slot,
      session:       h.session,
      sessionId:     h.sessionId,
      laps:          h.session.laps,
      carHealth:     h.session.car_health || null,
      lapTraces:     h.lapTraces,
      loadingTraces: h.loadingTraces,
      fetchTrace:    h.fetchTrace,
      selectedLaps:  cmpSelectedLaps[slot],
      customColors:  cmpCustomColors[slot],
      palette:       COMPARE_PALETTES[slot],
      accent:        COMPARE_PALETTES[slot][0],
      name:          h.session?.meta?.racer || `Driver ${slot + 2}`,
      metrics:       cmpMetrics[slot],
      metricsLoading: cmpMetricsLoading[slot],
      metricsError:  cmpMetricsError[slot],
      sectorMetrics: cmpSectorMetrics[slot],
      powerTraces:   cmpPowerTraces[slot],
      formulaTraces: cmpFormulaTraces[slot],
      lapColorMap:   cmpLapColorMaps[slot],
      lapNumberByIndex: Object.fromEntries((h.session?.laps || []).map(l => [l.index, l.lap_number])),
      onToggle:      (lapIndex) => handleToggleLapCmp(slot, lapIndex),
      onColorChange: (lapIndex, color) => handleColorChangeCmp(slot, lapIndex, color),
      onCalculate:   (revealPanel = true) => handleCalculateMetricsCmp(slot, revealPanel),
      onRemove:      () => handleRemoveSessionCmp(slot),
      canCalculate:  cmpSelectedLaps[slot].length > 0 && corners.length > 0,
      videoUsable:   !!(h.session.has_video || h.session.has_rendered_video),
    };
  }).filter(Boolean);

  const openTrackDifferencePanel = (pair = null) => {
    if (pair) setTrackDifferencePair(pair);
    updatePanelVisibility({ showTrackDifference: true });
    if (isMobile) {
      setActiveMobilePanel('trackDifference');
      localStorage.setItem('mobile.activePanel', 'trackDifference');
    }
  };

  // A formula panel overlays each comparison driver from that driver's own
  // evaluation of the expression — ChannelTrace reads `lapTraces` off the
  // descriptor, so remap it per formula the way PowerTrace does for power.
  const formulaCompareDrivers = (formulaId) =>
    compareDrivers.map(d => ({ ...d, lapTraces: d.formulaTraces?.[formulaId] || {} }));

  const splitTimeDatasets = [
    ...(session && sessionId ? [{
      key: 'primary',
      sessionId,
      driverName: driverName1,
      sessionLabel: session.meta?.session_label,
      date: session.meta?.date,
      vehicle: session.meta?.vehicle,
      accent: '#e8411a',
      sfOffsetM: primarySfOffsetM,
      data: sectorTimesData,
      loading: sectorTimesLoading,
      error: sectorTimesError,
    }] : []),
    ...compareDrivers.map(driver => {
      const state = cmpSectorTimesState[driver.slot];
      const current = state?.sessionId === driver.sessionId ? state : null;
      return {
        key: `compare-${driver.slot}`,
        sessionId: driver.sessionId,
        driverName: driver.name,
        sessionLabel: driver.session?.meta?.session_label,
        date: driver.session?.meta?.date,
        vehicle: driver.session?.meta?.vehicle,
        accent: driver.accent,
        sfOffsetM: driver.sfOffsetM,
        data: current?.data || null,
        loading: current ? current.loading : true,
        error: current?.error || null,
      };
    }),
  ];

  const canAddCompare = compareDrivers.length < MAX_COMPARE;
  const excludeSessionIds = [sessionId, ...cmpSessionIds].filter(Boolean);
  const addingCompare = cmpHooks.some(h => h.loading);
  const videoDrivers = [
    ...((session?.has_video || session?.has_rendered_video) ? [{
      sessionId,
      session,
      videoSrc: videoSrcFor(session, sessionId),
      defaultTrim: videoDefaultTrim(session),
      laps: session?.laps ?? [],
      selectedLaps,
      lapColors: lapColorMap,
      lapTraces,
      name: driverName1,
      isPrimary: true,
    }] : []),
    ...compareDrivers.filter(driver => driver.videoUsable).map(driver => ({
      sessionId: driver.sessionId,
      session: driver.session,
      videoSrc: videoSrcFor(driver.session, driver.sessionId),
      defaultTrim: videoDefaultTrim(driver.session),
      laps: driver.laps,
      selectedLaps: driver.selectedLaps,
      lapColors: driver.lapColorMap,
      lapTraces: driver.lapTraces,
      name: driver.name,
      isPrimary: false,
    })),
  ].slice(0, 4);
  const firstVideoDriver = videoDrivers[0] || null;
  const secondVideoDriver = videoDrivers[1] || null;

  const handleResetToSessions = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('session');
    history.replaceState(null, '', url);
    window.location.reload();
  };

  const handleDeleteCurrentSession = (isOwner && !restricted) ? async () => {
    if (!window.confirm(`Delete "${session.meta.session_name}"? This cannot be undone.`)) return;
    try {
      await deleteSession(sessionId);
    } catch (e) {
      alert(`Delete failed: ${e.message}`);
      return;
    }
    const url = new URL(window.location.href);
    url.searchParams.delete('session');
    history.replaceState(null, '', url);
    window.location.reload();
  } : null;

  const analysisDownloadMeta = {
    hasOriginal: session.has_original,
    canReload: session.can_reload,
    hasMp4: session.has_video,
    hasMov: session.has_original_video,
    hasRendered: session.has_rendered_video,
    originalFilename: session.original_filename,
  };

  const channelSignalProps = (group) => ({
    key: group.name,
    name: group.name,
    unit: group.unit,
    units: group.entries.map(entry => entry.unit || ''),
    yLabel: getDisplayUnit(group.unit, unitPrefs),
    lapTraces: group.primaryEntry ? (channelTracesMap[group.name] || {}) : {},
    selectedLaps: group.primaryEntry ? selectedLaps : [],
    compareDrivers: group.cmpEntries.map(entry => ({
      slot: entry.source,
      lapTraces: cmpChannelTraces[entry.source]?.[group.name] || {},
      selectedLaps: cmpSelectedLaps[entry.source],
      palette: COMPARE_PALETTES[entry.source],
      customColors: cmpCustomColors[entry.source],
      unit: entry.unit || '',
      yLabel: getDisplayUnit(entry.unit, unitPrefs),
      valueTransform: makeValueTransform(entry.unit, unitPrefs),
    })),
    valueTransform: makeValueTransform(group.unit, unitPrefs),
  });

  const genericChannelTraceProps = (group) => {
    const base = channelSignalProps(group);
    const extras = channelPanelExtraGroups[group.name] || [];
    const selectedExtras = channelPanelExtras[group.name] || [];
    return {
      lapTraces: base.lapTraces,
      selectedLaps: base.selectedLaps,
      compareDrivers: base.compareDrivers,
      customColors1,
      dataKey: 'channel_value',
      title: group.title,
      signalName: group.name,
      unit: base.unit,
      units: base.units,
      yLabel: base.yLabel,
      valueTransform: base.valueTransform,
      additionalSignals: extras.map(channelSignalProps),
      availableChannels: availableChannels.filter(ch => ch.name !== group.name),
      selectedAdditionalChannels: selectedExtras,
      onAdditionalChannelToggle: ch => handlePanelExtraChannelToggle(group.name, ch),
      splitMode: channelPanelConfigs[group.name]?.split === true,
      onSplitModeChange: () => handlePanelSplitToggle(group.name),
      sharedScale: channelPanelConfigs[group.name]?.sharedScale === true,
      onSharedScaleChange: () => handlePanelSharedScaleToggle(group.name),
      colorMode: channelPanelConfigs[group.name]?.colorMode || 'auto',
      onColorModeChange: colorMode => handlePanelColorModeChange(group.name, colorMode),
      sectors,
      onSectorsChange: handleSectorsChange,
      anchoredTooltip: unitPrefs.anchoredTooltip,
      unitPrefs,
      trackName,
      sessionDate: session?.meta?.date,
      sessionVehicle: session?.meta?.vehicle,
      driverName1,
      exportCompareDrivers: compareDrivers,
    };
  };

  // The Power panel's extra-channel / split / export plumbing, keyed under
  // POWER_PANEL_KEY in channelPanelConfigs like a generic channel window.
  const powerTraceProps = {
    lapTraces: powerTracesMap,
    selectedLaps,
    compareDrivers,
    customColors1,
    anchoredTooltip: unitPrefs.anchoredTooltip,
    onEditVehicle: restricted ? null : handleOpenCarPhysics,
    additionalSignals: (channelPanelExtraGroups[POWER_PANEL_KEY] || []).map(channelSignalProps),
    availableChannels,
    selectedAdditionalChannels: channelPanelExtras[POWER_PANEL_KEY] || [],
    onAdditionalChannelToggle: ch => handlePanelExtraChannelToggle(POWER_PANEL_KEY, ch),
    splitMode: channelPanelConfigs[POWER_PANEL_KEY]?.split === true,
    onSplitModeChange: () => handlePanelSplitToggle(POWER_PANEL_KEY),
    sharedScale: channelPanelConfigs[POWER_PANEL_KEY]?.sharedScale === true,
    onSharedScaleChange: () => handlePanelSharedScaleToggle(POWER_PANEL_KEY),
    colorMode: channelPanelConfigs[POWER_PANEL_KEY]?.colorMode || 'auto',
    onColorModeChange: colorMode => handlePanelColorModeChange(POWER_PANEL_KEY, colorMode),
    unitPrefs,
    trackName,
    sessionDate: session?.meta?.date,
    sessionVehicle: session?.meta?.vehicle,
    driverName1,
    exportCompareDrivers: compareDrivers,
  };

  // Keep this list aligned with the desktop WindowFrame render conditions.
  // Arrange acts only on panels that exist now; unlike a preset, it never opens
  // or closes anything.
  const openPanelIds = [
    showSpeedTrace && 'speed',
    showGTrace && 'gTrace',
    showDeltaTrace && 'delta',
    showPowerTrace && 'power',
    ...channelPanelGroups.map(group => `ch_${group.name}`),
    valueChannels.length > 0 && 'readouts',
    ...formulas
      .filter(f => panelVisibility[`showFormula_${f.id}`])
      .map(f => `fml_${f.id}`),
    showMap && 'map',
    showVideo && videoDrivers.length > 0 && 'video',
    showMetrics && 'metrics',
    showHeadToHead && 'headtohead',
    showTrackDifference && 'trackDifference',
    aiDebug && showPromptPanel && 'prompt',
    showLap3D && 'lap3d',
    showFrictionCircle && 'frictionCircle',
    showScatterPlot && 'scatterPlot',
    showSplits && 'splits',
    showComments && sessionId && 'comments',
  ].filter(Boolean);

  const handleArrangePanels = () => {
    setMaximizedPanelId(null);
    arrangeLayout(openPanelIds, getWorkspaceLayoutBounds());
  };

  const analysisToolbarProps = {
    sessionId,
    downloadMeta: analysisDownloadMeta,
    onReset: handleResetToSessions,
    onDeleteSession: handleDeleteCurrentSession,
    showMap,
    onToggleMap: () => updatePanelVisibility({ showMap: !showMap }),
    showSpeedTrace,
    onToggleSpeedTrace: () => updatePanelVisibility({ showSpeedTrace: !showSpeedTrace }),
    showGTrace,
    onToggleGTrace: () => updatePanelVisibility({ showGTrace: !showGTrace }),
    showDeltaTrace,
    onToggleDeltaTrace: () => updatePanelVisibility({ showDeltaTrace: !showDeltaTrace }),
    availableChannels,
    selectedChannels,
    onChannelToggle: handleChannelToggle,
    valueChannels,
    onValueToggle: handleValueChannelToggle,
    onAddEcuData: restricted ? null : () => setShowEcuModal(true),
    onAddVideo: (isOwner && !restricted) ? () => setShowRenderModal(true) : null,
    addVideoLabel: (session.has_rendered_video || session.has_gopro_files)
      ? 'Re-render Video' : 'Add Video',
    onShare: (productCapabilities.session_sharing && isOwner && !restricted)
      ? () => setShowShareModal(true) : null,
    isShared: sessionShared,
    onCopyViewLink: (restricted || !productCapabilities.session_sharing) ? null : handleCopyViewLink,
    onServerSync: (!restricted && productCapabilities.sync_to_server)
      ? handleOpenServerSync : null,
    hasVideo: !!(session.has_video || session.has_rendered_video),
    showVideo,
    onToggleVideo: () => updatePanelVisibility({ showVideo: !showVideo }),
    showMetrics,
    onToggleMetrics: () => updatePanelVisibility({ showMetrics: !showMetrics }),
    showFrictionCircle,
    onToggleFrictionCircle: () => updatePanelVisibility({ showFrictionCircle: !showFrictionCircle }),
    showScatterPlot,
    onToggleScatterPlot: () => updatePanelVisibility({ showScatterPlot: !showScatterPlot }),
    showSplits,
    onToggleSplits: () => updatePanelVisibility({ showSplits: !showSplits }),
    showPowerTrace,
    onTogglePowerTrace: () => updatePanelVisibility({ showPowerTrace: !showPowerTrace }),
    showHeadToHead,
    onToggleHeadToHead: () => updatePanelVisibility({ showHeadToHead: !showHeadToHead }),
    showTrackDifference,
    onToggleTrackDifference: () => updatePanelVisibility({ showTrackDifference: !showTrackDifference }),
    showLap3D,
    canLap3D: canCompare3D,
    // Opening seeds the panel from the current selection; closing leaves the lap
    // set alone, so reopening resumes the same race rather than re-deriving one.
    onToggleLap3D: () => (showLap3D
      ? updatePanelVisibility({ showLap3D: false })
      : openLap3dPanel()),
    enableAi: aiDebug,
    showPromptPanel,
    onTogglePrompt: () => updatePanelVisibility({ showPromptPanel: !showPromptPanel }),
    showComments,
    onToggleComments: () => updatePanelVisibility({ showComments: !showComments }),
    recapEnabled: enableAi && enableAiDebrief && !!sessionId,
    recapStatus: sessionRecap.recap?.status || null,
    onOpenRecap: enableAiDebrief ? handleOpenSessionRecap : null,
    formulas,
    panelVisibility,
    onFormulaToggle: (f, isActive) => isActive ? handleFormulaHide(f) : handleFormulaShow(f),
    onFormulaNew: () => setFormulaEditorOpen('create'),
    onFormulaEdit: (f) => setFormulaEditorOpen(f),
    onResetLayout: resetWorkspaceLayout,
    onArrangePanels: handleArrangePanels,
    publicMode: restricted,
    guestMode,
  };

  const handleToggleAnalysisHeader = () => {
    const rect = mainAreaRef.current?.getBoundingClientRect();
    const willCollapse = !analysisHeaderCollapsed;
    if (rect) {
      // Predict the post-toggle bounds so panel reflow happens in the same
      // render as the header change; ResizeObserver confirms the real bounds.
      setPanelAreaBounds({
        top: rect.top + (willCollapse ? -ANALYSIS_CHROME_DELTA : ANALYSIS_CHROME_DELTA),
        height: rect.height + (willCollapse ? ANALYSIS_CHROME_DELTA : -ANALYSIS_CHROME_DELTA),
      });
    }
    setAnalysisHeaderCollapsed(willCollapse);
  };

  return (
    <div className="app">
      {restricted && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem',
          padding: '0.4rem 1rem', background: '#1e293b', color: '#cbd5e1',
          fontSize: '0.85rem', borderBottom: '1px solid #334155', flexWrap: 'wrap',
        }}>
          {guestMode ? (
            <>
              <span>🧪 Trying TrackDay Analysis — guest mode.</span>
              <a href="/try" style={{ color: '#cbd5e1', fontWeight: 600, textDecoration: 'none' }}>
                ＋ New / my uploads
              </a>
              <a href="/app" onClick={(e) => { e.preventDefault(); window.location.replace('/app'); }}
                 style={{ color: '#60a5fa', fontWeight: 600, textDecoration: 'none' }}>
                Sign up to save your data →
              </a>
            </>
          ) : currentUser ? (
            <>
              <span>Viewing a shared session (read-only).</span>
              <a href="/app" onClick={(e) => { e.preventDefault(); window.location.replace('/app'); }}
                 style={{ color: '#60a5fa', fontWeight: 600, textDecoration: 'none' }}>
                Go to your dashboard →
              </a>
            </>
          ) : (
            <>
              <span>Viewing a shared session (read-only).</span>
              <a href="/app" onClick={(e) => { e.preventDefault(); window.location.replace('/app'); }}
                 style={{ color: '#60a5fa', fontWeight: 600, textDecoration: 'none' }}>
                Sign up to analyze your own data →
              </a>
            </>
          )}
        </div>
      )}
      <AppHeader
        current="session"
        helpPage="sessions"
        showBrand={false}
        collapsible={!isMobile}
        collapsed={!isMobile && analysisHeaderCollapsed}
        onToggleCollapsed={handleToggleAnalysisHeader}
        collapsedMeta={(
          <SessionMeta
            inline
            meta={session.meta}
            unitPrefs={unitPrefs}
            onRenameLabel={handleRenameLabel}
            onShare={analysisToolbarProps.onShare}
            isShared={sessionShared}
            isZoomed={isZoomed}
            onResetZoom={handleResetZoom}
            publicMode={restricted}
          />
        )}
        contextTools={!isMobile ? (
          <AnalysisToolbar {...analysisToolbarProps} collapsed={analysisHeaderCollapsed} onOpenStudio={restricted ? null : openStudioFromAnalysis} />
        ) : null}
        onGoToSessions={handleGoToSessions}
        onGoToStudio={pageNavProps.onGoToStudio}
        onGoToLeaderboard={restricted ? null : pageNavProps.onGoToLeaderboard}
        onGoToProgress={restricted ? null : pageNavProps.onGoToProgress}
        onGoToChannelStats={restricted ? null : pageNavProps.onGoToChannelStats}
        onGoToSearch={restricted ? null : pageNavProps.onGoToSearch}
        onGoToSimLab={restricted || !currentUser?.is_admin ? null : handleGoToSimLab}
        onGoToIssues={restricted ? null : pageNavProps.onGoToIssues}
        openIssueCount={openIssueCount}
        onReportIssue={restricted ? null : pageNavProps.onReportIssue}
        onOpenUserSettings={restricted ? null : pageNavProps.onOpenUserSettings}
        onLogout={restricted ? null : pageNavProps.onLogout}
        driverProfile={driverProfile}
        currentUser={currentUser}
        onStartMasquerade={restricted ? null : pageNavProps.onStartMasquerade}
        onStopMasquerade={currentUser?.masquerade ? pageNavProps.onStopMasquerade : null}
      />
      {(isMobile || !analysisHeaderCollapsed) && (
        <SessionHeader
          isMobile={isMobile}
          meta={session.meta}
          unitPrefs={unitPrefs}
          onRenameLabel={handleRenameLabel}
          onShare={analysisToolbarProps.onShare}
          isShared={sessionShared}
          isZoomed={isZoomed}
          onResetZoom={handleResetZoom}
          publicMode={restricted}
          mobileActions={isMobile ? <AnalysisToolbar {...analysisToolbarProps} isMobile /> : null}
        />
      )}

      {enableAiDebrief && showSessionRecap && (
        <SessionRecapDialog
          recapController={sessionRecap}
          trackName={session?.meta?.canonical_track_name || trackName || session?.meta?.track_name}
          sessionLabel={session?.meta?.session_label || null}
          lapTraces={lapTraces}
          selectedLaps={selectedLaps}
          corners={corners}
          sectors={sectors}
          onClose={handleCloseSessionRecap}
          onOpenDeepDive={handleOpenRecapDeepDive}
        />
      )}

      {isMobile ? (
        // ── Mobile layout ─────────────────────────────────────────────────────
        // The speed trace is always visible (top in portrait, left in
        // landscape); the bottom tab bar swaps the companion panel beside it.
        // There is no page scroll, so the charts own the whole touch surface
        // and their gestures never fight scrolling.
        (() => {
          const speedTrace = (
            <SpeedTrace
              lapTraces={lapTraces} selectedLaps={selectedLaps}
              compareDrivers={compareDrivers}
              customColors1={customColors1}
              sectors={sectors} onSectorsChange={handleSectorsChange}
              anchoredTooltip={unitPrefs.anchoredTooltip}
              sessionId={sessionId} availableChannels={availableChannels}
              unitPrefs={unitPrefs}
              trackName={trackName} sessionDate={session?.meta?.date}
              sessionVehicle={session?.meta?.vehicle} driverName1={driverName1}
            />
          );

          // Unlike the desktop floating windows, every companion panel the
          // session's data supports gets a tab — panelVisibility flags are a
          // desktop windowing concept and don't gate mobile tabs.
          const panels = [];

          panels.push({
            id: 'map', title: 'Track Map',
            content: (
              <TrackMap
                laps={session.laps}
                deltaBaseOverride={deltaBaseOverride}
                lapTraces={lapTraces} selectedLaps={selectedLaps}
                compareDrivers={compareDrivers}
                customColors1={customColors1}
                startFinish={trackStartFinish}
                corners={corners}
                sectors={sectors}
                onCornerMove={handleCornerMove}
                onCornerLabelChange={handleCornerLabelChange}
                onCornerAdd={handleCornerAdd}
                onCornerRemove={handleCornerRemove}
                onRenumberTurns={handleRenumberTurns}
                canEditCorners={!!currentUser?.is_admin}
                turnCount={turnCount} onTurnCountChange={handleTurnCountChange}
                sensitivity={sensitivity} onSensitivityChange={handleSensitivityChange}
                showTurnMarkers={showTurnMarkers}
                onToggleShowTurnMarkers={() => setShowTurnMarkers(v => {
                  localStorage.setItem('trackmap.showTurnMarkers', String(!v));
                  return !v;
                })}
                showPerLapApex={showPerLapApex}
                onTogglePerLapApex={() => setShowPerLapApex(v => !v)}
                metrics={metrics}
              />
            ),
          });

          panels.push({
            id: 'gTrace', title: 'G-Force Trace',
            content: (
              <GTrace
                lapTraces={lapTraces} selectedLaps={selectedLaps}
                compareDrivers={compareDrivers}
                customColors1={customColors1}
                anchoredTooltip={unitPrefs.anchoredTooltip}
                unitPrefs={unitPrefs}
              />
            ),
          });

          panels.push({
            id: 'delta', title: 'Time Delta',
            content: (
              <TimeDeltaTrace
                lapTraces={lapTraces} selectedLaps={selectedLaps}
                compareDrivers={compareDrivers}
                driverName1={driverName1}
                customColors1={customColors1}
                anchoredTooltip={unitPrefs.anchoredTooltip}
                baseOverride={deltaBaseOverride}
                onBaseOverrideChange={setDeltaBaseOverride}
                laps={session.laps}
                unitPrefs={unitPrefs}
                trackName={trackName} sessionDate={session?.meta?.date}
                sessionVehicle={session?.meta?.vehicle}
              />
            ),
          });

          panels.push({
            id: 'power', title: 'Power',
            content: <PowerTrace {...powerTraceProps} />,
          });

          channelPanelGroups.forEach((group) => panels.push({
            id: `ch_${group.name}`, title: group.title,
            content: (
              <ChannelTrace {...genericChannelTraceProps(group)} />
            ),
          }));

          if (valueChannels.length > 0) panels.push({
            id: 'readouts', title: 'Readouts',
            content: (
              <ReadoutsPanel
                channels={valueChannels}
                channelTracesMap={channelTracesMap}
                selectedLaps={selectedLaps}
                lapTraces={lapTraces}
                customColors1={customColors1}
                unitPrefs={unitPrefs}
                onRemoveChannel={handleValueChannelToggle}
                onReorder={updateValueChannelNames}
              />
            ),
          });

          formulas.forEach((f) => {
            if (!panelVisibility[`showFormula_${f.id}`]) return;
            panels.push({
              id: `fml_${f.id}`, title: f.name,
              content: (
                <ChannelTrace
                  lapTraces={formulaTracesMap[f.id] || {}}
                  selectedLaps={selectedLaps}
                  compareDrivers={formulaCompareDrivers(f.id)}
                  customColors1={customColors1}
                  dataKey="channel_value" title={f.name}
                  yLabel={f.unit || ''}
                  anchoredTooltip={unitPrefs.anchoredTooltip}
                  unitPrefs={unitPrefs}
                />
              ),
            });
          });

          panels.push({
            id: 'frictionCircle', title: 'Friction Circle',
            content: (
              <FrictionCircle
                lapTraces={lapTraces} selectedLaps={selectedLaps}
                compareDrivers={compareDrivers}
                customColors1={customColors1}
                trackName={trackName} sessionDate={session?.meta?.date}
                sessionVehicle={session?.meta?.vehicle}
              />
            ),
          });

          panels.push({
            id: 'scatterPlot', title: 'Scatter Plot',
            content: (
              <ScatterPlot
                availableChannels={availableChannels}
                channelTracesMap={channelTracesMap}
                selectedLaps={selectedLaps}
                lapTraces={lapTraces}
                compareDrivers={scatterCompareDrivers}
                onFetchChannel={fetchChannelTraceForLap}
                unitPrefs={unitPrefs}
                customColors1={customColors1}
                trackName={trackName} sessionDate={session?.meta?.date}
                sessionVehicle={session?.meta?.vehicle}
              />
            ),
          });

          panels.push({
            id: 'metrics', title: 'Corner Metrics',
            content: (
              <MetricsPanel
                corners={corners}
                lapTraces={lapTraces} selectedLaps={selectedLaps}
                metrics={metrics} loading={metricsLoading} error={metricsError}
                sectors={sectors}
                sectorMetrics={sectorMetrics}
                driverName1={driverName1}
                sessionId={session?.session_id}
                enableAi={enableAi && enableAiDebrief}
                allowPromptDebug={aiDebug}
                recapController={enableAiDebrief ? sessionRecap : null}
                focusCornerRequest={metricsFocusRequest}
                onFocusCornerHandled={handleMetricsFocusHandled}
                onLoadSession={restricted ? null : handleAddSession}
                loadedSessionIds={excludeSessionIds}
                canAddMore={canAddCompare}
                lapNumberByIndex={Object.fromEntries((session?.laps || []).map(l => [l.index, l.lap_number]))}
                compareDrivers={compareDrivers}
              />
            ),
          });

          panels.push({
            id: 'trackDifference', title: 'Track Difference',
            content: (
              <TrackDifference
                session={session}
                lapTraces={lapTraces}
                selectedLaps={selectedLaps}
                driverName1={driverName1}
                compareDrivers={compareDrivers}
                customColors1={customColors1}
                sectors={sectors}
                unitPrefs={unitPrefs}
                preferredPair={trackDifferencePair}
                primarySfOffsetM={primarySfOffsetM}
                onFetchTrace={fetchTrace}
                loadingTraces={loadingTraces}
                elevationProfile={trackElevationProfile}
              />
            ),
          });

          panels.push({
            id: 'headtohead', title: 'Head-to-Head',
            content: (
              <HeadToHead
                corners={corners}
                sectors={sectors}
                elevationProfile={trackElevationProfile}
                lapTraces={lapTraces} selectedLaps={selectedLaps}
                session={session} driverName1={driverName1}
                metrics={metrics} metricsLoading={metricsLoading}
                onCalculate={handleCalculateMetrics}
                compareDrivers={compareDrivers}
                unitPrefs={unitPrefs}
                trackName={trackName} sessionDate={session?.meta?.date}
                primarySfOffsetM={primarySfOffsetM}
                onCoach={(aiDebug && !restricted) ? (h2h) => handleBuildPrompt('headtohead', h2h) : null}
                onAiSummary={(enableAi && !restricted) ? (h2h) => handleBuildPrompt('headtohead', h2h, { silent: true }) : null}
                on3DCompare={setLap3dLaps}
                onOpenTrackDifference={openTrackDifferencePanel}
                coachModel={coachModel}
              />
            ),
          });

          panels.push({
            id: 'splits', title: 'Split Times',
            content: (
              <SectorTimes
                datasets={splitTimeDatasets}
                sectors={sectors}
                trackName={session?.meta?.canonical_track_name || trackName || session?.meta?.track_name}
              />
            ),
          });

          if (aiDebug) panels.push({
            id: 'prompt', title: 'AI Briefing',
            content: (
              <PromptPanel
                briefing={briefing} loading={promptLoading} error={promptError}
                mode={briefing?.mode}
                capabilities={capabilities}
                onBuild={handleBuildPrompt} canBuild={selectedLaps.length > 0 && corners.length > 0}
                forceOllama={restricted}
                onClose={() => updatePanelVisibility({ showPromptPanel: false })}
              />
            ),
          });

          if (sessionId) panels.push({
            id: 'comments', title: 'Comments',
            content: (
              <CommentsPanel
                sessionId={sessionId} canComment={canComment}
                corners={corners} turnsEnabled={showTurnMarkers}
                publicMode={restricted}
              />
            ),
          });

          if (firstVideoDriver) {
            panels.push({
              id: 'video', title: 'Video',
              content: (
                <VideoPanel
                  ref={videoRef} sessionId={firstVideoDriver.sessionId}
                  videoSrc={firstVideoDriver.videoSrc}
                  syncOffset={0}
                  defaultTrim={firstVideoDriver.defaultTrim}
                  downloadUrl={firstVideoDriver.session.has_rendered_video
                    ? `/api/rendered_video/${firstVideoDriver.sessionId}?download=1`
                    : undefined}
                  onClose={() => updatePanelVisibility({ showVideo: false })}
                  onVideoDeleted={firstVideoDriver.isPrimary ? handleVideoDeleted : undefined}
                  laps={firstVideoDriver.laps}
                  selectedLaps={firstVideoDriver.selectedLaps}
                  lapColors={firstVideoDriver.lapColors}
                  lapTraces={firstVideoDriver.lapTraces}
                  videoSrc2={secondVideoDriver?.videoSrc ?? null}
                  sessionId2={secondVideoDriver?.sessionId ?? null}
                  defaultTrim2={secondVideoDriver?.defaultTrim ?? 0}
                  laps2={secondVideoDriver?.laps ?? []}
                  selectedLaps2={secondVideoDriver?.selectedLaps ?? []}
                  lapColors2={secondVideoDriver?.lapColors ?? {}}
                  lapTraces2={secondVideoDriver?.lapTraces ?? {}}
                  driverName1={firstVideoDriver.name}
                  driverName2={secondVideoDriver?.name ?? 'Driver 2'}
                  videoDrivers={videoDrivers}
                  lap3dLaps={lap3dWanted}
                />
              ),
            });
          }

          const active = panels.find(p => p.id === activeMobilePanel)
            ?? panels.find(p => p.id === 'map')
            ?? panels[0];
          const selectPanel = (id) => {
            setActiveMobilePanel(id);
            localStorage.setItem('mobile.activePanel', id);
          };

          return (
            <>
              <div className="mobileMain">
                <div className="mobileSpeedSlot">
                  {speedTrace}
                </div>
                <div className="mobilePanelSlot">
                  <MobilePanel key={active.id}>
                    {active.content}
                  </MobilePanel>
                </div>
              </div>

              <MobileTabBar
                tabs={panels}
                activeId={active?.id}
                onSelect={selectPanel}
                onOpenLaps={() => setShowLapDrawer(v => !v)}
                lapsOpen={showLapDrawer}
              />

              {showLapDrawer && (
                <div className="lapDrawer" onClick={() => setShowLapDrawer(false)}>
                  <div className="lapDrawerContent" onClick={e => e.stopPropagation()}>
                    <div className="lapDrawerHandle" onClick={() => setShowLapDrawer(false)}>
                      <div className="lapDrawerBar" />
                    </div>
                    <LapSelector
              onOpen3D={open3DFromSelection} can3D={can3D}
              onCompare3D={open3DCompare} canCompare3D={canCompare3D}
                      isMobile
                      laps={session.laps}
                      selectedLaps={selectedLaps}
                      onToggle={handleToggleLap}
                      loadingTraces={loadingTraces}
                      driverName1={driverName1}
                      customColors1={customColors1}
                      onColorChange1={handleColorChange1}
                      carHealth1={session.car_health || null}
                      primarySfOffsetM={primarySfOffsetM}
                      canRealignPrimary={canRealignPrimary}
                      onRealignPrimary={() => handleRealignSession('primary')}
                      primarySfRealigned={primarySfRealigned}
                      canUnalignPrimary={canUnalignPrimary}
                      onUnalignPrimary={() => handleRealignSession('primary', true)}
                      realigningPrimary={realigningSlot === 'primary'}
                      compareDrivers={compareDrivers}
                      onAddSession={handleAddSession}
                      canAddMore={canAddCompare}
                      loadingAdd={addingCompare}
                      excludeSessionIds={excludeSessionIds}
                      trackName={session?.meta?.canonical_track_name || trackName || session?.meta?.track_name}
                      currentUserId={currentUser?.id}
                      isAdmin={!!currentUser?.is_admin}
                      adminModeActive={adminModeActive}
                      unitPrefs={unitPrefs}
                      lapSelectionMode={lapSelectionMode}
                      onLapSelectionModeChange={handleLapSelectionModeChange}
                      sectors={sectors}
                      sectorTimesData={sectorTimesData}
                    />
                  </div>
                </div>
              )}
            </>
          );
        })()
      ) : (
        // ── Desktop layout ────────────────────────────────────────────────────
        <>
          <div className="workspace">
            <LapSelector
              onOpen3D={open3DFromSelection} can3D={can3D}
              onCompare3D={open3DCompare} canCompare3D={canCompare3D}
              collapsed={lapsCollapsed}
              onToggleCollapsed={() => setLapsCollapsed(v => !v)}
              laps={session.laps}
              selectedLaps={selectedLaps}
              onToggle={handleToggleLap}
              loadingTraces={loadingTraces}
              driverName1={driverName1}
              customColors1={customColors1}
              onColorChange1={handleColorChange1}
              carHealth1={session.car_health || null}
              primarySfOffsetM={primarySfOffsetM}
              canRealignPrimary={canRealignPrimary}
              onRealignPrimary={() => handleRealignSession('primary')}
              primarySfRealigned={primarySfRealigned}
              canUnalignPrimary={canUnalignPrimary}
              onUnalignPrimary={() => handleRealignSession('primary', true)}
              realigningPrimary={realigningSlot === 'primary'}
              compareDrivers={compareDrivers}
              onAddSession={handleAddSession}
              canAddMore={canAddCompare}
              loadingAdd={addingCompare}
              excludeSessionIds={excludeSessionIds}
              trackName={session?.meta?.canonical_track_name || trackName || session?.meta?.track_name}
              currentUserId={currentUser?.id}
              isAdmin={!!currentUser?.is_admin}
              adminModeActive={adminModeActive}
              unitPrefs={unitPrefs}
              lapSelectionMode={lapSelectionMode}
              onLapSelectionModeChange={handleLapSelectionModeChange}
              sectors={sectors}
              sectorTimesData={sectorTimesData}
            />

            <div className="mainArea">
              <TabBar
                tabs={tabs}
                activeTabId={activeTabId}
                onSwitch={switchTab}
                onCreate={createTab}
                onRename={renameTab}
                onDelete={deleteTab}
              />
              <div id="analysis-panel-area" className="panelArea" ref={mainAreaRef} />
            </div>
          </div>

          {/* Floating window layer — sits above the fixed layout */}
          <div className="floatingLayer">
            {showSpeedTrace && shouldShow('speed') && (
              <WindowFrame
                id="speed" title="Speed Trace"
                layout={dispLayout(winLayout.speed)} onUpdate={handleWindowUpdate} allLayouts={dispAllLayouts}
                onClose={() => updatePanelVisibility({ showSpeedTrace: false })}
                minWidth={ANALYSIS_PLOT_MIN_WIDTH} minHeight={120} {...maxProps('speed')}
              >
                <SpeedTrace
                  lapTraces={lapTraces} selectedLaps={selectedLaps}
                  compareDrivers={compareDrivers}
                  customColors1={customColors1}
                  sectors={sectors} onSectorsChange={handleSectorsChange}
                  anchoredTooltip={unitPrefs.anchoredTooltip}
                  sessionId={sessionId} availableChannels={availableChannels}
                  unitPrefs={unitPrefs}
                  trackName={trackName} sessionDate={session?.meta?.date}
                  sessionVehicle={session?.meta?.vehicle} driverName1={driverName1}
                />
              </WindowFrame>
            )}

            {showGTrace && shouldShow('gTrace') && (
              <WindowFrame
                id="gTrace" title="G-Force Trace"
                layout={dispLayout(winLayout.gTrace)} onUpdate={handleWindowUpdate} allLayouts={dispAllLayouts}
                onClose={() => updatePanelVisibility({ showGTrace: false })}
                minWidth={ANALYSIS_PLOT_MIN_WIDTH} minHeight={120} {...maxProps('gTrace')}
              >
                <GTrace
                  lapTraces={lapTraces} selectedLaps={selectedLaps}
                  compareDrivers={compareDrivers}
                  customColors1={customColors1}
                  anchoredTooltip={unitPrefs.anchoredTooltip}
                  unitPrefs={unitPrefs}
                />
              </WindowFrame>
            )}

            {showDeltaTrace && shouldShow('delta') && (
              <WindowFrame
                id="delta" title="Time Delta"
                layout={dispLayout(winLayout.delta)} onUpdate={handleWindowUpdate} allLayouts={dispAllLayouts}
                onClose={() => updatePanelVisibility({ showDeltaTrace: false })}
                minWidth={ANALYSIS_PLOT_MIN_WIDTH} minHeight={120} {...maxProps('delta')}
              >
                <TimeDeltaTrace
                  lapTraces={lapTraces} selectedLaps={selectedLaps}
                  compareDrivers={compareDrivers}
                  driverName1={driverName1}
                  customColors1={customColors1}
                  anchoredTooltip={unitPrefs.anchoredTooltip}
                  baseOverride={deltaBaseOverride}
                  onBaseOverrideChange={setDeltaBaseOverride}
                  laps={session.laps}
                  unitPrefs={unitPrefs}
                  trackName={trackName} sessionDate={session?.meta?.date}
                  sessionVehicle={session?.meta?.vehicle}
                />
              </WindowFrame>
            )}

            {showPowerTrace && shouldShow('power') && (
              <WindowFrame
                id="power" title="Power"
                layout={dispLayout(winLayout.power ?? { x: 200, y: 610, width: 860, height: 220 })} onUpdate={handleWindowUpdate} allLayouts={dispAllLayouts}
                onClose={() => updatePanelVisibility({ showPowerTrace: false })}
                minWidth={ANALYSIS_PLOT_MIN_WIDTH} minHeight={120} {...maxProps('power')}
              >
                <PowerTrace {...powerTraceProps} />
              </WindowFrame>
            )}

            {channelPanelGroups.map((group, i) => {
              const pid = `ch_${group.name}`;
              if (!shouldShow(pid)) return null;
              return (
                <WindowFrame
                  key={group.name} id={pid} title={group.title}
                  layout={dispLayout(getChannelLayout(group.name, i))} onUpdate={handleWindowUpdate} allLayouts={dispAllLayouts}
                  onClose={() => handleCloseChannelGroup(group)}
                  minWidth={ANALYSIS_PLOT_MIN_WIDTH} minHeight={120} {...maxProps(pid)}
                >
                  <ChannelTrace {...genericChannelTraceProps(group)} />
                </WindowFrame>
              );
            })}

            {valueChannels.length > 0 && shouldShow('readouts') && (
              <WindowFrame
                id="readouts" title="Readouts"
                layout={dispLayout(winLayout.readouts ?? { x: 1080, y: 610, width: 320, height: 280 })} onUpdate={handleWindowUpdate} allLayouts={dispAllLayouts}
                onClose={handleCloseReadouts}
                minHeight={120} {...maxProps('readouts')}
              >
                <ReadoutsPanel
                  channels={valueChannels}
                  channelTracesMap={channelTracesMap}
                  selectedLaps={selectedLaps}
                  lapTraces={lapTraces}
                  customColors1={customColors1}
                  unitPrefs={unitPrefs}
                  onRemoveChannel={handleValueChannelToggle}
                  onReorder={updateValueChannelNames}
                />
              </WindowFrame>
            )}

            {formulas.map((f, i) => {
              const pid = `fml_${f.id}`;
              if (!panelVisibility[`showFormula_${f.id}`] || !shouldShow(pid)) return null;
              return (
                <WindowFrame
                  key={f.id} id={pid}
                  title={
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                          title={[f.description, f.expression && `Formula: ${f.expression}`].filter(Boolean).join('\n\n') || undefined}>
                      {f.color && <span style={{ width: 8, height: 8, borderRadius: '50%', background: f.color, flexShrink: 0 }} />}
                      {f.name}
                      {!isBuiltinFormula(f) && (<>
                        <button
                          onClick={e => { e.stopPropagation(); setFormulaEditorOpen(f); }}
                          style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', padding: '0 2px', fontSize: '0.75rem' }}
                          title="Edit formula"
                        >✎</button>
                        <button
                          onClick={e => { e.stopPropagation(); handleFormulaDelete(f); }}
                          style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', padding: '0 2px', fontSize: '0.75rem' }}
                          title="Delete formula"
                        >🗑</button>
                      </>)}
                    </span>
                  }
                  layout={dispLayout(winLayout[pid] ?? formulaDefault(i))} onUpdate={handleWindowUpdate} allLayouts={dispAllLayouts}
                  onClose={() => handleFormulaHide(f)}
                  minWidth={ANALYSIS_PLOT_MIN_WIDTH} minHeight={120} {...maxProps(pid)}
                >
                  {formulaTracesMap[f.id]?._error ? (
                    <div style={{ padding: '16px', color: '#dd5d5d', fontSize: '0.82rem', fontFamily: 'monospace' }}>
                      ⚠ {formulaTracesMap[f.id]._error}
                    </div>
                  ) : (
                    <ChannelTrace
                      lapTraces={formulaTracesMap[f.id] || {}}
                      selectedLaps={selectedLaps}
                      compareDrivers={formulaCompareDrivers(f.id)}
                      customColors1={customColors1}
                      dataKey="channel_value" title={f.name}
                      yLabel={f.unit || ''}
                      anchoredTooltip={unitPrefs.anchoredTooltip}
                      unitPrefs={unitPrefs}
                    />
                  )}
                </WindowFrame>
              );
            })}

            {showMap && shouldShow('map') && (
              <WindowFrame
                id="map" title="Track Map"
                layout={dispLayout(winLayout.map)} onUpdate={handleWindowUpdate} allLayouts={dispAllLayouts}
                onClose={() => updatePanelVisibility({ showMap: false })}
                {...maxProps('map')}
              >
                <TrackMap
                  laps={session.laps}
                  deltaBaseOverride={deltaBaseOverride}
                  lapTraces={lapTraces} selectedLaps={selectedLaps}
                  compareDrivers={compareDrivers}
                  customColors1={customColors1}
                  startFinish={trackStartFinish}
                  corners={corners}
                  sectors={sectors}
                  elevationProfile={trackElevationProfile}
                  onCornerMove={handleCornerMove}
                  onCornerLabelChange={handleCornerLabelChange}
                  onCornerAdd={handleCornerAdd}
                  onCornerRemove={handleCornerRemove}
                  onRenumberTurns={handleRenumberTurns}
                  canEditCorners={!!currentUser?.is_admin}
                  turnCount={turnCount} onTurnCountChange={handleTurnCountChange}
                  sensitivity={sensitivity} onSensitivityChange={handleSensitivityChange}
                  showTurnMarkers={showTurnMarkers}
                  onToggleShowTurnMarkers={() => setShowTurnMarkers(v => {
                    localStorage.setItem('trackmap.showTurnMarkers', String(!v));
                    return !v;
                  })}
                  showPerLapApex={showPerLapApex}
                  onTogglePerLapApex={() => setShowPerLapApex(v => !v)}
                  metrics={metrics}
                />
              </WindowFrame>
            )}

            {showVideo && firstVideoDriver && shouldShow('video') && (
              <WindowFrame
                id="video" title="Video"
                layout={dispLayout(winLayout.video)} onUpdate={handleWindowUpdate} allLayouts={dispAllLayouts}
                onClose={() => updatePanelVisibility({ showVideo: false })}
                {...maxProps('video')}
              >
                <VideoPanel
                  ref={videoRef} sessionId={firstVideoDriver.sessionId}
                  videoSrc={firstVideoDriver.videoSrc}
                  syncOffset={0}
                  defaultTrim={firstVideoDriver.defaultTrim}
                  downloadUrl={firstVideoDriver.session.has_rendered_video
                    ? `/api/rendered_video/${firstVideoDriver.sessionId}?download=1`
                    : undefined}
                  onClose={() => updatePanelVisibility({ showVideo: false })}
                  onVideoDeleted={firstVideoDriver.isPrimary ? handleVideoDeleted : undefined}
                  laps={firstVideoDriver.laps}
                  selectedLaps={firstVideoDriver.selectedLaps}
                  lapColors={firstVideoDriver.lapColors}
                  lapTraces={firstVideoDriver.lapTraces}
                  videoSrc2={secondVideoDriver?.videoSrc ?? null}
                  sessionId2={secondVideoDriver?.sessionId ?? null}
                  defaultTrim2={secondVideoDriver?.defaultTrim ?? 0}
                  laps2={secondVideoDriver?.laps ?? []}
                  selectedLaps2={secondVideoDriver?.selectedLaps ?? []}
                  lapColors2={secondVideoDriver?.lapColors ?? {}}
                  lapTraces2={secondVideoDriver?.lapTraces ?? {}}
                  driverName1={firstVideoDriver.name}
                  driverName2={secondVideoDriver?.name ?? 'Driver 2'}
                  videoDrivers={videoDrivers}
                  lap3dLaps={lap3dWanted}
                />
              </WindowFrame>
            )}

            {showMetrics && shouldShow('metrics') && (
              <WindowFrame
                id="metrics" title="Corner Metrics"
                layout={dispLayout(winLayout.metrics)} onUpdate={handleWindowUpdate} allLayouts={dispAllLayouts}
                onClose={() => updatePanelVisibility({ showMetrics: false })}
                {...maxProps('metrics')}
              >
                <MetricsPanel
                  corners={corners}
                  lapTraces={lapTraces} selectedLaps={selectedLaps}
                  metrics={metrics} loading={metricsLoading} error={metricsError}
                  sectors={sectors}
                  sectorMetrics={sectorMetrics}
                  driverName1={driverName1}
                  sessionId={session?.session_id}
                  enableAi={enableAi && enableAiDebrief}
                  allowPromptDebug={aiDebug}
                  recapController={enableAiDebrief ? sessionRecap : null}
                  focusCornerRequest={metricsFocusRequest}
                  onFocusCornerHandled={handleMetricsFocusHandled}
                  onLoadSession={restricted ? null : handleAddSession}
                  loadedSessionIds={excludeSessionIds}
                  canAddMore={canAddCompare}
                  lapNumberByIndex={Object.fromEntries((session?.laps || []).map(l => [l.index, l.lap_number]))}
                  compareDrivers={compareDrivers}
                />
              </WindowFrame>
            )}

            {showHeadToHead && shouldShow('headtohead') && (
              <WindowFrame
                id="headtohead" title="Head-to-Head"
                layout={dispLayout(winLayout.headtohead)} onUpdate={handleWindowUpdate} allLayouts={dispAllLayouts}
                onClose={() => updatePanelVisibility({ showHeadToHead: false })}
                minWidth={360} minHeight={240} {...maxProps('headtohead')}
              >
                <HeadToHead
                  corners={corners}
                  sectors={sectors}
                  elevationProfile={trackElevationProfile}
                  lapTraces={lapTraces} selectedLaps={selectedLaps}
                  session={session} driverName1={driverName1}
                  metrics={metrics} metricsLoading={metricsLoading}
                  onCalculate={handleCalculateMetrics}
                  compareDrivers={compareDrivers}
                  unitPrefs={unitPrefs}
                  trackName={trackName} sessionDate={session?.meta?.date}
                  primarySfOffsetM={primarySfOffsetM}
                  onCoach={(aiDebug && !restricted) ? (h2h) => handleBuildPrompt('headtohead', h2h) : null}
                  onAiSummary={(enableAi && !restricted) ? (h2h) => handleBuildPrompt('headtohead', h2h, { silent: true }) : null}
                  on3DCompare={setLap3dLaps}
                  onOpenTrackDifference={openTrackDifferencePanel}
                  coachModel={coachModel}
                />
              </WindowFrame>
            )}

            {showTrackDifference && shouldShow('trackDifference') && (
              <WindowFrame
                id="trackDifference" title="Track Difference"
                layout={dispLayout(winLayout.trackDifference)} onUpdate={handleWindowUpdate} allLayouts={dispAllLayouts}
                onClose={() => updatePanelVisibility({ showTrackDifference: false })}
                minWidth={ANALYSIS_PLOT_MIN_WIDTH} minHeight={320} {...maxProps('trackDifference')}
              >
                <TrackDifference
                  session={session}
                  lapTraces={lapTraces}
                  selectedLaps={selectedLaps}
                  driverName1={driverName1}
                  compareDrivers={compareDrivers}
                  customColors1={customColors1}
                  sectors={sectors}
                  unitPrefs={unitPrefs}
                  preferredPair={trackDifferencePair}
                  primarySfOffsetM={primarySfOffsetM}
                  onFetchTrace={fetchTrace}
                  loadingTraces={loadingTraces}
                  elevationProfile={trackElevationProfile}
                />
              </WindowFrame>
            )}

            {aiDebug && showPromptPanel && shouldShow('prompt') && (
              <WindowFrame
                id="prompt" title="AI Briefing"
                layout={dispLayout(winLayout.prompt)} onUpdate={handleWindowUpdate} allLayouts={dispAllLayouts}
                onClose={() => updatePanelVisibility({ showPromptPanel: false })}
                {...maxProps('prompt')}
              >
                <PromptPanel
                  briefing={briefing} loading={promptLoading} error={promptError}
                  mode={briefing?.mode}
                  capabilities={capabilities}
                  onBuild={handleBuildPrompt} canBuild={selectedLaps.length > 0 && corners.length > 0}
                  forceOllama={restricted}
                  onClose={() => updatePanelVisibility({ showPromptPanel: false })}
                />
              </WindowFrame>
            )}

            {showLap3D && shouldShow('lap3d') && (
              <WindowFrame
                id="lap3d" title="3D View"
                layout={dispLayout(winLayout.lap3d)} onUpdate={handleWindowUpdate} allLayouts={dispAllLayouts}
                onClose={() => updatePanelVisibility({ showLap3D: false })}
                minWidth={360} minHeight={280} {...maxProps('lap3d')}
              >
                {/* Suspense here as well as around the modal: they are separate
                    mount points and either can be the first to pull the chunk. */}
                <Suspense fallback={null}>
                  <Lap3DPanel
                    laps={lap3dPanelLaps}
                    stale={lap3dPanelStale}
                    onSync={() => setLap3dPanelLaps(lap3dFromSelection())}
                    getLapPoints={lap3dLapTrace}
                    corridorSessionId={lap3dCorridorSessionId}
                  />
                </Suspense>
              </WindowFrame>
            )}

            {showFrictionCircle && shouldShow('frictionCircle') && (
              <WindowFrame
                id="frictionCircle" title="Friction Circle"
                layout={dispLayout(winLayout.frictionCircle)} onUpdate={handleWindowUpdate} allLayouts={dispAllLayouts}
                onClose={() => updatePanelVisibility({ showFrictionCircle: false })}
                minWidth={ANALYSIS_PLOT_MIN_WIDTH} minHeight={240} {...maxProps('frictionCircle')}
              >
                <FrictionCircle
                  lapTraces={lapTraces} selectedLaps={selectedLaps}
                  compareDrivers={compareDrivers}
                  customColors1={customColors1}
                  trackName={trackName} sessionDate={session?.meta?.date}
                  sessionVehicle={session?.meta?.vehicle}
                />
              </WindowFrame>
            )}

            {showScatterPlot && shouldShow('scatterPlot') && (
              <WindowFrame
                id="scatterPlot" title="Scatter Plot"
                layout={dispLayout(winLayout.scatterPlot)} onUpdate={handleWindowUpdate} allLayouts={dispAllLayouts}
                onClose={() => updatePanelVisibility({ showScatterPlot: false })}
                minWidth={ANALYSIS_PLOT_MIN_WIDTH} minHeight={240} {...maxProps('scatterPlot')}
              >
                <ScatterPlot
                  availableChannels={availableChannels}
                  channelTracesMap={channelTracesMap}
                  selectedLaps={selectedLaps}
                  lapTraces={lapTraces}
                  compareDrivers={scatterCompareDrivers}
                  onFetchChannel={fetchChannelTraceForLap}
                  unitPrefs={unitPrefs}
                  customColors1={customColors1}
                  trackName={trackName} sessionDate={session?.meta?.date}
                  sessionVehicle={session?.meta?.vehicle}
                />
              </WindowFrame>
            )}

            {showSplits && shouldShow('splits') && (
              <WindowFrame
                id="splits" title="Split Times"
                layout={dispLayout(winLayout.splits)} onUpdate={handleWindowUpdate} allLayouts={dispAllLayouts}
                onClose={() => updatePanelVisibility({ showSplits: false })}
                minWidth={320} minHeight={160} {...maxProps('splits')}
              >
                <SectorTimes
                  datasets={splitTimeDatasets}
                  sectors={sectors}
                  trackName={session?.meta?.canonical_track_name || trackName || session?.meta?.track_name}
                />
              </WindowFrame>
            )}

            {showComments && sessionId && shouldShow('comments') && (
              <WindowFrame
                id="comments" title="Comments"
                layout={dispLayout(winLayout.comments)} onUpdate={handleWindowUpdate} allLayouts={dispAllLayouts}
                onClose={() => updatePanelVisibility({ showComments: false })}
                minWidth={260} minHeight={200} {...maxProps('comments')}
              >
                <CommentsPanel
                  sessionId={sessionId} canComment={canComment}
                  corners={corners} turnsEnabled={showTurnMarkers}
                  publicMode={restricted}
                />
              </WindowFrame>
            )}
          </div>
        </>
      )}

      {settingsModal}

      {productCapabilities.session_sharing && showShareModal && session && (
        <ShareModal
          sessionId={sessionId}
          sessionName={session.meta?.session_name}
          onClose={() => setShowShareModal(false)}
          onShared={() => setSessionShared(true)}
          onUnshared={() => listShares(sessionId)
            .then(list => setSessionShared(list.length > 0))
            .catch(() => {})}
        />
      )}

      {lap3dModal}

      {showRenderModal && session && (
        <VideoRenderModal
          sessionId={sessionId}
          isAdmin={!!currentUser?.is_admin}
          availableChannels={(session.channels || []).map((name, i) => ({ name, unit: (session.units || [])[i] || '' }))}
          unitPrefs={unitPrefs}
          hasExistingFiles={!!(session.has_rendered_video || session.has_gopro_files)}
          sessionDuration={session.meta?.duration ?? 0}
          lastRenderOverlays={session.last_render_overlays ?? null}
          initialStep={1}
          onClose={() => setShowRenderModal(false)}
          onRenderComplete={() => {
            setShowRenderModal(false);
            // Pull the session back down so the rendered video (and its sync
            // offset) reach the video panel without a manual reload.
            loadSession(sessionId).catch(() => {});
          }}
          onRenderCanceled={() => setShowRenderModal(false)}
        />
      )}

      {showEcuModal && session && (
        <EcuMergeModal
          sessionId={sessionId}
          sessionName={session.meta?.session_name}
          onClose={() => setShowEcuModal(false)}
          onMerged={(resp) => {
            // Splice the newly-added channels into session state so they appear
            // in the channel picker (and serve via the channel_trace endpoint).
            setSession(prev => prev
              ? { ...prev, channels: resp.channels, units: resp.units }
              : prev);
          }}
        />
      )}

      {formulaEditorOpen && (
        <FormulaEditor
          formula={formulaEditorOpen === 'create' ? null : formulaEditorOpen}
          sessionId={sessionId}
          selectedLaps={selectedLaps}
          onSave={handleFormulaSave}
          onClose={() => setFormulaEditorOpen(null)}
        />
      )}

      {needsTrackName && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: '#1e2130', border: '1px solid #3a3f55',
            borderRadius: 10, padding: '28px 32px', minWidth: 360,
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
            display: 'flex', flexDirection: 'column', gap: 16,
          }}>
            <h3 style={{ margin: 0, color: '#e0e4f0', fontSize: 16 }}>
              Name this track / session
            </h3>
            <p style={{ margin: 0, color: '#9aa0b8', fontSize: 13 }}>
              This circuit wasn't found in the track database. Enter a name
              so future sessions here can be matched automatically.
            </p>
            <input
              ref={trackNameInputRef}
              autoFocus
              value={trackNameDraft}
              onChange={e => setTrackNameDraft(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') handleTrackNameSubmit();
                if (e.key === 'Escape') setNeedsTrackName(false);
              }}
              placeholder="e.g. Summit Main"
              style={{
                background: '#12151f', border: '1px solid #3a3f55',
                borderRadius: 6, color: '#e0e4f0', fontSize: 14,
                padding: '8px 12px', outline: 'none',
              }}
            />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setNeedsTrackName(false)}
                style={{
                  background: 'transparent', border: '1px solid #3a3f55',
                  borderRadius: 6, color: '#9aa0b8', cursor: 'pointer',
                  fontSize: 13, padding: '7px 18px',
                }}
              >
                Skip
              </button>
              <button
                onClick={handleTrackNameSubmit}
                disabled={!trackNameDraft.trim()}
                style={{
                  background: trackNameDraft.trim() ? '#4a6cf7' : '#2a3050',
                  border: 'none', borderRadius: 6,
                  color: trackNameDraft.trim() ? '#fff' : '#5a6080',
                  cursor: trackNameDraft.trim() ? 'pointer' : 'default',
                  fontSize: 13, padding: '7px 18px',
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// AppInner has several early `return`s for different top-level views
// (leaderboard/progress/channelstats/upload-guard) before reaching the
// loaded-session view — wrapping here (once, outside all of them) ensures
// HelpButton/useHelp() is available no matter which view renders first.
// ShortcutOverlay lives here too (not inside any one AppInner branch) since
// it portals to document.body and only needs to be mounted once to be
// reachable from every view's HelpButton.
export default function App(props) {
  const [showMasqueradeModal, setShowMasqueradeModal] = useState(false);
  return (
    <HelpProvider>
      <IssueReportProvider
        currentUser={props.currentUser}
        enabled={props.appConfig?.capabilities?.user_accounts !== false && !props.publicMode && !props.guestMode}
      >
      <AppInner {...props} onRequestMasquerade={() => setShowMasqueradeModal(true)} />
      </IssueReportProvider>
      <ShortcutOverlay currentUser={props.currentUser} />
      {props.appConfig?.capabilities?.user_accounts !== false && showMasqueradeModal && (
        <MasqueradeModal
          onClose={() => setShowMasqueradeModal(false)}
          onStart={props.onStartMasquerade}
        />
      )}
    </HelpProvider>
  );
}
